// ============================================================================
// WORK ITEM DETAIL MODAL
// Comprehensive view with tabs for all work item data
// Uses UnifiedOverviewTab for static Overview layout (all item types)
// Uses custom components for complex UIs (Comments, Activity, Relationships)
// Date: October 13, 2025
// Updated: December 12, 2025 - Unified static overview replaces DynamicFormBuilder
// Updated: December 19, 2025 - Integrated QuickWorkflowActions for workflow transitions
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/job-details.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/job-details)
 * For team-member profiles (item_type === 'team_member') also update:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/team-member-detail.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/team-member-detail)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Trash2, Clock, MessageSquare, Activity, Link2, Paperclip, CheckSquare, Eye, Bell, Package, Target, Wrench, ChefHat, Camera, PenTool, MapPin, MessagesSquare, Printer, Save, Check, RefreshCw, ImageIcon, FileText, Calendar, Loader2, User, PlayCircle, StopCircle, Plus, DollarSign, Briefcase, Receipt, Copy, Mail, Users, ChevronRight, ChevronDown, Ban, Navigation, ExternalLink, Pause, GanttChart, Sparkles, Shield, History, ClipboardCheck, Download, Megaphone, Truck, ArrowRight, Send, CheckCircle2, Repeat, TrendingDown, Brain, GraduationCap, GitCompareArrows, ListOrdered, Scale, Trophy } from 'lucide-react';
import EmailContextTab from './tabs/EmailContextTab';
import type { WorkItem } from './UniversalWorkItems';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { emit } from '@/lib/typedEventBus';
import { isUuid } from '@/lib/uuid';
import { logUiAudit } from '@/lib/audit/uiAudit';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useBackgroundAwareInterval } from '@/hooks/useBackgroundAwareInterval';
import { useTheme } from '@/contexts/ThemeContext';
import { useModalState } from '@/contexts/ModalStateContext';
import { useTaskViewerTracking } from '@/hooks/useTaskViewerTracking';
import QuickAddTaskButton from './shared/QuickAddTaskButton';
import AppointmentBillingTab, { type AppointmentBillingSectionId } from './tabs/AppointmentBillingTab';
import BillingDashboard, { type BillingDashboardProps, type BillingSectionId } from './tabs/BillingDashboard';
import OpportunityPipelineTab from './tabs/OpportunityPipelineTab';
import AssetMaintenanceTab from './tabs/AssetMaintenanceTab';
import AssetValuationTab from './tabs/AssetValuationTab';
import RecipeBOMTab from './tabs/RecipeBOMTab';
import ProductImagesTab from './tabs/ProductImagesTab';
import FieldServicePhotosTab from './tabs/FieldServicePhotosTab';
import FieldServiceSignatureTab from './tabs/FieldServiceSignatureTab';
import SupportingPhotosTab from './tabs/SupportingPhotosTab';
import QuotationRequestsTab from './tabs/QuotationRequestsTab';
import ProjectBudgetTab from './tabs/ProjectBudgetTab';
import ProjectTeamPerformanceTab from './tabs/ProjectTeamPerformanceTab';
import ProjectTimeTrackingTab from './tabs/ProjectTimeTrackingTab';
import ProjectExpensesTab from './tabs/ProjectExpensesTab';

import JobExpensesTab from './tabs/JobExpensesTab';
import WorkItemDrawingsTab from './tabs/WorkItemDrawingsTab';
import { DualSLATimer } from './shared/SLATimer';
import SubtaskDetailDrawer from './work-items/SubtaskDetailDrawer';
import WorkBreakdownTab from './work-items/WorkBreakdownTab';
const AIOperationsReviewTab = React.lazy(() => import('./work-items/AIOperationsReviewTab'));
const ProcessChecklistTab = React.lazy(() => import('./work-items/ProcessChecklistTab'));
import JobChatTab from './work-items/JobChatTab';
import AppointmentsTab from './work-items/AppointmentsTab';
import DeliveriesTab from './work-items/DeliveriesTab';
import ProposalTab from './work-items/ProposalTab';
import { AI_PROPOSAL_MANAGER_SOLUTION_KEY } from '@/lib/operations/aiProposalManagerConfig';
import { MOBILE_SAFE_AREA_BOTTOM, workItemMobileOverlayPaddingStyle, workItemTabViewportStyle } from './work-items/mobileSafeArea';
import JobsTab from './work-items/JobsTab';
import CommentsSection from './work-items/CommentsSection';
import ActivityTimeline from './work-items/ActivityTimeline';
import { InlineLoadingPill, CardLoadingRow } from './shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';
import DetailsSidebar, { type DetailsSidebarInitialData } from './work-items/DetailsSidebar';
import ProcessGatePanel from './work-items/ProcessGatePanel';
import { buildDetailBundleHydrationState, type WorkItemDetailBundlePayload, type WorkItemOverviewInitialData } from './work-items/detailBundleHydration';
import FinanceDocumentPreview, { isFinanceDocType } from './work-items/FinanceDocumentPreview';
import WorkItemPreviewModal from './work-items/WorkItemPreviewModal';
import FinanceReportPreviewModal, { type FinanceReportPreviewContext, type FinanceReportPreviewOption } from './reports/FinanceReportPreviewModal';
import UnifiedOverviewTab from './work-items/UnifiedOverviewTab';
import { notify, confirm } from '@/lib/notifications';
import { copyTextToClipboard as copyTextToClipboardValue } from '@/lib/clipboard';
import { downloadRecordsAsCsv } from '@/lib/export/clientExport';
import { formatDateSAST, formatDateTimeSAST, formatTimeSAST, getTodayStrSAST } from '@/lib/sastTime';
import { canShowAiOperationsReviewTabForItemType } from '@/lib/operations/aiOperationsManagerConfig';
import { getEffectiveOperationsActionPermission, type OperationsActionKey } from '@/lib/operations/actionPermissions';
import { resolveOperationsBillingStrictness } from '@/lib/operations/billingStrictness';
import { QuickWorkflowActions } from './workflow/ServiceWorkflowManager';
import TeamMemberOverviewTab from './tabs/TeamMemberOverviewTab';
import ProjectOverviewTab from './tabs/ProjectOverviewTab';
import ProjectPlanTab from './tabs/ProjectPlanTab';
const ProjectGanttTab = React.lazy(() => import('./tabs/ProjectGanttTab'));
import TaskOverviewTab from './tabs/TaskOverviewTab';
import VisitorOverviewTab from './tabs/VisitorOverviewTab';
import VotingOverviewTab from './tabs/VotingOverviewTab';
import VotingAgendaTab from './tabs/VotingAgendaTab';
import VotingParticipantsTab from './tabs/VotingParticipantsTab';
import VotingQuorumTab from './tabs/VotingQuorumTab';
import VotingResultsTab from './tabs/VotingResultsTab';
import FollowUpOverviewTab from './tabs/FollowUpOverviewTab';
import SoftwareOverviewTab from './tabs/SoftwareOverviewTab';
import StakeholderOverviewTab from './tabs/StakeholderOverviewTab';
import StakeholderPeopleTab from './tabs/StakeholderPeopleTab';
import StakeholderLocationsTab from './tabs/StakeholderLocationsTab';
import StakeholderDocumentsTab from './tabs/StakeholderDocumentsTab';
const StakeholderHierarchyTab = React.lazy(() => import('./tabs/StakeholderHierarchyTab'));
import StakeholderTimeTrackingTab from './tabs/StakeholderTimeTrackingTab';
import StakeholderAssetsTab from './tabs/StakeholderAssetsTab';
import { getPreferredStakeholderFinancePreviewOptionId, getStakeholderContactId, getStakeholderFinancePreviewOptions, getStakeholderFinancePreviewPreferenceKey, getStakeholderFinanceTemplatePreferenceNamespace, getStakeholderLinkedAccountId, resolveVirtualStakeholderRecordDelete } from './contacts/stakeholderWorkItemAdapter';
import { parseStakeholderEntityWorkItemId } from '@/lib/contacts/stakeholderWorkItemIdentity';
import DocViewerOverviewTab from './tabs/DocViewerOverviewTab';
import LegalOverviewTab from './tabs/LegalOverviewTab';
import RecurringJobOverviewTab from './tabs/RecurringJobOverviewTab';
import RecurringJobInvocationHistoryTab from './tabs/RecurringJobInvocationHistoryTab';
const TeamMemberScheduleTab = React.lazy(() => import('./tabs/TeamMemberScheduleTab'));
const TeamMemberPerformanceTab = React.lazy(() => import('./tabs/TeamMemberPerformanceTab'));
const TeamMemberCapacityBudgetTab = React.lazy(() => import('./tabs/TeamMemberCapacityBudgetTab'));
import TeamMemberLicensesTab from './tabs/TeamMemberLicensesTab';
import TeamMemberSmartTaskTab from './tabs/TeamMemberSmartTaskTab';
const TeamMemberVehicleInspectionsTab = React.lazy(() => import('./tabs/TeamMemberVehicleInspectionsTab'));
const TeamMemberReconciliationTab = React.lazy(() => import('./tabs/TeamMemberReconciliationTab'));
const TeamMemberMobileWarehouseTab = React.lazy(() => import('./tabs/TeamMemberMobileWarehouseTab'));
const VehicleDetailModal = React.lazy(() => import('@/app/client-zone/modules/total-operations/components/fleet-management/VehicleDetailModal'));
import { useSmartTaskConfig } from '@/app/client-zone/modules/total-operations/components/smart-task/hooks/useSmartTaskConfig';
import useAppointmentLiveTimers from '@/hooks/useAppointmentLiveTimers';
import { WorkItemDetailContextProvider, useWorkItemDetailContext, getCachedTabConfig, setCachedTabConfig, getCachedJobTypes, getCachedCurrentUser, type OperationsUser } from '@/contexts/WorkItemDetailContext';
import AIOperationsAssistant, { type WorkItemAssistantContext } from '@/app/client-zone/modules/total-operations/components/AIOperationsAssistant';
import { fetchDemoSessionResponse, TOTAL_OPERATIONS_DEMO_MODULE_KEY } from '@/lib/demo/clientContext';
import { getWorkItemTypeIdentity } from '@/lib/work-items/typeIdentity';
import useModuleColor from '@/hooks/useModuleColor';
import { GuidedTour } from '@/components/training/guided-tour';
import { getStandardWorkItemStatusBadgeClass, getAppointmentStatusHeaderColor } from '@/lib/appointmentStatusVisual';
import {
  getSuggestedProgressFromStatus,
  isAdminReopenLockedStatus,
  isActivelyWorkedStatus,
  isNegativeOutcomeStatus,
  isOnRouteWorkItemStatus,
  isOnSiteWorkItemStatus,
  isSuccessfulCompletionStatus,
  isTerminalWorkItemStatus,
  normalizeWorkflowStatusKey,
} from '@/lib/work-items/operationalStatusConfig';
import { getAppointmentDisplayStatus } from '@/lib/work-items/appointmentRescheduleState';
import { isClosedCommentWorkItemStatus } from '@/lib/work-items/commentAccess';
import { isAdminRole } from '@/lib/auth-core';
import { useJobConfig } from '@/hooks/useJobConfig';
import type { AppointmentCompletionRequirementKey } from '@/lib/work-items/appointmentTypeConfig';
import { matchesAppointmentRealtimeMessage } from './tabs/teamMemberOperationsRealtime';
import { getTimerAggregationModeFromCustomFields, useTimerAggregationMode } from './tabs/billingDashboardUtils';
import { WORK_ITEM_LIST_CHANGED_EVENT } from '@/lib/websocket/lifecycle';
import dynamic from 'next/dynamic';
import {
  isOperationsJobBillingWorkspaceAllowed,
  isOperationsJobGenerateInvoiceAllowed,
  isOperationsJobBillingTimersSignoffAllowed,
  isOperationsJobBillingFinalizeAllowed,
  isOperationsJobEditTimersForCompletionAllowed,
} from '@/lib/operations/jobBillingAccess';
import { canShowJobBillableLedgerForStatus, isReadOnlyJobBillingStatus, isHardBlockedJobBillingStatus } from '@/lib/operations/jobBillingStatusAccess';
import {
  isOperationsJobEditFieldServicePhotosAllowed,
  isOperationsJobEditWorkItemCommentsAllowed,
} from '@/lib/operations/workItemPortalAccess';
import { ADVANCED_MANUFACTURING_MODULE_CONTEXT } from '@/lib/operations/advancedManufacturingScope';
import {
  OPERATIONS_RBAC_REFRESH_REQUESTED_EVENT,
  isOperationsRbacRefreshRequestedForUser,
  type OperationsRbacRefreshDetail,
} from '@/lib/operations/rbacRefresh';
import {
  getOperationsMyFeaturesFetchInit,
  getOperationsMyFeaturesPath,
} from '@/lib/operations/myFeaturesClient';
import { useEffectivePermissions, IAM_PERMISSIONS_CHANGED_EVENT } from '@/lib/iam/useEffectivePermissions';
import { PERMISSION_DEFINITIONS_BY_KEY } from '@/lib/iam/permissionDefinitions';

// ============================================================================
// TYPES
// ============================================================================

import type { WorkItemTemplate } from './WorkItemModal';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// Available tab IDs for filtering
export type WorkItemTabId =
  | 'overview' | 'email' | 'subtasks' | 'appointments' | 'jobs' | 'drawings'
  | 'proposal' | 'expenses' | 'budget' | 'timeseries' | 'inventory' | 'billing' | 'productImages'
  | 'pipeline' | 'maintenance' | 'recipe' | 'chat' | 'ai_review' | 'supporting_documents' | 'comments'
  | 'valuation'
  | 'activity' | 'time' | 'relationships' | 'attachments' | 'checklists'
  | 'watchers' | 'details' | 'work_breakdown' | 'photos' | 'signatures' | 'supporting_photos'
  | 'inspection' | 'license' | 'history' | 'dashboard' | 'tracking_security'
  | 'team_overview' | 'team_schedule' | 'team_performance' | 'team_capacity' | 'team_licenses' | 'team_vehicle_inspections' | 'team_reconciliation' | 'team_smart_task' | 'team_mobile_warehouse'
  | 'project_plan' | 'gantt' | 'project_team' | 'document'
  | 'stakeholder_people' | 'stakeholder_locations' | 'stakeholder_documents' | 'stakeholder_hierarchy' | 'stakeholder_time_tracking' | 'stakeholder_assets'
  | 'voting_agenda' | 'voting_participants' | 'voting_quorum' | 'voting_results'
  | 'marketing' | 'deliveries' | 'recurring' | 'invocation_history';

const LEGAL_WORK_ITEM_TYPES = ['matter', 'court_date', 'contract', 'trust_transaction'] as const;
type LegalWorkItemType = (typeof LEGAL_WORK_ITEM_TYPES)[number];

/** Work-items list scope: operational `template_key` (e.g. service-operations) or industry `solution_key` (e.g. field-services). */
const REFERENCE_DOCS_HIDDEN_JOB_LAUNCH_TEMPLATE_KEYS = new Set([
  'service-operations',
  'field-services',
]);

const LEGAL_DEFAULT_VISIBLE_TABS: WorkItemTabId[] = [
  'overview',
  'billing',
  'document',
  'supporting_documents',
  'activity',
  'relationships',
  'details',
];

// Follow-ups are lightweight operational queries — lean tab set only.
// Comments live inline on the follow-up overview, so no separate comments tab.
const FOLLOW_UP_VISIBLE_TAB_IDS = new Set<WorkItemTabId>(['overview', 'history', 'details']);

// Voting sessions are lifecycle-managed work items — a curated tab set:
// overview (session meta + lifecycle), the bespoke voting panels, and the
// generic history feed for the audit trail. Generic tabs like work breakdown,
// billing, documents and relationships don't apply.
const VOTING_VISIBLE_TAB_IDS = new Set<WorkItemTabId>([
  'overview',
  'voting_agenda',
  'voting_participants',
  'voting_quorum',
  'voting_results',
  'history',
  'details',
]);

/** Guided-tour flows hosted inside this modal — the embedded tour mount scopes
 *  its index (and launch claims) to these ids. */
const WORK_ITEM_DETAIL_TOUR_FLOW_IDS = ['work-item-detail', 'work-item-job-billing'];

const LEGACY_BILLING_TAB_IDS: WorkItemTabId[] = ['billing', 'time', 'expenses', 'maintenance', 'inventory'];
const LEGACY_SUPPORTING_DOCUMENT_TAB_IDS: WorkItemTabId[] = ['supporting_documents', 'comments', 'attachments', 'photos', 'signatures', 'supporting_photos'];

const getBillingSectionForTab = (tabId?: string | null): BillingSectionId => {
  switch (tabId) {
    case 'billing':
      return 'costing';
    case 'time':
      return 'time';
    case 'expenses':
      return 'costing';
    case 'maintenance':
      return 'maintenance';
    case 'inventory':
      return 'costing';
    case 'submissions':
      return 'submissions';
    default:
      return 'costing';
  }
};

const getAppointmentBillingSectionForTab = (tabId?: WorkItemTabId | null): AppointmentBillingSectionId => {
  switch (tabId) {
    case 'time':
      return 'time';
    case 'expenses':
    case 'billing':
      return 'billing';
    case 'inventory':
    default:
      return 'time';
  }
};

type SupportingDocumentsSectionId = 'comments' | 'attachments' | 'photos' | 'signatures' | 'quotation_requests';

const getSupportingDocumentsSectionForTab = (tabId?: WorkItemTabId | null): SupportingDocumentsSectionId => {
  switch (tabId) {
    case 'attachments':
      return 'attachments';
    case 'photos':
    case 'supporting_photos':
      return 'photos';
    case 'signatures':
      return 'signatures';
    default:
      return 'comments';
  }
};

type WorkItemTabContentRenderer = React.ReactNode | ((workItem: WorkItem) => React.ReactNode);

interface CustomWorkItemTabConfig {
  id: WorkItemTabId;
  label: string;
  icon: React.ElementType;
  count?: number;
  visible?: boolean;
  mobileOnly?: boolean;
}

interface WorkItemDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  workItem?: WorkItem | null;
  /** Backwards-compatible alias for `workItem` */
  item?: WorkItem | null;
  /** Backwards-compatible alias for `workItem` used by conversion/source flows */
  sourceItem?: WorkItem | null;
  /** Optional lazy-load identifier when only an ID is available */
  workItemId?: string | number;
  onEdit?: (item: WorkItem) => void;
  onDelete?: (id: string) => void;
  onRefresh?: () => void;
  /** Backwards-compatible alias for `onRefresh` */
  onUpdate?: () => void;
  templates?: WorkItemTemplate[];
  /** Optional array of tab IDs to show. If not provided, shows all relevant tabs based on item type */
  visibleTabs?: WorkItemTabId[];
  /** Preferred tab to show when the modal opens */
  initialTab?: WorkItemTabId;
  /** Override the modal title */
  title?: string;
  /** Override the header icon */
  headerIcon?: React.ElementType;
  /** Optional tab metadata to merge into the modal navigation */
  customTabs?: CustomWorkItemTabConfig[];
  /** Optional module key used to theme stakeholder detail headers via the standard module color hook */
  moduleColorKey?: string;
  /** Optional custom overview content, useful for subtype-specific detail experiences */
  overviewContent?: WorkItemTabContentRenderer;
  /** Optional active solution key for filtering assignees by team membership */
  activeSolutionKey?: string | null;
  /** Optional custom content for one or more tabs */
  customTabContent?: Partial<Record<WorkItemTabId, WorkItemTabContentRenderer>>;
  /** Callback for email reply - triggers MultiComposeManager */
  onReply?: (emailData: { email: Record<string, unknown>; all?: boolean; emailTaskId?: string }) => void;
  /** Callback when work item status changes (e.g., for moving emails on task completion) */
  onStatusChange?: (workItemId: string, newStatus: string, workItem: WorkItem) => void;
  /** Callback to merge the current stakeholder contact with another contact */
  onMergeContact?: (contactId: number) => void;
  /** When true, the modal is displayed in read-only mode with an overlay banner. Editing and saving are disabled. */
  readOnly?: boolean;
  /** Reason displayed in the read-only banner (e.g. "This appointment has been missed.") */
  readOnlyReason?: string;
  /**
   * Parent list scope (`WorkItemsViewWrapper` `moduleContext` / operational template key).
   * Used to suppress drawing-register tabs for standard field-service job lists without extra API work.
   */
  listModuleContext?: string | null;
  /** Override the z-index of the modal container (default 120). Use when opening above another fixed-position modal. */
  containerZIndex?: number;
  /** Override the data-tour-id on the tab navigation (defaults to "work-item-detail-tabs"). */
  tabsTourId?: string;
  /** Override the data-tour-id on the modal container (defaults to "work-item-detail-modal"). */
  containerTourId?: string;
  /**
   * When true, optimises the modal for workspace (Jira-like) items:
   * - Suppresses the consolidated "Supporting Documents" tab
   * - Promotes Comments and Attachments to top-level tabs
   * - Hides billing, relationships, and document tabs
   * Pair with `visibleTabs` to fully control the tab set.
   */
  workspaceItemMode?: boolean;
}

interface TabConfig {
  id: WorkItemTabId;
  label: string;
  icon: React.ElementType;
  count?: number;
  visible: boolean;
  mobileOnly?: boolean; // Only show on mobile screens
}

type UnknownRecord = Record<string, unknown>;

type WorkItemFormData = UnknownRecord & {
  tags?: string | string[];
  contact_id?: number | string | null;
  location_id?: number | string | null;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  location_name?: string;
  location_address?: string;
  location_coordinates?: unknown;
  custom_fields?: UnknownRecord;
  item_subtype?: string | null;
  status?: string;
  title?: string;
  priority?: string;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  due_date?: string | null;
  estimated_hours?: number | string | null;
  progress_percentage?: number | string | null;
};

type WorkItemWithMetadata = WorkItem & {
  client_id?: string | number | null;
  share_token?: string | null;
  share_revoked_at?: string | null;
  created_by?: number | null;
  parent_id?: string | null;
  parent_job_number?: string | null;
  custom_fields?: unknown;
  assignees?: Array<{
    first_name?: string;
    last_name?: string;
  }>;
};

type FormDataSourceItem = WorkItemWithMetadata & WorkItemFormData & {
  __appointmentDetailMode?: unknown;
  __appointmentGroupContext?: unknown;
};

interface WorkItemHierarchyAncestor {
  id: string;
  item_type?: string;
  item_number: string;
  title: string;
}

const isUnknownRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const parseUnknownRecord = (value: unknown): UnknownRecord => {
  if (isUnknownRecord(value)) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return isUnknownRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return {};
};

const areShallowEqualRecords = (
  left: Record<string, unknown> | null | undefined,
  right: Record<string, unknown> | null | undefined,
): boolean => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return !left && !right;
  }

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => Object.is(left[key], right[key]));
};

const areEquivalentFieldValues = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) {
    return true;
  }

  if (isUnknownRecord(left) && isUnknownRecord(right)) {
    return areShallowEqualRecords(left, right);
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((value, index) => Object.is(value, right[index]));
  }

  return false;
};

const getWorkItemCustomFields = (item?: Pick<WorkItemWithMetadata, 'custom_fields'> | null): UnknownRecord => {
  return parseUnknownRecord(item?.custom_fields);
};

const isPlaceholderTeamMemberTitle = (value: unknown): boolean => {
  return typeof value === 'string' && /^team member\s+\d+$/i.test(value.trim());
};

const getErrorMessage = (error: unknown, fallback = 'Operation failed'): string => {
  return error instanceof Error ? error.message : fallback;
};

const toDisplayString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
};

const humanizeUnknownValue = (value: unknown, fallback = ''): string => {
  return toDisplayString(value, fallback).replace(/_/g, ' ');
};

const toDateLikeValue = (value: unknown): string | number | Date | null => {
  if (value instanceof Date || typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return null;
};

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

type AppointmentRequirementOverride = {
  enabled?: boolean;
  reason?: string;
};

const isAppointmentRequirementOverride = (value: unknown): value is AppointmentRequirementOverride => {
  return isUnknownRecord(value);
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format a UTC timestamp as "time ago" in user's local timezone
 * Backend sends UTC timestamps (e.g., "2025-10-13T19:41:31.066Z")
 * JavaScript Date automatically converts to browser's local timezone
 */
const formatTimeAgo = (dateString: string | null | undefined): string => {
  if (!dateString) return 'recently';

  const date = new Date(dateString);

  // Check for invalid date
  if (isNaN(date.getTime())) return 'recently';

  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return `${formatDateSAST(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })}, ${formatTimeSAST(date)}`;
};

const formatCompactDateTimeSAST = (value: Date | string | number | null | undefined): string => {
  const dateLikeValue = toDateLikeValue(value);
  if (dateLikeValue == null) {
    return '';
  }

  const normalizedDateValue = typeof dateLikeValue === 'number'
    ? new Date(dateLikeValue)
    : dateLikeValue;

  return `${formatDateSAST(normalizedDateValue, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })}, ${formatTimeSAST(normalizedDateValue)}`;
};

/**
 * Format viewer display name using first_name and last_name instead of user_name (which may be email)
 */
const formatViewerDisplayName = (viewer: { user_name?: string; first_name?: string; last_name?: string } | null | undefined): string => {
  if (!viewer) return 'Unknown';

  // Prefer first_name + last_name if available
  const firstName = viewer.first_name ? viewer.first_name.trim() : '';
  const lastName = viewer.last_name ? viewer.last_name.trim() : '';

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  if (firstName) {
    return firstName;
  }

  if (lastName) {
    return lastName;
  }

  // Fallback to user_name if name fields are not available
  return viewer.user_name || 'Unknown';
};

/**
 * Get current access token from authService
 * This ensures we always use the latest, non-expired token
 */
const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const isTerminalOrArchivedStatus = (status?: string | null, itemType?: string | null): boolean => {
  const normalizedStatus = normalizeWorkflowStatusKey(status);
  return normalizedStatus === 'archived' || isTerminalWorkItemStatus(normalizedStatus, itemType);
};

const getOperationalStatusBadgeClass = (status: string | null | undefined, isDark: boolean, itemType?: string | null): string => {
  // Use centralized status badge mapping for consistency across all interfaces
  return getStandardWorkItemStatusBadgeClass(status || '', itemType || undefined);
};

const getHeaderStatusPillClass = (status: string | null | undefined): string => {
  const normalizedStatus = normalizeWorkflowStatusKey(status);

  if (normalizedStatus === 'resolved') {
    return 'bg-sky-500/30 text-sky-100';
  }

  if (isSuccessfulCompletionStatus(normalizedStatus)) {
    return 'bg-green-500/30 text-green-100';
  }

  if (isNegativeOutcomeStatus(normalizedStatus)) {
    return 'bg-red-500/30 text-red-100';
  }

  if (isActivelyWorkedStatus(normalizedStatus)) {
    return 'bg-blue-500/30 text-blue-100';
  }

  if (isOnSiteWorkItemStatus(normalizedStatus)) {
    return 'bg-cyan-500/30 text-cyan-100';
  }

  if (isOnRouteWorkItemStatus(normalizedStatus)) {
    return 'bg-violet-500/30 text-violet-100';
  }

  return 'bg-white/20 text-white';
};

const renderBrandedTabLoader = (label: string, primaryColor: string, className = 'py-10') => (
  <InlineLoadingPill
    label={label}
    primaryColor={primaryColor}
    className={className}
  />
);

// ============================================================================
// COMMENTS COMPONENT
// NOTE: CommentsTab is superseded by the imported CommentsSection component.
// ============================================================================

interface Comment {
  id: string;
  user_id: number;
  user_name: string;
  comment_text: string;
  is_internal: boolean;
  created_at: string;
  updated_at: string;
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CommentsTab({ workItemId, isActive, includeRelated = false }: { workItemId: string; isActive: boolean; includeRelated?: boolean }) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'public' | 'internal'>('all');
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const relatedParam = includeRelated ? '?include_related=true' : '';
      const response = await fetch(`/api/v1/work-items/${workItemId}/comments${relatedParam}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setComments(data.data || []);
        }
      }
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  }, [includeRelated, workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadComments();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadComments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment_text: newComment, is_internal: isInternal })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setComments((prevComments) => [data.data, ...prevComments]);
          setNewComment('');
          setIsInternal(false); // Reset to public after adding comment
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!(await confirm({ message: 'Delete this comment?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        setComments(comments.filter(c => c.id !== commentId));
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Toggle comment visibility (internal/public)
  const handleToggleVisibility = async (commentId: string, currentIsInternal: boolean) => {
    try {
      setTogglingVisibility(commentId);
      const response = await fetch(`/api/v1/work-items/${workItemId}/comments/${commentId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_internal: !currentIsInternal })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setComments(comments.map(c =>
            c.id === commentId ? { ...c, is_internal: !currentIsInternal } : c
          ));
        }
      }
    } catch (error) {
      console.error('Error toggling comment visibility:', error);
    } finally {
      setTogglingVisibility(null);
    }
  };

  const isDark = currentTheme.mode === 'dark';

  // Filter comments — single memoized pass over the array
  const { filteredComments, publicCount, internalCount } = useMemo(() => {
    let pub = 0;
    let int = 0;
    const filtered: Comment[] = [];
    for (const c of comments) {
      if (c.is_internal) { int++; } else { pub++; }
      if (filter === 'all' || (filter === 'public' && !c.is_internal) || (filter === 'internal' && c.is_internal)) {
        filtered.push(c);
      }
    }
    return { filteredComments: filtered, publicCount: pub, internalCount: int };
  }, [comments, filter]);

  if (loading) {
    return renderBrandedTabLoader('Loading comments...', '#3b82f6', 'py-12');
  }

  return (
    <div className="space-y-4">
      {/* Add Comment Form */}
      <div className={`rounded-lg p-4 border-2 ${isInternal
        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
        }`}>
        {/* Comment Type Toggle - More prominent */}
        <div className="flex items-center gap-3 mb-3">
          <button
            type="button"
            onClick={() => setIsInternal(false)}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${!isInternal
              ? 'bg-blue-600 text-white shadow-md'
              : `${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-50'} border ${isDark ? 'border-gray-600' : 'border-gray-300'}`
              }`}
          >
            <MessageSquare className="w-4 h-4" />
            Public Reply
          </button>
          <button
            type="button"
            onClick={() => setIsInternal(true)}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${isInternal
              ? 'bg-yellow-500 text-white shadow-md'
              : `${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-50'} border ${isDark ? 'border-gray-600' : 'border-gray-300'}`
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Internal Note
          </button>
        </div>

        {/* Context hint */}
        <div className={`text-xs mb-2 px-2 py-1 rounded ${isInternal
          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
          }`}>
          {isInternal
            ? '🔒 Internal notes are only visible to your team - customers cannot see these'
            : '👁️ Public replies are visible to customers and agents'}
        </div>

        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={isInternal ? "Add internal note (team members only)..." : "Add public reply (customer will see this)..."}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none ${isDark ? 'bg-gray-700 text-gray-100' : 'bg-white text-gray-900'
            } ${isInternal ? 'border-yellow-400 dark:border-yellow-600' : 'border-blue-400 dark:border-blue-600'}`}
          style={{
            '--tw-ring-color': isInternal ? '#eab308' : currentTheme.colors.primary
          } as React.CSSProperties}
          rows={3}
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={handleAddComment}
            disabled={submitting || !newComment.trim()}
            className={`px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2 ${isInternal ? 'bg-yellow-500 hover:bg-yellow-600' : ''
              }`}
            style={!isInternal ? { backgroundColor: currentTheme.colors.primary } : {}}
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Adding...
              </>
            ) : (
              <>
                {isInternal ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                ) : (
                  <MessageSquare className="w-4 h-4" />
                )}
                Add {isInternal ? 'Internal Note' : 'Public Reply'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      {comments.length > 0 && (
        <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'all'
              ? `${isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow-sm'}`
              : `${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
              }`}
          >
            All ({comments.length})
          </button>
          <button
            onClick={() => setFilter('public')}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${filter === 'public'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
              : `${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
              }`}
          >
            <MessageSquare className="w-3 h-3" />
            Public ({publicCount})
          </button>
          <button
            onClick={() => setFilter('internal')}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${filter === 'internal'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
              : `${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
              }`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Internal ({internalCount})
          </button>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-3">
        {filteredComments.length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <MessageSquare className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p>{filter === 'all' ? 'No comments yet. Be the first to comment!' : `No ${filter} comments.`}</p>
          </div>
        ) : (
          filteredComments.map(comment => (
            <div key={comment.id} className={`border-l-4 rounded-lg p-4 ${comment.is_internal
              ? `border-l-yellow-500 ${isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`
              : `border-l-blue-500 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`
              } border`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <span className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{comment.user_name}</span>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formatDateTimeSAST(comment.created_at)}</span>
                    {/* Appointment source badge */}
                    {comment.source_appointment_number && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                        {comment.source_appointment_number}
                      </span>
                    )}
                    {/* Visibility badge with toggle */}
                    <button
                      onClick={() => handleToggleVisibility(comment.id, comment.is_internal)}
                      disabled={togglingVisibility === comment.id}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded transition-all cursor-pointer hover:opacity-80 ${comment.is_internal
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 border border-yellow-300 dark:border-yellow-700'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 border border-blue-300 dark:border-blue-700'
                        }`}
                      title={`Click to make ${comment.is_internal ? 'public' : 'internal'}`}
                    >
                      {togglingVisibility === comment.id ? (
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                      ) : comment.is_internal ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      ) : (
                        <MessageSquare className="w-3 h-3" />
                      )}
                      {comment.is_internal ? 'Internal' : 'Public'}
                      <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </button>
                  </div>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>{comment.comment_text}</p>
                </div>
                <button
                  onClick={() => handleDeleteComment(comment.id)}
                  className={`${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'} ml-2 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30`}
                  title="Delete comment"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ACTIVITY FEED COMPONENT
// ============================================================================

const ACTIVITY_ITEMS_PER_PAGE = 50;

interface ActivityItem {
  id: string;
  action?: string;
  action_type?: string;
  description?: string;
  user_name?: string;
  created_at: string;
  field_changed?: string;
  old_value?: unknown;
  new_value?: unknown;
  user?: {
    name?: string;
    email?: string;
  };
  details?: {
    doc_type?: string;
    doc_number?: string;
    location?: string;
    duration?: string;
    signer?: string;
    count?: number;
    assignee_name?: string;
    updated_fields?: string[];
    changes?: Record<string, { old?: unknown; new?: unknown }>;
  };
  metadata?: string | Record<string, unknown>;
  changes?: Array<{
    field: string;
    old_value?: unknown;
    new_value?: unknown;
    old_display_value?: string | null;
    new_display_value?: string | null;
    reference_type?: string | null;
    source?: string;
  }>;
}

type ActivityFilter = 'all' | 'changes' | 'status' | 'comments';

const ACTIVITY_FILTERS: Array<{ key: ActivityFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'changes', label: 'Changes' },
  { key: 'status', label: 'Status' },
  { key: 'comments', label: 'Comments' }
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ActivityTab({ workItemId, isActive }: { workItemId: string; isActive: boolean }) {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();

  const loadActivity = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await fetch(`/api/v1/work-items/${workItemId}/activity?page=${pageNum}&limit=${ACTIVITY_ITEMS_PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const newActivities: ActivityItem[] = data.data || [];
          if (append) {
            setActivities(prev => [...prev, ...newActivities]);
          } else {
            setActivities(newActivities);
          }
          setHasMore(newActivities.length === ACTIVITY_ITEMS_PER_PAGE);
          setPage(pageNum);
        }
      }
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('Error loading activity:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadActivity();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadActivity]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      loadActivity(page + 1, true);
    }
  };

  const fetchAllActivitiesForExport = useCallback(async (): Promise<ActivityItem[]> => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required to export activity history.');
    }

    const collectedActivities: ActivityItem[] = [];
    let pageNum = 1;

    while (true) {
      const response = await fetch(`/api/v1/work-items/${workItemId}/activity?page=${pageNum}&limit=${ACTIVITY_ITEMS_PER_PAGE}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load activity history for export.');
      }

      const payload = await response.json();
      if (!payload.success) {
        throw new Error(payload.error || 'Failed to load activity history for export.');
      }

      const batch: ActivityItem[] = payload.data || [];
      collectedActivities.push(...batch);

      if (batch.length < ACTIVITY_ITEMS_PER_PAGE) {
        break;
      }

      pageNum += 1;
    }

    return collectedActivities;
  }, [workItemId]);

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'created': return { emoji: '🎉', color: 'text-green-500' };
      case 'updated': return { emoji: '✏️', color: 'text-blue-500' };
      case 'status_changed': return { emoji: '🔄', color: 'text-purple-500' };
      case 'work_item_visible': return { emoji: '📥', color: 'text-sky-500' };
      case 'work_item_opened': return { emoji: '👁️', color: 'text-cyan-500' };
      case 'ui_action': return { emoji: '🧭', color: 'text-indigo-500' };
      case 'assigned': return { emoji: '👤', color: 'text-indigo-500' };
      case 'unassigned': return { emoji: '👤', color: 'text-gray-500' };
      case 'commented': return { emoji: '💬', color: 'text-cyan-500' };
      case 'completed': return { emoji: '✅', color: 'text-green-600' };
      case 'deleted': return { emoji: '🗑️', color: 'text-red-500' };
      case 'sent': return { emoji: '📤', color: 'text-blue-600' };
      case 'paid': return { emoji: '💰', color: 'text-green-500' };
      case 'approved': return { emoji: '✅', color: 'text-green-600' };
      case 'rejected': return { emoji: '❌', color: 'text-red-500' };
      case 'checkin': return { emoji: '▶️', color: 'text-green-500' };
      case 'checkout': return { emoji: '⏹️', color: 'text-orange-500' };
      case 'signature': return { emoji: '✍️', color: 'text-purple-500' };
      case 'photo': return { emoji: '📷', color: 'text-pink-500' };
      case 'attachment': return { emoji: '📎', color: 'text-gray-500' };
      default: return { emoji: '📝', color: 'text-gray-500' };
    }
  };

  const getFieldLabel = useCallback((field?: string | null) => {
    switch ((field || '').trim()) {
      case 'assigned_to':
        return 'assignee';
      case 'assigned_users':
        return 'assignees';
      case 'contact_id':
        return 'contact';
      case 'contact_person_id':
      case 'person_id':
        return 'contact person';
      case 'location_id':
        return 'location';
      default:
        return humanizeUnknownValue(field || '', 'details');
    }
  }, []);

  const normalizeComparableActivityValue = useCallback((value: unknown) => {
    const display = toDisplayString(value, '').trim().toLowerCase();
    if (!display || display === 'null' || display === 'undefined' || display === 'none') {
      return '';
    }
    return display;
  }, []);

  const getChangeDisplayValue = useCallback((change: NonNullable<ActivityItem['changes']>[number], side: 'old' | 'new') => {
    const preferred = side === 'old' ? change.old_display_value : change.new_display_value;
    if (typeof preferred === 'string' && preferred.trim()) {
      return preferred.trim();
    }

    return toDisplayString(side === 'old' ? change.old_value : change.new_value, '').trim();
  }, []);

  const getMeaningfulChanges = useCallback((activity: ActivityItem) => {
    const candidateChanges = Array.isArray(activity.changes) && activity.changes.length > 0
      ? activity.changes
      : (activity.field_changed || activity.old_value !== undefined || activity.new_value !== undefined)
        ? [{
          field: activity.field_changed || 'details',
          old_value: activity.old_value,
          new_value: activity.new_value,
          old_display_value: null,
          new_display_value: null,
        }]
        : [];

    return candidateChanges.filter((change) => {
      const oldDisplay = getChangeDisplayValue(change, 'old');
      const newDisplay = getChangeDisplayValue(change, 'new');
      return normalizeComparableActivityValue(oldDisplay) !== normalizeComparableActivityValue(newDisplay);
    });
  }, [getChangeDisplayValue, normalizeComparableActivityValue]);

  const shouldSuppressActivity = useCallback((activity: ActivityItem) => {
    const action = activity.action || 'updated';
    if (!['updated', 'field_updated', 'assigned', 'unassigned', 'assignee_added', 'assignee_removed'].includes(action)) {
      return false;
    }

    return getMeaningfulChanges(activity).length === 0;
  }, [getMeaningfulChanges]);

  // Generate a human-readable description from activity data with detailed audit info
  const getActivityDescription = useCallback((activity: ActivityItem): string => {
    const action = activity.action || 'updated';
    const field = activity.field_changed;
    const oldVal = activity.old_value;
    const newVal = activity.new_value;
    const details = activity.details;
    const userName = activity.user?.name || activity.user?.email || 'Someone';
    const metadata = (() => {
      if (typeof activity.metadata !== 'string') {
        return activity.metadata || {};
      }

      try {
        return JSON.parse(activity.metadata) as Record<string, unknown>;
      } catch {
        return {};
      }
    })();
    const meaningfulChanges = getMeaningfulChanges(activity);

    if (meaningfulChanges.length > 0) {
      const primaryChange = meaningfulChanges[0];
      const fieldLabel = getFieldLabel(primaryChange.field);
      const oldDisplay = getChangeDisplayValue(primaryChange, 'old');
      const newDisplay = getChangeDisplayValue(primaryChange, 'new');

      if (meaningfulChanges.length === 1) {
        if (fieldLabel === 'assignee') {
          if (oldDisplay && newDisplay) {
            return `${userName} reassigned from ${oldDisplay} to ${newDisplay}`;
          }
          if (newDisplay) {
            return `${userName} assigned ${newDisplay}`;
          }
          if (oldDisplay) {
            return `${userName} unassigned ${oldDisplay}`;
          }
        }

        if (oldDisplay && newDisplay) {
          return `${userName} changed ${fieldLabel}: ${oldDisplay} → ${newDisplay}`;
        }

        if (newDisplay) {
          return `${userName} set ${fieldLabel} to ${newDisplay}`;
        }

        if (oldDisplay) {
          return `${userName} cleared ${fieldLabel} (${oldDisplay})`;
        }
      }

      if (meaningfulChanges.length <= 3) {
        return `${userName} updated ${meaningfulChanges.map((change) => getFieldLabel(change.field)).join(', ')}`;
      }

      return `${userName} updated ${meaningfulChanges.length} fields`;
    }

    // Handle specific actions with detailed audit info
    if (action === 'created') {
      if (details?.doc_type) {
        return `${userName} created ${details.doc_type.replace(/_/g, ' ')} ${details.doc_number || ''}`;
      }
      if (metadata?.item_type) {
        return `${userName} created ${humanizeUnknownValue(metadata.item_type, 'item')} "${toDisplayString(metadata.title)}"`;
      }
      return `${userName} created this item`;
    }

    if (action === 'work_item_visible') {
      const actionDetails = toDisplayString(metadata?.action_details, '').trim();
      if (actionDetails) {
        return actionDetails;
      }

      const itemNumber = toDisplayString(metadata?.item_number, '').trim();
      return itemNumber
        ? `${userName} synced ${itemNumber} into the SmartTask queue`
        : `${userName} synced this item into SmartTask`;
    }

    if (action === 'work_item_opened' || action === 'ui_action') {
      const actionDetails = toDisplayString(metadata?.action_details, '').trim();
      if (actionDetails) {
        return actionDetails;
      }

      const itemNumber = toDisplayString(metadata?.item_number, '').trim();
      const uiAction = toDisplayString(metadata?.ui_action, '').trim();
      if ((action === 'work_item_opened' || uiAction === 'work_item_opened') && itemNumber) {
        return `${userName} opened ${itemNumber} in SmartTask`;
      }

      const uiComponent = toDisplayString(metadata?.ui_component || metadata?.component, '').trim();
      if (uiComponent) {
        return `${userName} interacted with ${uiComponent}`;
      }

      return `${userName} performed a SmartTask UI action`;
    }

    if (action === 'deleted') {
      if (metadata?.title) {
        return `${userName} deleted "${metadata.title}" (${metadata.item_type || 'item'})`;
      }
      return `${userName} deleted this item`;
    }

    if (action === 'sent') {
      return `${userName} sent document to customer`;
    }

    if (action === 'paid') {
      return `${userName} recorded payment${newVal ? `: ${newVal}` : ''}`;
    }

    if (action === 'assigned' || action === 'assignee_added') {
      // Try to get assignee name from metadata or details
      const assigneeName = metadata?.assignee_name || details?.assignee_name || newVal || 'user';
      return `${userName} added ${assigneeName} as assignee`;
    }

    if (action === 'unassigned' || action === 'assignee_removed') {
      const assigneeName = metadata?.assignee_name || details?.assignee_name || oldVal || 'user';
      return `${userName} removed ${assigneeName} from assignees`;
    }

    if (action === 'status_changed' || field === 'status') {
      const fromStatus = humanizeUnknownValue(oldVal, 'none');
      const toStatus = humanizeUnknownValue(newVal, 'unknown');
      return `${userName} changed status: ${fromStatus} → ${toStatus}`;
    }

    if (action === 'status_cascaded') {
      const parentTypeLabel = humanizeUnknownValue(metadata.parent_type, 'related item');
      const nextStatusLabel = humanizeUnknownValue(metadata.new_status, 'updated');
      const normalizedNextStatus = normalizeWorkflowStatusKey(toDisplayString(metadata.new_status));

      if (normalizedNextStatus === 'closed') {
        return `This record was closed automatically when the linked ${parentTypeLabel} was closed.`;
      }

      return `This record was updated automatically to stay in step with the linked ${parentTypeLabel} (${nextStatusLabel}).`;
    }

    if (action === 'checkin') {
      return `${userName} checked in${details?.location ? ` at ${details.location}` : ''}`;
    }

    if (action === 'checkout') {
      return `${userName} checked out${details?.duration ? ` (${details.duration})` : ''}`;
    }

    if (action === 'signature') {
      return `${userName} captured signature${details?.signer ? ` from ${details.signer}` : ''}`;
    }

    if (action === 'photo') {
      return `${userName} added photo${details?.count && details.count > 1 ? `s (${details.count})` : ''}`;
    }

    if (action === 'priority_changed' || field === 'priority') {
      return `${userName} changed priority: ${oldVal || 'none'} → ${newVal || 'none'}`;
    }

    if (action === 'scheduled' || field === 'scheduled_start') {
      if (newVal) {
        const scheduledValue = toDateLikeValue(newVal);
        if (scheduledValue != null) {
          const scheduledDate = formatCompactDateTimeSAST(scheduledValue);
          return `${userName} scheduled for ${scheduledDate}`;
        }
      }
      return `${userName} updated schedule`;
    }

    if (action === 'updated' || action === 'field_updated') {
      // Check for document updates with details
      if (details?.updated_fields) {
        const fields = details.updated_fields;
        if (fields.length === 1) {
          const fieldName = fields[0].replace(/_/g, ' ');
          if (details.changes?.[fields[0]]) {
            const change = details.changes[fields[0]];
            return `${userName} updated ${fieldName}: "${change.old || '-'}" → "${change.new || '-'}"`;
          }
          return `${userName} updated ${fieldName}`;
        } else if (fields.length <= 3) {
          return `${userName} updated ${fields.map((f: string) => f.replace(/_/g, ' ')).join(', ')}`;
        } else {
          return `${userName} updated ${fields.length} fields`;
        }
      }

      if (field) {
        const fieldName = field.replace(/_/g, ' ');
        if (oldVal !== undefined && newVal !== undefined) {
          // Truncate long values
          const truncate = (val: unknown) => {
            const str = String(val);
            return str.length > 30 ? str.substring(0, 30) + '...' : str;
          };
          return `${userName} updated ${fieldName}: "${truncate(oldVal)}" → "${truncate(newVal)}"`;
        } else if (newVal !== undefined) {
          return `${userName} set ${fieldName} to "${newVal}"`;
        } else {
          return `${userName} updated ${fieldName}`;
        }
      }

      return `${userName} updated this item`;
    }

    // Fallback with user name
    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, ' ');
    return `${userName} ${actionLabel.toLowerCase()}${field ? ` ${field.replace(/_/g, ' ')}` : ''}`;
  }, [getChangeDisplayValue, getFieldLabel, getMeaningfulChanges]);

  const filterActivities = useCallback((itemsToFilter: ActivityItem[]) => {
    const visibleActivities = itemsToFilter.filter(activity => !shouldSuppressActivity(activity));
    if (filter === 'all') return visibleActivities;
    if (filter === 'changes') return visibleActivities.filter(a => a.action === 'updated' || a.action === 'field_updated' || a.field_changed);
    if (filter === 'status') return visibleActivities.filter(a => a.action === 'status_changed' || a.field_changed === 'status');
    if (filter === 'comments') return visibleActivities.filter(a => a.action === 'commented');
    return visibleActivities;
  }, [filter, shouldSuppressActivity]);

  const filteredActivities = useMemo(() => filterActivities(activities), [activities, filterActivities]);
  const isDark = currentTheme.mode === 'dark';

  const handleExportFilteredActivity = useCallback(async () => {
    if (exporting) {
      return;
    }

    try {
      setExporting(true);
      const allActivities = await fetchAllActivitiesForExport();
      const exportActivities = filterActivities(allActivities);

      if (exportActivities.length === 0) {
        notify.warning('No activity entries match the current filter.');
        return;
      }

      downloadRecordsAsCsv(
        exportActivities.map((activity) => ({
          id: activity.id,
          created_at: activity.created_at,
          displayed_at: formatDateTimeSAST(activity.created_at),
          action: activity.action || activity.action_type || 'updated',
          description: getActivityDescription(activity),
          user_name: activity.user?.name || activity.user_name || '',
          user_email: activity.user?.email || '',
          field_changed: activity.field_changed || '',
          old_value: activity.old_value ?? '',
          new_value: activity.new_value ?? '',
          metadata: activity.metadata ?? null,
          details: activity.details ?? null,
        })),
        `work-item-${String(workItemId)}-activity-${filter}-${getTodayStrSAST()}`
      );

      notify.success(`Exported ${exportActivities.length} activity entr${exportActivities.length === 1 ? 'y' : 'ies'}.`);
    } catch (error) {
      console.error('Error exporting activity:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to export activity history.');
    } finally {
      setExporting(false);
    }
  }, [exporting, fetchAllActivitiesForExport, filter, filterActivities, getActivityDescription, workItemId]);

  // Group activities by date
  const groupedActivities = filteredActivities.reduce<Record<string, ActivityItem[]>>((groups, activity) => {
    const date = formatDateSAST(activity.created_at, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {});

  if (loading) {
    return renderBrandedTabLoader('Loading activity...', '#8b5cf6', 'py-12');
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter Bar */}
      <div className={`flex items-center gap-2 mb-3 p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {ACTIVITY_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${filter === f.key
              ? 'bg-blue-600 text-white'
              : `${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`
              }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {filteredActivities.length} entries
          </span>
          <button
            type="button"
            onClick={() => { void handleExportFilteredActivity(); }}
            disabled={exporting || filteredActivities.length === 0}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${isDark
              ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              : 'bg-white text-gray-700 hover:bg-gray-200 border border-gray-200'}`}
            title={filteredActivities.length > 0 ? 'Export the current activity filter to CSV' : 'No filtered activity to export'}
          >
            <Download className={`w-3.5 h-3.5 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Activity Log - Compact Timeline */}
      <div className="flex-1 overflow-auto">
        {Object.keys(groupedActivities).length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Activity className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p>No activity yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedActivities).map(([date, dayActivities]) => (
              <div key={date}>
                {/* Date Header */}
                <div className={`sticky top-0 z-10 py-1 px-2 text-xs font-semibold ${isDark ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  {date}
                </div>

                {/* Day's Activities - Compact List */}
                <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                  {dayActivities.map((activity) => {
                    const iconInfo = getActivityIcon(activity.action || 'updated');
                    return (
                      <div
                        key={activity.id}
                        className={`flex items-center gap-2 py-1.5 px-2 hover:${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} transition-colors`}
                      >
                        {/* Icon */}
                        <span className="text-sm flex-shrink-0" title={activity.action}>
                          {iconInfo.emoji}
                        </span>

                        {/* Description */}
                        <span className={`flex-1 text-sm truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {getActivityDescription(activity)}
                        </span>

                        {/* User */}
                        {activity.user?.name && (
                          <span className={`text-xs flex-shrink-0 max-w-[100px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {activity.user.name}
                          </span>
                        )}

                        {/* Time */}
                        <span className={`text-xs flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                          {formatTimeSAST(activity.created_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center py-3">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  {loadingMore ? (
                    <span className="inline-flex items-center">
                      <InlineLoadingPill label="Loading more..." primaryColor="#8b5cf6" className="py-0" />
                    </span>
                  ) : (
                    'Load more activity'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// TIME ENTRIES COMPONENT
// Enhanced to show travel entries, appointment actual times, and manual entries
// ============================================================================

interface TimeEntry {
  id: string;
  entry_type?: 'manual' | 'mobile_checkin' | 'auto' | 'travel' | 'appointment';
  activity_type?: 'travel' | 'job' | 'break' | 'wait' | 'admin' | 'manual' | 'on_site' | 'checkin' | 'delivery' | 'maintenance' | 'field_work';
  entry_date: string;
  start_time?: string;
  end_time?: string;
  started_at?: string;
  ended_at?: string;
  duration_hours: number;
  duration_minutes?: number;
  description?: string;
  is_billable: boolean;
  user_name?: string;
  created_at: string;
  // GPS-verified check-in fields
  gps_check_in?: {
    lat: number;
    lng: number;
    accuracy?: number;
    timestamp?: string;
  };
  gps_check_out?: {
    lat: number;
    lng: number;
    accuracy?: number;
    timestamp?: string;
  };
  // Travel-specific fields
  distance_km?: number;
  vehicle_registration?: string;
  start_odometer?: number;
  end_odometer?: number;
  // Reference fields - where this entry came from
  source_item_number?: string;
  source_item_type?: string;
  source_item_title?: string;
  // New API fields
  tracked_via?: 'manual' | 'mobile_app' | 'auto' | 'desktop';
  is_from_related?: boolean;  // True if entry is from a related item (travel, appointment)
  user_display_name?: string;
  metadata?: {
    distance_km?: number;
    vehicle_id?: string;
    vehicle_registration?: string;
    start_odometer?: number;
    end_odometer?: number;
    start_location?: { lat: number; lng: number; accuracy?: number };
    end_location?: { lat: number; lng: number; accuracy?: number };
    travel_work_item_id?: string;
    travel_work_item_number?: string;
    ended_early_reason?: string;
    [key: string]: unknown;
  };
}

interface RelatedWorkItemRelationship {
  item?: {
    id: string;
    item_type?: string;
    item_number: string;
    title: string;
    status: string;
    created_at: string;
    scheduled_start?: string;
    scheduled_end?: string;
    custom_fields?: Record<string, unknown>;
    assignees?: Array<{
      first_name?: string;
      last_name?: string;
    }>;
  };
}

interface RelatedWorkItemRelationshipGroups {
  incoming?: {
    child_of?: RelatedWorkItemRelationship[];
    scheduled_for?: RelatedWorkItemRelationship[];
  };
  outgoing?: {
    parent_of?: RelatedWorkItemRelationship[];
    scheduled_for?: RelatedWorkItemRelationship[];
  };
}

type TimeEntriesSection = 'all' | 'travel' | 'work' | 'manual';

const hasRelatedItem = (
  relationship: RelatedWorkItemRelationship
): relationship is RelatedWorkItemRelationship & { item: NonNullable<RelatedWorkItemRelationship['item']> } => Boolean(relationship.item);

// Travel entry from work_items table
interface TravelWorkItem {
  id: string;
  item_number: string;
  title: string;
  status: string;
  created_at: string;
  scheduled_start_date?: string;
  custom_fields?: {
    travel_type?: string;
    parent_item_number?: string;
    start_location?: { lat: number; lng: number; accuracy?: number };
    end_location?: { lat: number; lng: number; accuracy?: number };
    start_odometer?: number;
    end_odometer?: number;
    distance_km?: number;
    vehicle_registration?: string;
    estimated_duration_minutes?: number;
    actual_duration_minutes?: number;
    travel_started_at?: string;
    travel_ended_at?: string;
  };
}

// Appointment with actual times
interface AppointmentWithTimes {
  id: string;
  item_number: string;
  title: string;
  status: string;
  scheduled_start?: string;
  scheduled_end?: string;
  custom_fields?: {
    travel_started_at?: string;
    arrived_at?: string;
    check_in_timestamp?: string;
    check_out_timestamp?: string;
    travel_time_minutes?: number;
    completed_travel_entry?: {
      duration_minutes?: number;
      distance_km?: number;
    };
  };
  assigned_user_name?: string;
}

// Enhanced summary from API with related items included
interface TimeEntriesSummary {
  total_entries: number;
  total_duration_hours: number;
  billable_duration_hours: number;
  non_billable_duration_hours: number;
  billable_amount: number;
  total_hours_formatted: string;
  related_items_count: number;
  travel_hours: number;
  travel_distance_km: number;
  onsite_hours: number;
  travel_entries_count: number;
  onsite_entries_count: number;
}

interface TimeEntriesTabProps {
  workItemId: string;
  isActive: boolean;
  userRole?: string;
  workItemStatus?: string;
  itemType?: string | null;
  /**
   * When false, manual journal time entries cannot be added, edited, or deleted (Team RBAC:
   * `operations_job_edit_timers_for_completion`). Owner/root pass true from the shell.
   * Defaults to true when omitted.
   */
  parentAllowsManualTimeFromOperations?: boolean;
}

function TimeEntriesTab({
  workItemId,
  isActive,
  userRole = 'user',
  workItemStatus = 'open',
  itemType,
  parentAllowsManualTimeFromOperations = true,
}: TimeEntriesTabProps) {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [travelEntries, setTravelEntries] = useState<TravelWorkItem[]>([]);
  const [appointments, setAppointments] = useState<AppointmentWithTimes[]>([]);
  const [summary, setSummary] = useState<TimeEntriesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [activeSection, setActiveSection] = useState<TimeEntriesSection>('all');
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({
    entry_date: getTodayStrSAST(),
    start_time: '',
    end_time: '',
    duration_hours: '',
    description: '',
    is_billable: true
  });

  // Load time entries with related items (travel, appointments) included
  const loadTimeEntries = useCallback(async () => {
    try {
      setLoading(true);
      // Use include_related=true to get time entries from all assignees' travel and work
      const response = await fetch(`/api/v1/work-items/${workItemId}/time-entries?include_related=true`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEntries(data.data || []);
          setSummary(data.summary || null);
        }
      }
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('Error loading time entries:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  // Load travel work_items and appointments linked to this job
  const loadTravelAndAppointments = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      // Fetch relationships to find travel and appointment items
      const relResponse = await fetch(`/api/v1/work-items/${workItemId}/relationships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (relResponse.ok) {
        const relData = await relResponse.json();
        if (relData.success && relData.data?.relationships) {
          const { incoming = {}, outgoing = {} } = relData.data.relationships as RelatedWorkItemRelationshipGroups;

          // Get all child items (travel entries are typically children of jobs)
          const childOfItems = [...(incoming.child_of || []), ...(outgoing.parent_of || [])];
          const scheduledForItems = [...(incoming.scheduled_for || []), ...(outgoing.scheduled_for || [])];

          // Filter travel items
          const travelItems: TravelWorkItem[] = childOfItems
            .filter(hasRelatedItem)
            .filter((rel) => rel.item.item_type === 'travel')
            .map((rel) => ({
              id: rel.item.id,
              item_number: rel.item.item_number,
              title: rel.item.title,
              status: rel.item.status,
              created_at: rel.item.created_at,
              custom_fields: (rel.item.custom_fields || {}) as TravelWorkItem['custom_fields']
            }));

          setTravelEntries(travelItems);

          // Filter appointment items
          const apptItems: AppointmentWithTimes[] = [...childOfItems, ...scheduledForItems]
            .filter(hasRelatedItem)
            .filter((rel) => rel.item.item_type === 'appointment')
            .map((rel) => ({
              id: rel.item.id,
              item_number: rel.item.item_number,
              title: rel.item.title,
              status: rel.item.status,
              scheduled_start: rel.item.scheduled_start,
              scheduled_end: rel.item.scheduled_end,
              custom_fields: (rel.item.custom_fields || {}) as AppointmentWithTimes['custom_fields'],
              assigned_user_name: rel.item.assignees?.[0]?.first_name
                ? `${rel.item.assignees[0].first_name} ${rel.item.assignees[0].last_name || ''}`.trim()
                : undefined
            }));

          setAppointments(apptItems);
        }
      }
    } catch (error) {
      console.error('Error loading travel and appointments:', error);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadTimeEntries();
      loadTravelAndAppointments();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadTimeEntries, loadTravelAndAppointments]);

  // Check if user can edit time entries based on role and job status
  const canEditTimeEntries = useMemo(() => {
    const isAdminOrOwner = userRole === 'admin' || userRole === 'owner' || userRole === 'root';
    const isSupervisor = userRole === 'supervisor';
    const isJobCompleted = isTerminalOrArchivedStatus(workItemStatus, itemType);
    if (isAdminOrOwner) return true;
    if (isSupervisor && !isJobCompleted) return true;
    return false;
  }, [userRole, workItemStatus, itemType]);

  const canMutateManualTimeEntries = useMemo(() => {
    if (!parentAllowsManualTimeFromOperations) {
      return false;
    }
    return canEditTimeEntries;
  }, [parentAllowsManualTimeFromOperations, canEditTimeEntries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canMutateManualTimeEntries) {
      if (!parentAllowsManualTimeFromOperations) {
        notify.warning('Your team role does not allow editing manual time entries on jobs (Team RBAC: edit timers for completion).');
      } else {
        notify.warning('You do not have permission to change time entries for this job.');
      }
      return;
    }

    if (!formData.duration_hours || parseFloat(formData.duration_hours) <= 0) {
      notify.error('Please enter a valid duration');
      return;
    }

    try {
      const method = editingEntry ? 'PUT' : 'POST';
      const url = editingEntry
        ? `/api/v1/work-items/${workItemId}/time-entries/${editingEntry.id}`
        : `/api/v1/work-items/${workItemId}/time-entries`;

      // Combine entry_date + start_time into started_at
      const started_at = formData.start_time
        ? `${formData.entry_date}T${formData.start_time}:00`
        : `${formData.entry_date}T00:00:00`;

      // Combine entry_date + end_time into ended_at (optional)
      const ended_at = formData.end_time
        ? `${formData.entry_date}T${formData.end_time}:00`
        : undefined;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          started_at,
          ended_at,
          duration_hours: parseFloat(formData.duration_hours),
          description: formData.description,
          is_billable: formData.is_billable,
          entry_type: 'manual' // Mark as manually added
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          notify.success(editingEntry ? 'Time entry updated' : 'Time entry added');
          await loadTimeEntries();
          setFormData({
            entry_date: getTodayStrSAST(),
            start_time: '',
            end_time: '',
            duration_hours: '',
            description: '',
            is_billable: true
          });
          setShowForm(false);
          setEditingEntry(null);
        }
      } else {
        notify.error('Failed to save time entry');
      }
    } catch (error) {
      console.error('Error adding time entry:', error);
      notify.error('Failed to save time entry');
    }
  };

  const handleEdit = (entry: TimeEntry) => {
    // Check role-based permissions first
    if (!canMutateManualTimeEntries) {
      if (!canEditTimeEntries) {
        const isJobCompleted = isTerminalOrArchivedStatus(workItemStatus, itemType);
        if (isJobCompleted) {
          notify.warning('Only admin or owner can edit time entries on terminal or archived jobs');
        } else {
          notify.warning('You do not have permission to edit time entries');
        }
      } else {
        notify.warning('Your team role does not allow editing manual time entries on jobs (Team RBAC: edit timers for completion).');
      }
      return;
    }

    setEditingEntry(entry);
    setFormData({
      entry_date: entry.entry_date || getTodayStrSAST(),
      start_time: entry.start_time ? entry.start_time.split('T')[1]?.substring(0, 5) || '' : '',
      end_time: entry.end_time ? entry.end_time.split('T')[1]?.substring(0, 5) || '' : '',
      duration_hours: String(entry.duration_hours || ''),
      description: entry.description || '',
      is_billable: entry.is_billable !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (entry: TimeEntry) => {
    // Check role-based permissions first
    if (!canMutateManualTimeEntries) {
      if (!canEditTimeEntries) {
        const isJobCompleted = isTerminalOrArchivedStatus(workItemStatus, itemType);
        if (isJobCompleted) {
          notify.warning('Only admin or owner can delete time entries on terminal or archived jobs');
        } else {
          notify.warning('You do not have permission to delete time entries');
        }
      } else {
        notify.warning('Your team role does not allow deleting manual time entries on jobs (Team RBAC: edit timers for completion).');
      }
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Time Entry',
      message: 'Are you sure you want to delete this time entry? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => { }
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/time-entries/${entry.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        notify.success('Time entry deleted');
        await loadTimeEntries();
      } else {
        notify.error('Failed to delete time entry');
      }
    } catch (error) {
      console.error('Error deleting time entry:', error);
      notify.error('Failed to delete time entry');
    }
  };

  // Use API summary for totals, fall back to local calculation
  const totalHours = summary?.total_duration_hours ?? entries.reduce((sum, entry) => {
    const hours = Number(entry.duration_hours) || 0;
    const minutes = (Number(entry.duration_minutes) || 0) / 60;
    return sum + hours + minutes;
  }, 0);

  const billableHours = summary?.billable_duration_hours ?? entries.filter(e => e.is_billable).reduce((sum, entry) => {
    const hours = Number(entry.duration_hours) || 0;
    const minutes = (Number(entry.duration_minutes) || 0) / 60;
    return sum + hours + minutes;
  }, 0);

  // Use API summary for travel stats, fall back to local calculation from travel work items
  const totalTravelHours = summary?.travel_hours ?? (travelEntries.reduce((sum, travel) => {
    const mins = travel.custom_fields?.actual_duration_minutes || travel.custom_fields?.estimated_duration_minutes || 0;
    return sum + mins;
  }, 0) / 60);

  const travelEntriesCount = summary?.travel_entries_count ?? travelEntries.length;

  // Use API summary for distance, fall back to travel entries
  const totalTravelKm = summary?.travel_distance_km ?? travelEntries.reduce((sum, travel) => {
    return sum + (travel.custom_fields?.distance_km || 0);
  }, 0);

  // Use API summary for on-site hours, fall back to appointments calculation
  const appointmentWorkHours = summary?.onsite_hours ?? (appointments.reduce((sum, appt) => {
    const checkIn = appt.custom_fields?.check_in_timestamp;
    const checkOut = appt.custom_fields?.check_out_timestamp;
    if (checkIn && checkOut) {
      const duration = (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60);
      return sum + duration;
    }
    return sum;
  }, 0) / 60);

  const onsiteEntriesCount = summary?.onsite_entries_count ?? appointments.filter(a => a.custom_fields?.check_out_timestamp).length;

  // Classify entries by source
  const mobileEntries = entries.filter(e => e.tracked_via === 'mobile_app' || e.entry_type === 'mobile_checkin');
  const manualEntries = entries.filter(e => e.tracked_via === 'manual' || (!e.tracked_via && e.entry_type !== 'mobile_checkin'));

  const isDark = currentTheme.mode === 'dark';

  const timeEntrySectionTabs: Array<{ key: TimeEntriesSection; label: string; count: number }> = [
    { key: 'all', label: 'All', count: entries.length + travelEntries.length + appointments.length },
    { key: 'travel', label: '🚗 Travel', count: travelEntries.length },
    { key: 'work', label: '🔧 Appointments', count: appointments.length },
    { key: 'manual', label: '✍️ Manual', count: entries.length }
  ];

  // Format time helper
  const formatTimeOnly = (dateStr: string | undefined) => {
    if (!dateStr) return '--:--';
    return formatTimeSAST(dateStr);
  };

  if (loading) {
    return (
      <InlineLoadingPill label="Loading time entries..." primaryColor="#3b82f6" className="py-12" />
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Summary Stats */}
      <div className={`rounded-xl p-6 border ${isDark ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Clock className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Time Tracking</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'} ({mobileEntries.length} from mobile, {manualEntries.length} manual)
                {summary?.related_items_count && summary.related_items_count > 0 && (
                  <span className="ml-1 text-blue-500">• includes {summary.related_items_count} related item{summary.related_items_count !== 1 ? 's' : ''}</span>
                )}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-right">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Hours</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                {totalHours.toFixed(1)}h
              </p>
            </div>
            <div className="text-right">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Billable</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                {billableHours.toFixed(1)}h
              </p>
            </div>
          </div>
        </div>

        {/* Extended Stats - Travel and Appointment Work */}
        {(travelEntriesCount > 0 || onsiteEntriesCount > 0 || travelEntries.length > 0 || appointments.length > 0) && (
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t ${isDark ? 'border-blue-800' : 'border-blue-200'}`}>
            {/* Travel Time */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                🚗 Travel Time
              </p>
              <p className={`text-xl font-bold ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                {totalTravelHours.toFixed(1)}h
              </p>
              <p className={`text-xs ${isDark ? 'text-orange-400/70' : 'text-orange-600/70'}`}>
                {travelEntriesCount || travelEntries.length} trip{(travelEntriesCount || travelEntries.length) !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Travel Distance */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                📍 Distance
              </p>
              <p className={`text-xl font-bold ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                {totalTravelKm.toFixed(1)} km
              </p>
            </div>

            {/* On-site Work Time */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                🔧 On-site Work
              </p>
              <p className={`text-xl font-bold ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                {appointmentWorkHours.toFixed(1)}h
              </p>
              <p className={`text-xs ${isDark ? 'text-green-400/70' : 'text-green-600/70'}`}>
                {onsiteEntriesCount} session{onsiteEntriesCount !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Appointments */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                📅 Appointments
              </p>
              <p className={`text-xl font-bold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                {appointments.length}
              </p>
              <p className={`text-xs ${isDark ? 'text-purple-400/70' : 'text-purple-600/70'}`}>
                {appointments.filter(a => isTerminalWorkItemStatus(a.status, 'appointment')).length} done
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Section Tabs */}
      {(travelEntries.length > 0 || appointments.length > 0) && (
        <div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {timeEntrySectionTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveSection(tab.key)}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeSection === tab.key
                ? `${isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow-sm'}`
                : `${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
                }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      )}

      {/* Travel Entries Section */}
      {(activeSection === 'all' || activeSection === 'travel') && travelEntries.length > 0 && (
        <div className="space-y-3">
          <h4 className={`text-sm font-semibold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
            🚗 Travel Entries
          </h4>
          {travelEntries.map(travel => (
            <div
              key={travel.id}
              className={`rounded-xl p-4 border ${isDark ? 'bg-orange-900/10 border-orange-800/50' : 'bg-orange-50 border-orange-200'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {travel.title}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getOperationalStatusBadgeClass(travel.status, isDark, 'travel')}`}>
                      {travel.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {travel.custom_fields?.actual_duration_minutes && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Duration</p>
                        <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {travel.custom_fields.actual_duration_minutes} min
                        </p>
                      </div>
                    )}
                    {travel.custom_fields?.distance_km && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Distance</p>
                        <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {travel.custom_fields.distance_km.toFixed(1)} km
                        </p>
                      </div>
                    )}
                    {travel.custom_fields?.vehicle_registration && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Vehicle</p>
                        <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {travel.custom_fields.vehicle_registration}
                        </p>
                      </div>
                    )}
                    {(travel.custom_fields?.start_odometer || travel.custom_fields?.end_odometer) && (
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Odometer</p>
                        <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {travel.custom_fields.start_odometer || '?'} → {travel.custom_fields.end_odometer || '?'} km
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Appointments Section */}
      {(activeSection === 'all' || activeSection === 'work') && appointments.length > 0 && (
        <div className="space-y-3">
          <h4 className={`text-sm font-semibold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            🔧 Appointment Work Sessions
          </h4>
          {appointments.map(appt => {
            const hasWork = appt.custom_fields?.check_in_timestamp && appt.custom_fields?.check_out_timestamp;
            const checkIn = appt.custom_fields?.check_in_timestamp;
            const checkOut = appt.custom_fields?.check_out_timestamp;
            const workDuration = hasWork
              ? ((new Date(checkOut!).getTime() - new Date(checkIn!).getTime()) / (1000 * 60 * 60)).toFixed(1)
              : null;

            return (
              <div
                key={appt.id}
                className={`rounded-xl p-4 border ${isDark ? 'bg-green-900/10 border-green-800/50' : 'bg-green-50 border-green-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {appt.title}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getOperationalStatusBadgeClass(appt.status, isDark, 'appointment')}`}>
                        {appt.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-mono flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        #{appt.item_number}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(appt.item_number, 'Item number');
                          }}
                          className="p-0.5 hover:bg-gray-500/10 rounded transition-colors"
                          title="Copy item number"
                        >
                          <Copy className="w-2.5 h-2.5" />
                        </button>
                      </span>
                      <span className={`text-[10px] font-mono flex items-center gap-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        ID: {appt.id}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(appt.id, 'ID');
                          }}
                          className="p-0.5 hover:bg-gray-500/10 rounded transition-colors"
                          title="Copy UUID"
                        >
                          <Copy className="w-2 h-2" />
                        </button>
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                      {/* Planned */}
                      <div>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Planned</p>
                        <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatTimeOnly(appt.scheduled_start)} - {formatTimeOnly(appt.scheduled_end)}
                        </p>
                      </div>

                      {/* Arrived */}
                      {appt.custom_fields?.arrived_at && (
                        <div>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Arrived</p>
                          <p className={`font-medium ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                            {formatTimeOnly(appt.custom_fields.arrived_at)}
                          </p>
                        </div>
                      )}

                      {/* Started */}
                      {checkIn && (
                        <div>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Started</p>
                          <p className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                            {formatTimeOnly(checkIn)}
                          </p>
                        </div>
                      )}

                      {/* Completed */}
                      {checkOut && (
                        <div>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Completed</p>
                          <p className={`font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                            {formatTimeOnly(checkOut)}
                          </p>
                        </div>
                      )}

                      {/* Work Duration */}
                      {workDuration && (
                        <div>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Worked</p>
                          <p className={`font-bold ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                            {workDuration}h
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Travel time for this appointment */}
                    {appt.custom_fields?.completed_travel_entry && (
                      <div className={`mt-2 text-xs ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                        🚗 Travel: {appt.custom_fields.completed_travel_entry.duration_minutes || 0} min
                        {appt.custom_fields.completed_travel_entry.distance_km && (
                          <span> • {appt.custom_fields.completed_travel_entry.distance_km.toFixed(1)} km</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Time Entry Button */}
      {(activeSection === 'all' || activeSection === 'manual') && !showForm && (
        <button
          type="button"
          onClick={() => {
            if (!canMutateManualTimeEntries) {
              notify.warning(
                parentAllowsManualTimeFromOperations
                  ? 'You do not have permission to add time entries for this job.'
                  : 'Your team role does not allow adding manual time entries on jobs (Team RBAC: edit timers for completion).',
              );
              return;
            }
            setEditingEntry(null);
            setFormData({
              entry_date: getTodayStrSAST(),
              start_time: '',
              end_time: '',
              duration_hours: '',
              description: '',
              is_billable: true
            });
            setShowForm(true);
          }}
          disabled={!canMutateManualTimeEntries}
          title={!canMutateManualTimeEntries ? 'Time entry changes are not allowed for your role on this job.' : undefined}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
        >
          <Plus className="w-5 h-5" />
          Add Time Entry
        </button>
      )}

      {/* Add Time Entry Form - Modal Overlay */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowForm(false); setEditingEntry(null); } }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <form onSubmit={handleSubmit} className={`relative w-full max-w-lg rounded-xl p-6 space-y-4 border shadow-2xl ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <h4 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
              </h4>
              <button type="button" onClick={() => { setShowForm(false); setEditingEntry(null); }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.entry_date}
                  onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  required
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Duration (hours) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.05"
                  min="0.01"
                  value={formData.duration_hours}
                  onChange={(e) => {
                    const duration = parseFloat(e.target.value) || 0;
                    const newFormData = { ...formData, duration_hours: e.target.value };

                    // Auto-calculate end_time if start_time is set
                    if (formData.start_time && duration > 0) {
                      const [hours, minutes] = formData.start_time.split(':').map(Number);
                      const startMinutes = hours * 60 + minutes;
                      const endMinutes = startMinutes + (duration * 60);
                      const endHours = Math.floor(endMinutes / 60) % 24;
                      const endMins = Math.floor(endMinutes % 60);
                      newFormData.end_time = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
                    }

                    setFormData(newFormData);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  placeholder="2.5"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Time (Optional)
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => {
                    const newFormData = { ...formData, start_time: e.target.value };

                    // Auto-calculate end_time if duration is set
                    if (e.target.value && formData.duration_hours) {
                      const duration = parseFloat(formData.duration_hours);
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      const startMinutes = hours * 60 + minutes;
                      const endMinutes = startMinutes + (duration * 60);
                      const endHours = Math.floor(endMinutes / 60) % 24;
                      const endMins = Math.floor(endMinutes % 60);
                      newFormData.end_time = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
                    }

                    setFormData(newFormData);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Time (Optional)
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => {
                    const newFormData = { ...formData, end_time: e.target.value };

                    // Auto-calculate duration if start_time is set
                    if (formData.start_time && e.target.value) {
                      const [startHours, startMinutes] = formData.start_time.split(':').map(Number);
                      const [endHours, endMinutes] = e.target.value.split(':').map(Number);
                      const startTotalMinutes = startHours * 60 + startMinutes;
                      let endTotalMinutes = endHours * 60 + endMinutes;

                      // Handle overnight shifts
                      if (endTotalMinutes < startTotalMinutes) {
                        endTotalMinutes += 24 * 60;
                      }

                      const durationMinutes = endTotalMinutes - startTotalMinutes;
                      newFormData.duration_hours = String((durationMinutes / 60).toFixed(2));
                    }

                    setFormData(newFormData);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                rows={2}
                placeholder="What did you work on?"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_billable"
                checked={formData.is_billable}
                onChange={(e) => setFormData({ ...formData, is_billable: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="is_billable" className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Billable time
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingEntry(null);
                }}
                className={`flex-1 px-4 py-2 border rounded-lg font-medium transition-colors ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                {editingEntry ? 'Update Entry' : 'Add Entry'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Time Entries List (Manual entries) */}
      {(activeSection === 'all' || activeSection === 'manual') && (
        entries.length === 0 ? (
          <div className={`text-center py-12 rounded-lg border-2 border-dashed ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            <Clock className={`w-16 h-16 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className="font-medium text-lg mb-1">No Manual Time Entries</p>
            <p className="text-sm">Add manual entries or wait for mobile check-ins to appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className={`text-sm font-semibold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
              ✍️ All Time Entries
            </h4>
            {entries.map((entry) => {
              const isMobileEntry = entry.tracked_via === 'mobile_app' || entry.entry_type === 'mobile_checkin';
              const isTravelEntry = entry.activity_type === 'travel' || entry.source_item_type === 'travel';
              const isFromRelated = entry.is_from_related || entry.source_item_type;
              const hasGPS = entry.gps_check_in || entry.gps_check_out || entry.metadata?.gps_check_in || entry.metadata?.gps_check_out;

              // Get entry type label
              const getEntryTypeLabel = () => {
                if (isTravelEntry) return '🚗 Travel';
                if (entry.activity_type === 'on_site' || entry.source_item_type === 'appointment') return '🔧 On-site Work';
                if (isMobileEntry) return '📱 Mobile Check-in';
                return '✍️ Manual Entry';
              };

              // Get entry type color
              const getEntryTypeColor = () => {
                if (isTravelEntry) return isDark ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700';
                if (entry.activity_type === 'on_site' || entry.source_item_type === 'appointment') return isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700';
                if (isMobileEntry) return isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
                return isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700';
              };

              return (
                <div
                  key={entry.id}
                  className={`rounded-xl overflow-hidden border transition-all hover:shadow-md ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}
                >
                  {/* Header */}
                  <div className={`px-6 py-3 flex items-center justify-between ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEntryTypeColor()}`}>
                        {getEntryTypeLabel()}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {formatDateSAST(entry.entry_date || entry.started_at || new Date(), {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      {(entry.user_name || entry.user_display_name) && (
                        <span className={`text-sm flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          <User className="w-3 h-3" />
                          {entry.user_display_name || entry.user_name}
                        </span>
                      )}
                      {/* Show source item if from a related item */}
                      {isFromRelated && entry.source_item_number && (
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          from {entry.source_item_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {(Number(entry.duration_hours || 0) + (Number(entry.duration_minutes || 0) / 60)).toFixed(2)}h
                      </span>
                      {entry.is_billable && (
                        <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
                          Billable
                        </span>
                      )}
                      {/* Show edit/delete for users with permission - admins can edit ALL entries including mobile */}
                      {canMutateManualTimeEntries && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(entry)}
                            className={`p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                            title={`Edit entry${isMobileEntry ? ' (GPS-verified - adjust for billing)' : ''}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(entry)}
                            className={`p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 ${isDark ? 'text-red-400' : 'text-red-600'}`}
                            title="Delete entry"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {hasGPS ? (
                      <div className="grid grid-cols-2 gap-6">
                        {/* Check-In with GPS */}
                        {entry.gps_check_in && (
                          <div>
                            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold mb-3">
                              <PlayCircle className="w-5 h-5" />
                              <span>Check-In</span>
                            </div>
                            {entry.start_time && (
                              <div className={`flex items-center gap-2 mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                <Clock className="w-4 h-4" />
                                <span className="font-mono text-lg">
                                  {formatTimeSAST(entry.start_time)}
                                </span>
                              </div>
                            )}
                            <div className={`rounded-lg p-3 border ${isDark ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                              <div className="flex items-start gap-2">
                                <MapPin className={`w-4 h-4 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                                <div>
                                  <p className={`text-xs font-medium mb-1 ${isDark ? 'text-green-300' : 'text-green-700'}`}>GPS Verified</p>
                                  <a
                                    href={`https://www.google.com/maps?q=${entry.gps_check_in.lat},${entry.gps_check_in.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`text-xs font-mono underline ${isDark ? 'text-green-400' : 'text-green-600'}`}
                                  >
                                    {entry.gps_check_in.lat.toFixed(6)}, {entry.gps_check_in.lng.toFixed(6)}
                                  </a>
                                  {entry.gps_check_in.accuracy && (
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      Accuracy: ±{entry.gps_check_in.accuracy.toFixed(0)}m
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Check-Out with GPS */}
                        {entry.gps_check_out ? (
                          <div>
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-semibold mb-3">
                              <StopCircle className="w-5 h-5" />
                              <span>Check-Out</span>
                            </div>
                            {entry.end_time && (
                              <div className={`flex items-center gap-2 mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                <Clock className="w-4 h-4" />
                                <span className="font-mono text-lg">
                                  {formatTimeSAST(entry.end_time)}
                                </span>
                              </div>
                            )}
                            <div className={`rounded-lg p-3 border ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-start gap-2">
                                <MapPin className={`w-4 h-4 mt-0.5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                                <div>
                                  <p className={`text-xs font-medium mb-1 ${isDark ? 'text-red-300' : 'text-red-700'}`}>GPS Verified</p>
                                  <a
                                    href={`https://www.google.com/maps?q=${entry.gps_check_out.lat},${entry.gps_check_out.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`text-xs font-mono underline ${isDark ? 'text-red-400' : 'text-red-600'}`}
                                  >
                                    {entry.gps_check_out.lat.toFixed(6)}, {entry.gps_check_out.lng.toFixed(6)}
                                  </a>
                                  {entry.gps_check_out.accuracy && (
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      Accuracy: ±{entry.gps_check_out.accuracy.toFixed(0)}m
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 font-semibold mb-3">
                              <Clock className="w-5 h-5" />
                              <span>In Progress</span>
                            </div>
                            <div className={`rounded-lg p-3 border ${isDark ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                              <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                                Worker has not checked out yet
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Manual entry without GPS
                      <div className="flex items-center gap-6">
                        {(entry.start_time || entry.end_time) && (
                          <div className="flex items-center gap-4">
                            {entry.start_time && (
                              <div>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Start</p>
                                <p className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {formatTimeSAST(entry.start_time)}
                                </p>
                              </div>
                            )}
                            {entry.end_time && (
                              <div>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>End</p>
                                <p className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {formatTimeSAST(entry.end_time)}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        {entry.description && (
                          <div className="flex-1">
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Description</p>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{entry.description}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ============================================================================
// RELATIONSHIPS COMPONENT
// ============================================================================

interface Relationship {
  relationship_id: string;
  relationship_type: string;
  notes?: string;
  item: {
    id: string;
    item_type: string;
    item_number: string;
    title: string;
    status: string;
    priority: string;
  };
}

interface RelationshipsData {
  outgoing: Record<string, Relationship[]>;
  incoming: Record<string, Relationship[]>;
}

interface RelationshipSearchResult {
  id: string;
  item_type: string;
  item_number: string;
  title: string;
}

function RelationshipsTab({ workItemId, onOpenItem, isActive }: { workItemId: string; onOpenItem?: (itemId: string) => void; isActive: boolean }) {
  const [relationships, setRelationships] = useState<RelationshipsData>({ outgoing: {}, incoming: {} });
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RelationshipSearchResult[]>([]);
  const [selectedType, setSelectedType] = useState<string>('relates_to');
  const [notes, setNotes] = useState('');
  const hasLoadedRef = useRef<string | boolean>(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { currentTheme: relTheme } = useTheme();
  const isDark = relTheme.mode === 'dark';

  const loadRelationships = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/relationships`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRelationships(data.data.relationships || { outgoing: {}, incoming: {} });
        }
      }
    } catch (error) {
      console.error('Error loading relationships:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive) {
      // Reset hasLoadedRef when workItemId changes
      if (!hasLoadedRef.current || hasLoadedRef.current !== workItemId) {
        loadRelationships();
        hasLoadedRef.current = workItemId;
      }
    }
  }, [isActive, loadRelationships, workItemId]);

  // Clear pending search debounce on unmount to prevent
  // setState-after-unmount and stray fetch calls.
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, []);

  const searchItems = useCallback((query: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/api/v1/work-items?search=${encodeURIComponent(query)}&limit=10`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSearchResults((data.data || []).filter((item: RelationshipSearchResult) => item.id !== workItemId));
          }
        }
      } catch (error) {
        console.error('Error searching items:', error);
      }
    }, 300);
  }, [workItemId]);

  const handleAddRelationship = async (targetItemId: string) => {
    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/relationships`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_item_id: targetItemId,
          relationship_type: selectedType,
          notes: notes || undefined
        })
      });

      if (response.ok) {
        setShowAddForm(false);
        setSearchQuery('');
        setSearchResults([]);
        setNotes('');
        loadRelationships();
      }
    } catch (error) {
      console.error('Error adding relationship:', error);
    }
  };

  const handleRemoveRelationship = async (relationshipId: string) => {
    if (!(await confirm({ message: 'Remove this relationship?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/relationships/${relationshipId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        loadRelationships();
      }
    } catch (error) {
      console.error('Error removing relationship:', error);
    }
  };

  const getRelationshipLabel = (type: string): string => {
    const labels: Record<string, string> = {
      blocks: '🚫 Blocks',
      blocked_by: '⛔ Blocked By',
      depends_on: '🔗 Depends On',
      depended_by: '📌 Depended By',
      child_of: '👶 Child Of',
      parent_of: '👨 Parent Of',
      relates_to: '🔗 Related To',
      duplicates: '📋 Duplicates',
      duplicated_by: '📋 Duplicated By',
      causes: '⚡ Causes',
      caused_by: '⚡ Caused By',
      precedes: '⏮️ Precedes',
      follows: '⏭️ Follows',
      linked: '🔗 Linked'
    };
    return labels[type] || type;
  };

  const renderRelationshipCard = (rel: Relationship) => {
    const isBlocking = rel.relationship_type === 'blocks' || rel.relationship_type === 'blocked_by';

    return (
      <div
        key={rel.relationship_id}
        className={`p-3 rounded-lg border ${isBlocking
          ? isDark ? 'border-red-700 bg-red-900/30' : 'border-red-200 bg-red-50'
          : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          } hover:shadow-sm transition-shadow`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 text-xs rounded ${rel.item.item_type === 'ticket' ? 'bg-blue-100 text-blue-800' :
                rel.item.item_type === 'job' ? 'bg-green-100 text-green-800' :
                  rel.item.item_type === 'project' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                }`}>
                {rel.item.item_type}
              </span>
              <span className="text-xs text-gray-500">#{rel.item.item_number}</span>
              <span className={`px-2 py-0.5 text-xs rounded ${getOperationalStatusBadgeClass(rel.item.status, isDark, rel.item.item_type)}`}>
                {rel.item.status}
              </span>
            </div>
            <button
              onClick={() => onOpenItem?.(rel.item.id)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 text-left break-words"
            >
              {rel.item.title}
            </button>
            {rel.notes && (
              <p className="text-xs text-gray-600 mt-1">{rel.notes}</p>
            )}
          </div>
          <button
            onClick={() => handleRemoveRelationship(rel.relationship_id)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
            title="Remove relationship"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <InlineLoadingPill label="Loading relationships..." primaryColor="#6366f1" className="py-8" />
    );
  }

  const allOutgoing = Object.entries(relationships?.outgoing || {}).flatMap(([type, rels]) => rels.map(r => ({ ...r, type })));
  const allIncoming = Object.entries(relationships?.incoming || {}).flatMap(([type, rels]) => rels.map(r => ({ ...r, type })));
  const totalCount = allOutgoing.length + allIncoming.length;

  return (
    <div className="space-y-6">
      {/* Add Relationship Form */}
      {showAddForm ? (
        <div className={`border rounded-lg p-4 space-y-4 ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between">
            <h3 className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Add Relationship</h3>
            <button
              onClick={() => {
                setShowAddForm(false);
                setSearchQuery('');
                setSearchResults([]);
                setNotes('');
              }}
              className={isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Relationship Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
            >
              <option value="relates_to">🔗 Related To</option>
              <option value="blocks">🚫 Blocks</option>
              <option value="blocked_by">⛔ Blocked By</option>
              <option value="depends_on">🔗 Depends On</option>
              <option value="child_of">👶 Child Of</option>
              <option value="parent_of">👨 Parent Of</option>
              <option value="duplicates">📋 Duplicates</option>
              <option value="causes">⚡ Causes</option>
              <option value="precedes">⏮️ Precedes</option>
              <option value="follows">⏭️ Follows</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Search Work Items</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchItems(e.target.value);
              }}
              placeholder="Type item number or title..."
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
            />
          </div>

          {searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-2">
              {searchResults.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleAddRelationship(item.id)}
                  className={`w-full text-left p-3 border rounded-lg transition-all ${isDark ? 'border-gray-700 hover:bg-gray-700 hover:shadow-sm' : 'border-gray-200 hover:bg-white hover:shadow-sm'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>{item.item_type}</span>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>#{item.item_number}</span>
                  </div>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{item.title}</p>
                </button>
              ))}
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add context about this relationship..."
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
            />
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className={`w-full px-4 py-3 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${isDark ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400' : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'}`}
        >
          <Link2 className="w-5 h-5" />
          Add Relationship
        </button>
      )}

      {/* Relationships Display */}
      {totalCount === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Link2 className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p>No relationships yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Outgoing Relationships */}
          {Object.entries(relationships.outgoing).map(([type, rels]) => (
            <div key={`out-${type}`}>
              <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{getRelationshipLabel(type)}</h3>
              <div className="space-y-2">
                {rels.map((rel) => renderRelationshipCard(rel))}
              </div>
            </div>
          ))}

          {/* Incoming Relationships */}
          {Object.entries(relationships.incoming).map(([type, rels]) => (
            <div key={`in-${type}`}>
              <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {getRelationshipLabel(type)} (Incoming)
              </h3>
              <div className="space-y-2">
                {rels.map((rel) => renderRelationshipCard(rel))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ATTACHMENTS COMPONENT
// ============================================================================

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_size_formatted: string;
  file_type?: string;
  mime_type?: string;
  description?: string;
  created_at: string;
  uploader_name: string;
  download_url: string;
}

function AttachmentsTab({ workItemId, isActive, showFieldServiceTabs, workItem }: { workItemId: string; isActive: boolean; showFieldServiceTabs?: boolean; workItem?: WorkItem }) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const hasLoadedRef = useRef(false);

  const loadAttachments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/attachments?include_related=true`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAttachments(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  const handleFiles = useCallback(async (files: FileList) => {
    setUploading(true);
    const uploadedFiles: string[] = [];
    const failedFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        // Upload file using FormData to media upload endpoint
        const formData = new FormData();
        formData.append('file', file);
        formData.append('work_item_id', workItemId);

        // Determine category based on file type
        const isImage = file.type.startsWith('image/');
        if (isImage) {
          formData.append('photo_type', 'attachment');
        }

        const response = await fetch('/api/v1/work-items/media/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: formData
        });

        if (response.ok) {
          const uploadResult = await response.json();
          const fileUrl = uploadResult.url || uploadResult.photo?.url;

          // Create a work_item_attachments record so the file appears in the Attachments tab
          if (fileUrl) {
            try {
              await fetch(`/api/v1/work-items/${workItemId}/attachments`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${getAuthToken()}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  file_name: file.name,
                  file_path: fileUrl,
                  file_size: file.size,
                  file_type: file.type || undefined,
                  mime_type: file.type || undefined,
                  category: 'attachment',
                }),
              });
            } catch (attErr) {
              console.warn(`Failed to create attachment record for ${file.name}:`, attErr);
            }
          }

          uploadedFiles.push(file.name);
        } else {
          const error = await response.json();
          console.error(`Failed to upload ${file.name}:`, error);
          failedFiles.push(file.name);
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.error(`Error uploading ${file.name}:`, error);
        failedFiles.push(file.name);
      }
    }

    setUploading(false);
    await loadAttachments();

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Show result notification
    if (uploadedFiles.length > 0 && failedFiles.length === 0) {
      notify.success(`${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''} uploaded successfully`);
    } else if (uploadedFiles.length > 0 && failedFiles.length > 0) {
      notify.warning(`${uploadedFiles.length} uploaded, ${failedFiles.length} failed`);
    } else if (failedFiles.length > 0) {
      notify.error(`Failed to upload ${failedFiles.length} file${failedFiles.length > 1 ? 's' : ''}`);
    }
  }, [loadAttachments, workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadAttachments();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadAttachments]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDelete = async (attachmentId: string, fileName: string) => {
    await confirm({
      title: 'Delete Attachment',
      message: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/v1/work-items/${workItemId}/attachments/${attachmentId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`
            }
          });

          if (response.ok) {
            setAttachments((prevAttachments) => prevAttachments.filter(a => a.id !== attachmentId));
            notify.success('Attachment deleted');
          } else {
            notify.error('Failed to delete attachment');
          }
        } catch (error) {
          console.error('Error deleting attachment:', error);
          notify.error('Failed to delete attachment');
        }
      }
    });
  };

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return '📄';
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📕';
    if (fileType.includes('word') || fileType.includes('document')) return '📘';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📗';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📙';
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('tar')) return '📦';
    if (fileType.includes('text')) return '📝';
    return '📄';
  };

  const isImage = (fileType?: string) => {
    return fileType?.startsWith('image/') || false;
  };

  const isPdf = (att: Attachment) => {
    const ft = (att.file_type || att.mime_type || '').toLowerCase();
    return ft === 'application/pdf' || ft.endsWith('.pdf');
  };

  const isPreviewableDoc = (att: Attachment) => {
    const ft = (att.file_type || att.mime_type || '').toLowerCase();
    // Browser-native iframe preview for text, HTML, and common doc formats via proxy
    return ft.startsWith('text/') ||
      ft.includes('html') ||
      ft.includes('word') ||
      ft.includes('document') ||
      ft.includes('excel') ||
      ft.includes('spreadsheet') ||
      ft.includes('powerpoint') ||
      ft.includes('presentation') ||
      ft.includes('csv') ||
      ft.includes('json') ||
      ft.includes('xml');
  };

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  if (loading) {
    return (
      <InlineLoadingPill label="Loading attachments..." primaryColor="#2563eb" className="py-8" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
          ? isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-400 bg-blue-50'
          : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
          }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
        {uploading ? (
          <>
            <div className="mb-3 flex justify-center">
              <InlineLoadingPill label="Uploading files..." primaryColor="#2563eb" />
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Please wait while your files are being uploaded
            </p>
          </>
        ) : (
          <>
            <Paperclip className={`w-12 h-12 mx-auto mb-3 ${dragActive ? 'text-blue-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className={`mb-2 font-medium ${dragActive ? 'text-blue-600' : isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {dragActive ? 'Drop files here!' : 'Drag and drop files here'}
            </p>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>or</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Files
            </button>
            <p className={`text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Supports all file types up to 50MB
            </p>
          </>
        )}
      </div>

      {/* Attachments List */}
      {attachments.length === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Paperclip className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p>No attachments yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Images Section - Inline Preview */}
          {attachments.filter(a => isImage(a.file_type)).length > 0 && (
            <div className="space-y-3">
              <h4 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Images ({attachments.filter(a => isImage(a.file_type)).length})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {attachments.filter(a => isImage(a.file_type)).map((attachment) => (
                  <div
                    key={attachment.id}
                    className={`group relative border rounded-lg overflow-hidden ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div
                      className="aspect-square cursor-pointer"
                      onClick={() => setPreviewAttachment(attachment)}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={attachment.download_url}
                        alt={attachment.file_name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="system-ui" font-size="12">Image</text></svg>';
                        }}
                      />
                    </div>
                    {/* Overlay with actions */}
                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t ${isDark ? 'from-gray-900/90' : 'from-black/70'} to-transparent flex flex-col justify-end p-2`}>
                      <p className="text-white text-xs truncate mb-1">{attachment.file_name}</p>
                      <div className="flex gap-1">
                        <a
                          href={attachment.download_url}
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 px-2 py-1 text-xs bg-white/20 hover:bg-white/30 text-white rounded text-center transition-colors"
                        >
                          Download
                        </a>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(attachment.id, attachment.file_name);
                          }}
                          className="px-2 py-1 text-xs bg-red-500/70 hover:bg-red-500 text-white rounded transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other Files Section */}
          {attachments.filter(a => !isImage(a.file_type)).length > 0 && (
            <div className="space-y-3">
              <h4 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Files ({attachments.filter(a => !isImage(a.file_type)).length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {attachments.filter(a => !isImage(a.file_type)).map((attachment) => (
                  <div
                    key={attachment.id}
                    className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-3xl flex-shrink-0">
                        {getFileIcon(attachment.file_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                              {attachment.file_name}
                            </h4>
                            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {attachment.file_size_formatted} • {formatDateSAST(attachment.created_at, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              by {attachment.uploader_name}
                            </p>
                            {attachment.description && (
                              <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{attachment.description}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDelete(attachment.id, attachment.file_name)}
                            className={`p-1 transition-colors flex-shrink-0 ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => setPreviewAttachment(attachment)}
                            className={`px-3 py-1 text-xs rounded transition-colors ${isDark ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900/70' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                          >
                            View
                          </button>
                          <a
                            href={attachment.download_url}
                            download
                            className={`px-3 py-1 text-xs rounded transition-colors ${isDark ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900/70' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* File Preview Modal */}
      {previewAttachment && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewAttachment(null)}
        >
          <div className="relative max-w-5xl w-full h-full flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              <a
                href={previewAttachment.download_url}
                download
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download
              </a>
              <button
                onClick={() => setPreviewAttachment(null)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="absolute top-4 left-4 z-10">
              <p className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-lg">{previewAttachment.file_name}</p>
            </div>
            {isImage(previewAttachment.file_type) ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={previewAttachment.download_url}
                alt={previewAttachment.file_name}
                className="max-w-full max-h-[90vh] rounded-lg object-contain"
              />
            ) : isPdf(previewAttachment) ? (
              <iframe
                src={`/api/v1/proxy-file?${new URLSearchParams({ url: previewAttachment.download_url, filename: previewAttachment.file_name }).toString()}`}
                className="w-full h-[90vh] rounded-lg bg-white"
                title={previewAttachment.file_name}
              />
            ) : isPreviewableDoc(previewAttachment) ? (
              <iframe
                src={`/api/v1/proxy-file?${new URLSearchParams({ url: previewAttachment.download_url, filename: previewAttachment.file_name }).toString()}`}
                className="w-full h-[90vh] rounded-lg bg-white"
                title={previewAttachment.file_name}
              />
            ) : (
              <div className="text-center text-white">
                <FileText className="w-24 h-24 mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">{getFileIcon(previewAttachment.file_type)} {previewAttachment.file_name}</h3>
                <p className="text-gray-400 mb-4">Preview not available for this file type</p>
                <a
                  href={previewAttachment.download_url}
                  download
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 mx-auto transition-colors"
                >
                  <Download className="w-5 h-5" /> Download File ({previewAttachment.file_size_formatted})
                </a>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ============================================================================
// CHECKLISTS COMPONENT
// NOTE: ChecklistsTab is superseded by the imported WorkBreakdownTab component.
// ============================================================================

type ChecklistTemplateApi = {
  template_key?: string;
  template_label?: string;
  applies_to_subtype?: string;
  checklist_template?: {
    items?: Array<{
      text: string;
      required?: boolean;
      category?: string;
      children?: Array<{ text: string; required?: boolean; category?: string }>;
    }>;
  };
};

/** Row shape returned by `/api/v1/work-items/:id/checklists` (legacy checklist tab). */
interface WorkItemChecklistRow {
  id: string;
  item_text: string;
  is_checked: boolean;
  is_required?: boolean;
  category?: string;
  /** Plain name string or nested user object from API */
  checked_by?: string | { name?: string | null } | null;
  checked_at?: string;
  children?: WorkItemChecklistRow[];
}

function formatChecklistCheckedByLabel(
  checkedBy: WorkItemChecklistRow['checked_by'],
): string {
  if (checkedBy == null || checkedBy === '') return '';
  if (typeof checkedBy === 'string') return checkedBy;
  return checkedBy.name?.trim() || '';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ChecklistsTab({ workItemId, isActive }: { workItemId: string; isActive: boolean }) {
  const [items, setItems] = useState<WorkItemChecklistRow[]>([]);
  const [flatItems, setFlatItems] = useState<WorkItemChecklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItemText, setNewItemText] = useState('');
  const [addingSubItemTo, setAddingSubItemTo] = useState<string | null>(null);
  const [subItemText, setSubItemText] = useState('');
  const [adding, setAdding] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState<ChecklistTemplateApi[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const hasLoadedRef = useRef(false);
  const { currentTheme: clTheme } = useTheme();
  const isDark = clTheme.mode === 'dark';

  const loadChecklists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/checklists`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setItems(data.data || []);
          setFlatItems(data.flat || data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading checklists:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadChecklists();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadChecklists]);

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await fetch('/api/v1/checklist-templates', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTemplates(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading checklist templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openTemplateModal = () => {
    setShowTemplateModal(true);
    loadTemplates();
  };

  const applyTemplate = async (template: ChecklistTemplateApi) => {
    try {
      setApplyingTemplate(true);

      const templateItems = template.checklist_template?.items || [];

      // Build bulk items with children
      const bulkItems = templateItems.map((templateItem, i) => ({
        item_text: templateItem.text,
        position: i,
        is_required: templateItem.required || false,
        category: templateItem.category || 'General',
        children: templateItem.children?.map((child, ci) => ({
          item_text: child.text,
          position: ci,
          is_required: child.required || false,
          category: child.category || templateItem.category || 'General'
        })) || []
      }));

      const response = await fetch(`/api/v1/work-items/${workItemId}/checklists`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: bulkItems })
      });

      if (!response.ok) {
        console.error('Error applying template');
      }

      await loadChecklists();
      setShowTemplateModal(false);

    } catch (error) {
      console.error('Error applying template:', error);
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleAddItem = async (parentId?: string) => {
    const textToAdd = parentId ? subItemText : newItemText;
    if (!textToAdd.trim()) return;

    try {
      setAdding(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/checklists`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_text: textToAdd,
          parent_id: parentId || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (parentId) {
            setSubItemText('');
            setAddingSubItemTo(null);
          } else {
            setNewItemText('');
          }
          await loadChecklists();
        }
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleToggleItem = async (itemId: string, currentState: boolean) => {
    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/checklists/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_checked: !currentState
        })
      });

      if (response.ok) {
        await loadChecklists();
      }
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!(await confirm({ message: 'Delete this checklist item and all its sub-items?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/checklists/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        await loadChecklists();
      }
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups(prev => {
      const updated = new Set(prev);
      if (updated.has(groupId)) {
        updated.delete(groupId);
      } else {
        updated.add(groupId);
      }
      return updated;
    });
  };

  // Calculate progress from flat items (all items including nested)
  const completedCount = flatItems.filter(item => item.is_checked).length;
  const totalCount = flatItems.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  // Helper to get child completion stats for a parent
  const getChildStats = (parent: WorkItemChecklistRow) => {
    if (!parent.children || parent.children.length === 0) return null;
    const total = parent.children.length;
    const completed = parent.children.filter((c) => c.is_checked).length;
    return { total, completed, percentage: Math.round((completed / total) * 100) };
  };

  if (loading) {
    return (
      <InlineLoadingPill label="Loading checklists..." primaryColor="#2563eb" className="py-8" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      {totalCount > 0 && (
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Progress: {completedCount} of {totalCount} completed
            </span>
            <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{progressPercentage}%</span>
          </div>
          <div className={`w-full rounded-full h-2.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className={`h-2.5 rounded-full transition-all duration-300 ${progressPercentage === 100 ? 'bg-green-500' : 'bg-blue-600'
                }`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Add New Item & Add Template */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
            placeholder="Add a checklist item..."
            disabled={adding}
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 disabled:bg-gray-100'}`}
          />
          <button
            onClick={() => handleAddItem()}
            disabled={adding || !newItemText.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
          >
            <CheckSquare className="w-4 h-4" />
            Add
          </button>
        </div>

        {/* Add Template Button */}
        <button
          onClick={openTemplateModal}
          className={`w-full px-4 py-2.5 border-2 border-dashed rounded-lg flex items-center justify-center gap-2 transition-colors ${isDark
            ? 'border-gray-600 text-gray-400 hover:border-purple-500 hover:text-purple-400 hover:bg-purple-500/10'
            : 'border-gray-300 text-gray-500 hover:border-purple-500 hover:text-purple-600 hover:bg-purple-50'
            }`}
        >
          <FileText className="w-4 h-4" />
          Add from Template
        </button>
      </div>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`w-full max-w-lg mx-4 rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Select Checklist Template
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 max-h-[400px] overflow-y-auto">
              {loadingTemplates ? (
                <InlineLoadingPill label="Loading templates..." primaryColor="#8b5cf6" className="py-8" />
              ) : templates.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FileText className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p>No checklist templates available.</p>
                  <p className="text-sm mt-1">Templates can be created in Service Operations settings.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template, templateIndex) => {
                    const items = template.checklist_template?.items || [];
                    const childCount = items.reduce((sum: number, item: { children?: unknown[] }) => sum + (item.children?.length || 0), 0);

                    return (
                      <button
                        key={template.template_key ?? `template-${templateIndex}`}
                        onClick={() => applyTemplate(template)}
                        disabled={applyingTemplate}
                        className={`w-full text-left p-4 rounded-lg border transition-all ${applyingTemplate
                          ? 'opacity-50 cursor-not-allowed'
                          : isDark
                            ? 'border-gray-700 bg-gray-700/50 hover:border-purple-500 hover:bg-purple-500/10'
                            : 'border-gray-200 bg-gray-50 hover:border-purple-500 hover:bg-purple-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {template.template_label}
                            </h4>
                            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {items.length} items{childCount > 0 ? ` (${childCount} sub-items)` : ''} &bull; {template.applies_to_subtype}
                            </p>
                          </div>
                          <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'
                            }`}>
                            {items.length + childCount} total
                          </div>
                        </div>

                        {/* Preview first 3 items with sub-item indicators */}
                        {items.slice(0, 3).map((item, i) => (
                          <div key={i} className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <div className="flex items-center gap-1.5">
                              <CheckSquare className="w-3 h-3" />
                              <span className="truncate">{item.text}</span>
                              {item.children && item.children.length > 0 && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                  +{item.children.length} sub
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                        {items.length > 3 && (
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            +{items.length - 3} more items
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={`flex justify-end gap-3 px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowTemplateModal(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checklist Items - Hierarchical Rendering */}
      {items.length === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <CheckSquare className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p>No checklist items yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isCollapsed = collapsedGroups.has(item.id);
            const childStats = getChildStats(item);

            return (
              <div key={item.id} className="space-y-0">
                {/* Parent Item */}
                <div
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${item.is_checked
                    ? isDark ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'
                    : hasChildren
                      ? isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300 shadow-sm'
                      : isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                >
                  {/* Collapse toggle for items with children */}
                  {hasChildren ? (
                    <button
                      onClick={() => toggleGroupCollapse(item.id)}
                      className={`flex-shrink-0 mt-0.5 p-0.5 rounded transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      {isCollapsed ? (
                        <ChevronRight className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      ) : (
                        <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      )}
                    </button>
                  ) : (
                    <div className="w-5" />
                  )}

                  <button
                    onClick={() => handleToggleItem(item.id, item.is_checked)}
                    className="flex-shrink-0 mt-0.5"
                  >
                    {item.is_checked ? (
                      <div className="w-5 h-5 bg-green-600 rounded flex items-center justify-center">
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className={`w-5 h-5 border-2 rounded hover:border-blue-500 transition-colors ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm ${item.is_checked
                        ? 'text-gray-500 line-through'
                        : hasChildren
                          ? isDark ? 'text-gray-100 font-medium' : 'text-gray-900 font-medium'
                          : isDark ? 'text-gray-200' : 'text-gray-900'
                        }`}>
                        {item.item_text}
                      </p>
                      {item.is_required && (
                        <span className={`text-[10px] px-1 py-0.5 rounded font-medium ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600'}`}>
                          Required
                        </span>
                      )}
                      {item.category && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                          {item.category}
                        </span>
                      )}
                    </div>

                    {/* Child progress indicator */}
                    {hasChildren && childStats && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className={`flex-1 max-w-[120px] rounded-full h-1.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${childStats.percentage === 100 ? 'bg-green-500' : 'bg-blue-500'
                              }`}
                            style={{ width: `${childStats.percentage}%` }}
                          />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {childStats.completed}/{childStats.total}
                        </span>
                      </div>
                    )}

                    {item.is_checked && item.checked_by && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        ✓ Checked by {formatChecklistCheckedByLabel(item.checked_by)}
                        {item.checked_at && ` on ${formatDateSAST(item.checked_at, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}`}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Add sub-item button */}
                    <button
                      onClick={() => setAddingSubItemTo(addingSubItemTo === item.id ? null : item.id)}
                      className={`p-1 transition-colors rounded ${isDark ? 'text-gray-500 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'}`}
                      title="Add sub-item"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className={`flex-shrink-0 p-1 transition-colors ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Add Sub-Item Input */}
                {addingSubItemTo === item.id && (
                  <div className={`flex gap-2 ml-10 mt-1 p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <input
                      type="text"
                      value={subItemText}
                      onChange={(e) => setSubItemText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddItem(item.id)}
                      placeholder="Add sub-item..."
                      autoFocus
                      className={`flex-1 px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                    <button
                      onClick={() => handleAddItem(item.id)}
                      disabled={adding || !subItemText.trim()}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => { setAddingSubItemTo(null); setSubItemText(''); }}
                      className={`px-2 py-1.5 text-sm rounded ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Children (Sub-items) - Drill Down */}
                {hasChildren && !isCollapsed && (
                  <div className={`ml-10 space-y-1 mt-1 pl-3 border-l-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {item.children!.map((child) => (
                      <div
                        key={child.id}
                        className={`flex items-start gap-3 p-2.5 rounded-lg border transition-all ${child.is_checked
                          ? isDark ? 'bg-green-900/10 border-green-800/50' : 'bg-green-50/80 border-green-100'
                          : isDark ? 'bg-gray-800/50 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-100 hover:border-gray-200'
                          }`}
                      >
                        <button
                          onClick={() => handleToggleItem(child.id, child.is_checked)}
                          className="flex-shrink-0 mt-0.5"
                        >
                          {child.is_checked ? (
                            <div className="w-4 h-4 bg-green-600 rounded flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <div className={`w-4 h-4 border-2 rounded hover:border-blue-500 transition-colors ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                          )}
                        </button>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm ${child.is_checked
                              ? 'text-gray-500 line-through'
                              : isDark ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                              {child.item_text}
                            </p>
                            {child.is_required && (
                              <span className={`text-[10px] px-1 py-0.5 rounded font-medium ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600'}`}>
                                Required
                              </span>
                            )}
                          </div>
                          {child.is_checked && child.checked_by && (
                            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              ✓ {formatChecklistCheckedByLabel(child.checked_by)}
                            </p>
                          )}
                        </div>

                        <button
                          onClick={() => handleDeleteItem(child.id)}
                          className={`flex-shrink-0 p-1 transition-colors ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TIME SERIES TAB COMPONENT (for sensor subtypes)
// ============================================================================

interface TimeSeriesData {
  id: string;
  metric_key: string;
  metric_value: number;
  unit?: string;
  recorded_at: string;
  metadata?: Record<string, unknown>;
}

function TimeSeriesTab({ workItemId, isActive }: { workItemId: string; isActive: boolean }) {
  const [data, setData] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [newReading, setNewReading] = useState({
    metric_key: 'temperature',
    metric_value: '',
    unit: '°C'
  });
  const [submitting, setSubmitting] = useState(false);
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/timeseries?limit=50`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading time series data:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadData();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadData]);

  const handleAddReading = async () => {
    if (!newReading.metric_value) return;

    try {
      setSubmitting(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/timeseries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          metric_key: newReading.metric_key,
          metric_value: parseFloat(newReading.metric_value),
          unit: newReading.unit,
          recorded_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData((prevData) => [result.data, ...prevData]);
          setNewReading({ metric_key: 'temperature', metric_value: '', unit: '°C' });
        }
      }
    } catch (error) {
      console.error('Error adding reading:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Delete this reading?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/timeseries?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        setData(data.filter(d => d.id !== id));
      }
    } catch (error) {
      console.error('Error deleting reading:', error);
    }
  };

  const isDark = currentTheme.mode === 'dark';

  if (loading) {
    return renderBrandedTabLoader('Loading time series...', '#14b8a6', 'py-10');
  }

  return (
    <div className="space-y-4">
      {/* Add New Reading */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
          Add New Reading
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Metric
            </label>
            <select
              value={newReading.metric_key}
              onChange={(e) => setNewReading({ ...newReading, metric_key: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="temperature">Temperature</option>
              <option value="humidity">Humidity</option>
              <option value="pressure">Pressure</option>
              <option value="voltage">Voltage</option>
              <option value="current">Current</option>
              <option value="power">Power</option>
              <option value="flow_rate">Flow Rate</option>
              <option value="level">Level</option>
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Value
            </label>
            <input
              type="number"
              step="0.01"
              value={newReading.metric_value}
              onChange={(e) => setNewReading({ ...newReading, metric_value: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Unit
            </label>
            <input
              type="text"
              value={newReading.unit}
              onChange={(e) => setNewReading({ ...newReading, unit: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="°C"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleAddReading}
              disabled={submitting || !newReading.metric_value}
              className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-colors"
              style={{ backgroundColor: currentTheme.colors.primary }}
            >
              {submitting ? 'Adding...' : 'Add Reading'}
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      {data.length === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No time series data recorded yet
        </div>
      ) : (
        <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full">
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Metric
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Value
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Recorded At
                </th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((reading) => (
                <tr key={reading.id} className={isDark ? 'bg-gray-900 hover:bg-gray-800' : 'bg-white hover:bg-gray-50'}>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    <span className="font-medium capitalize">{reading.metric_key.replace('_', ' ')}</span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    <span className="font-mono">{reading.metric_value}</span>
                    {reading.unit && <span className="text-gray-500 ml-1">{reading.unit}</span>}
                  </td>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formatTimeAgo(reading.recorded_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <button
                      onClick={() => handleDelete(reading.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// WATCHERS TAB COMPONENT
// ============================================================================

interface Watcher {
  id: string;
  user_id: number;
  user_name: string;
  user_email: string;
  added_at: string;
  added_by?: number;
}

interface WatcherSearchResult {
  id: number;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
}

function WatchersTab({ workItemId, currentUserId, isActive }: { workItemId: string; currentUserId?: number; isActive: boolean }) {
  const [watchers, setWatchers] = useState<Watcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<WatcherSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [isWatching, setIsWatching] = useState(false);
  const hasLoadedRef = useRef(false);
  const watcherSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { currentTheme: wtTheme } = useTheme();
  const isDark = wtTheme.mode === 'dark';

  const loadWatchers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/watchers`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWatchers(data.data || []);
          // Check if current user is watching
          if (currentUserId) {
            setIsWatching(data.data.some((w: Watcher) => w.user_id === currentUserId));
          }
        }
      }
    } catch (error) {
      console.error('Error loading watchers:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadWatchers();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadWatchers]);

  // Clear pending watcher search debounce on unmount to prevent
  // setState-after-unmount and stray fetch calls.
  useEffect(() => {
    return () => {
      if (watcherSearchDebounceRef.current) {
        clearTimeout(watcherSearchDebounceRef.current);
        watcherSearchDebounceRef.current = null;
      }
    };
  }, []);

  const searchUsers = useCallback((term: string) => {
    if (watcherSearchDebounceRef.current) clearTimeout(watcherSearchDebounceRef.current);
    if (!term.trim() || term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    watcherSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/api/v1/users?search=${encodeURIComponent(term)}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const watcherIds = watchers.map(w => w.user_id);
            setSearchResults((data.data || []).filter((u: WatcherSearchResult) => !watcherIds.includes(u.id)));
          }
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [watchers]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    searchUsers(value);
  };

  const handleAddWatcher = async (userId: number) => {
    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/watchers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Transform POST response to match the Watcher interface expected by the UI
          // POST returns: { id, user_id, created_at, user: { id, email, name } }
          // UI expects: { id, user_id, user_name, user_email, added_at }
          const rawWatcher = data.data;
          const transformedWatcher: Watcher = {
            id: rawWatcher.id,
            user_id: rawWatcher.user_id,
            user_name: rawWatcher.user?.name || rawWatcher.user_name || 'Unknown',
            user_email: rawWatcher.user?.email || rawWatcher.user_email || '',
            added_at: rawWatcher.created_at || rawWatcher.added_at || new Date().toISOString()
          };
          setWatchers((prevWatchers) => [...prevWatchers, transformedWatcher]);
          setSearchTerm('');
          setSearchResults([]);
          if (userId === currentUserId) {
            setIsWatching(true);
          }
        }
      }
    } catch (error) {
      console.error('Error adding watcher:', error);
    }
  };

  const handleRemoveWatcher = async (watcherId: string, userId: number) => {
    if (!(await confirm({ message: 'Remove this watcher?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/watchers/${watcherId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        setWatchers(watchers.filter(w => w.id !== watcherId));
        if (userId === currentUserId) {
          setIsWatching(false);
        }
      }
    } catch (error) {
      console.error('Error removing watcher:', error);
    }
  };

  const handleToggleWatch = async () => {
    if (!currentUserId) return;

    if (isWatching) {
      // Find current user's watcher record and remove it
      const myWatcher = watchers.find(w => w.user_id === currentUserId);
      if (myWatcher) {
        await handleRemoveWatcher(myWatcher.id, currentUserId);
      }
    } else {
      // Add current user as watcher
      await handleAddWatcher(currentUserId);
    }
  };

  if (loading) {
    return renderBrandedTabLoader('Loading watchers...', '#6366f1', 'py-8');
  }

  return (
    <div className="space-y-6">
      {/* Watch/Unwatch Button */}
      {currentUserId && (
        <div className={`rounded-lg p-4 border ${isDark ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-800' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className={`w-5 h-5 ${isWatching ? isDark ? 'text-blue-400' : 'text-blue-600' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <div>
                <h4 className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  {isWatching ? 'You are watching this item' : 'Watch this item'}
                </h4>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isWatching
                    ? 'You will receive notifications about updates'
                    : 'Get notified about comments, status changes, and updates'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleWatch}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${isWatching
                ? isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
            >
              {isWatching ? 'Unwatch' : 'Watch'}
            </button>
          </div>
        </div>
      )}

      {/* Add Watcher Search */}
      <div className="space-y-2">
        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Add Watchers
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search users by name or email..."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
          />
          {searching && (
            <div className={`absolute right-3 top-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className={`absolute z-10 mt-1 w-full border rounded-lg shadow-lg max-h-60 overflow-y-auto ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              {searchResults.map((user) => {
                // Construct display name from first_name/last_name or fall back to name or email
                const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.email || 'Unknown';
                const initial = displayName.charAt(0).toUpperCase();
                return (
                  <button
                    key={user.id}
                    onClick={() => handleAddWatcher(user.id)}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 border-b last:border-b-0 ${isDark ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                      {initial}
                    </div>
                    <div className="flex-1">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{displayName}</div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {searchTerm && searchResults.length === 0 && !searching && (
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No users found</p>
        )}
      </div>

      {/* Current Watchers List */}
      <div>
        <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Current Watchers ({watchers.length})
        </h4>

        {watchers.length === 0 ? (
          <div className={`text-center py-8 rounded-lg ${isDark ? 'text-gray-400 bg-gray-800' : 'text-gray-500 bg-gray-50'}`}>
            <Eye className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p>No watchers yet.</p>
            <p className="text-sm mt-1">Add watchers to notify them of updates.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {watchers.map((watcher) => (
              <div
                key={watcher.id}
                className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {watcher.user_name?.charAt(0)?.toUpperCase() || watcher.user_email?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {watcher.user_name || 'Unknown'}
                      {watcher.user_id === currentUserId && (
                        <span className={`ml-2 text-xs font-normal ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>(You)</span>
                      )}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{watcher.user_email}</div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Watching since {formatTimeAgo(watcher.added_at)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveWatcher(watcher.id, watcher.user_id)}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                  title="Remove watcher"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className={`border rounded-lg p-4 ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex gap-3">
          <Bell className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-900'}`}>
            <p className="font-medium mb-1">About Watchers</p>
            <ul className={`space-y-1 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
              <li>• Watchers receive notifications when this item is updated</li>
              <li>• Notifications include comments, status changes, and assignments</li>
              <li>• You can watch/unwatch items at any time</li>
              <li>• Assigned users are automatically added as watchers</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================

/**
 * Copy text to clipboard with visual feedback
 */
async function copyToClipboard(text: string, label: string = 'Text') {
  try {
    await copyTextToClipboardValue(text);
    notify.success(`${label} copied to clipboard`);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    notify.error('Failed to copy to clipboard');
  }
}

let activeWorkItemDetailModalInstanceIds: string[] = [];
let workItemDetailModalInstanceCounter = 0;
const activeWorkItemDetailModalListeners = new Set<() => void>();

const createWorkItemDetailModalInstanceId = (): string => {
  workItemDetailModalInstanceCounter += 1;
  return `work-item-detail-modal-${workItemDetailModalInstanceCounter}`;
};

const notifyActiveWorkItemDetailModalListeners = () => {
  activeWorkItemDetailModalListeners.forEach((listener) => listener());
};

/**
 * Outer wrapper that provides the shared WorkItemDetailContext.
 * This allows the inner component (and all child tabs) to consume context data.
 */
/**
 * Fleet vehicles always open in the dedicated VehicleDetailModal — the same
 * synthesized work-item experience FleetManagementView renders (all fleet
 * tabs + DetailsSidebar) — regardless of which surface opened them. Callers
 * that supply their own `overviewContent` (FleetManagementView itself)
 * keep full control and are not delegated.
 */
function getDelegatedVehicleWorkItem(props: WorkItemDetailModalProps): WorkItem | null {
  if (props.overviewContent) return null;
  const candidate = props.workItem ?? props.item ?? props.sourceItem;
  if (!candidate) return null;
  return candidate.item_type === 'asset' && candidate.item_subtype === 'vehicle' ? candidate : null;
}

export default function WorkItemDetailModal(props: WorkItemDetailModalProps) {
  const instanceIdRef = useRef<string>('');
  const [activeInstanceIds, setActiveInstanceIds] = useState<string[]>(activeWorkItemDetailModalInstanceIds);

  if (!instanceIdRef.current) {
    instanceIdRef.current = createWorkItemDetailModalInstanceId();
  }

  const delegatedVehicleItem = getDelegatedVehicleWorkItem(props);

  useEffect(() => {
    if (delegatedVehicleItem) {
      // The delegated VehicleDetailModal registers its own modal instance.
      return;
    }

    const syncActiveInstance = () => {
      setActiveInstanceIds(activeWorkItemDetailModalInstanceIds);
    };

    activeWorkItemDetailModalListeners.add(syncActiveInstance);
    syncActiveInstance();

    return () => {
      activeWorkItemDetailModalListeners.delete(syncActiveInstance);

      if (activeWorkItemDetailModalInstanceIds.includes(instanceIdRef.current)) {
        activeWorkItemDetailModalInstanceIds = activeWorkItemDetailModalInstanceIds.filter((id) => id !== instanceIdRef.current);
        notifyActiveWorkItemDetailModalListeners();
      }
    };
  }, [delegatedVehicleItem]);

  useEffect(() => {
    if (delegatedVehicleItem) {
      return;
    }

    if (!props.isOpen) {
      if (activeWorkItemDetailModalInstanceIds.includes(instanceIdRef.current)) {
        activeWorkItemDetailModalInstanceIds = activeWorkItemDetailModalInstanceIds.filter((id) => id !== instanceIdRef.current);
        notifyActiveWorkItemDetailModalListeners();
      }

      return;
    }

    // Keep every open instance registered — visibility is capped by the
    // isVisibleInstance check below. Capping this list would permanently
    // hide an evicted-but-open modal even after newer modals close.
    const nextActiveInstanceIds = [
      ...activeWorkItemDetailModalInstanceIds.filter((id) => id !== instanceIdRef.current),
      instanceIdRef.current,
    ];

    const hasChanged = nextActiveInstanceIds.length !== activeWorkItemDetailModalInstanceIds.length
      || nextActiveInstanceIds.some((id, index) => id !== activeWorkItemDetailModalInstanceIds[index]);

    if (hasChanged) {
      activeWorkItemDetailModalInstanceIds = nextActiveInstanceIds;
      notifyActiveWorkItemDetailModalListeners();
    }
  }, [props.isOpen, delegatedVehicleItem]);

  // Only the two most recently opened instances render; earlier ones stay
  // mounted but hidden and re-appear once newer modals close. Delegated
  // vehicle modals don't register here — VehicleDetailModal renders a nested
  // WorkItemDetailModal that registers itself.
  const isVisibleInstance = props.isOpen && (
    delegatedVehicleItem != null || activeInstanceIds.slice(-2).includes(instanceIdRef.current)
  );

  if (!isVisibleInstance) return null;

  const modalContent = delegatedVehicleItem ? (
    <React.Suspense fallback={null}>
      <VehicleDetailModal
        vehicle={delegatedVehicleItem}
        onClose={props.onClose}
        onRefresh={props.onRefresh ?? props.onUpdate}
        initialTab={props.initialTab}
        listModuleContext={props.listModuleContext}
        activeSolutionKey={props.activeSolutionKey}
      />
    </React.Suspense>
  ) : (
    <WorkItemDetailContextProvider>
      <WorkItemDetailModalInner {...props} />
    </WorkItemDetailContextProvider>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}

function WorkItemDetailModalInner({
  isOpen,
  onClose,
  workItem: primaryWorkItem,
  item: legacyWorkItem,
  sourceItem,
  workItemId: fallbackWorkItemId,
  onEdit,
  onDelete,
  onRefresh: onRefreshProp,
  onUpdate,
  templates = [],
  visibleTabs: customVisibleTabs,
  initialTab,
  title: customTitle,
  headerIcon: CustomHeaderIcon,
  customTabs,
  moduleColorKey,
  overviewContent,
  customTabContent,
  onReply,
  onStatusChange,
  onMergeContact,
  readOnly = false,
  readOnlyReason,
  listModuleContext = null,
  activeSolutionKey = null,
  containerZIndex = 120,
  tabsTourId,
  containerTourId = 'work-item-detail-modal',
  workspaceItemMode = false,
}: WorkItemDetailModalProps) {
  const { currentTheme, clientBranding } = useTheme();
  const { registerBackHandler, unregisterBackHandler } = useModalState();
  // ── Offline-backed effective permissions (same source as SmartTask) ──
  // useEffectivePermissions loads from IndexedDB (effectivePermissionsStore)
  // on mount for instant offline render, then revalidates from
  // /api/v1/iam/effective-permissions. It listens for
  // IAM_PERMISSIONS_CHANGED_EVENT (broadcast by the WebSocket IAM channel
  // in lifecycle.ts, or by PermissionsSlideOver after a save) and
  // automatically re-fetches. The 30s TTL cache matches SmartTask.
  const effectivePermissions = useEffectivePermissions('total-operations');
  const detailContext = useWorkItemDetailContext();
  const cacheCurrentUser = detailContext?.setCachedCurrentUser;
  const cacheJobTypes = detailContext?.setCachedJobTypes;
  const hydrateOperationsUsers = detailContext?.hydrateOperationsUsers;
  const contactPeople = detailContext?.contactPeople;
  const contactPeopleContactId = detailContext?.contactPeopleContactId ?? null;
  const loadContactPeople = detailContext?.loadContactPeople;
  const resetDetailItemData = detailContext?.resetItemData;
  const [activeTab, setActiveTab] = useState<WorkItemTabId>(initialTab || 'overview');
  const [activeHistorySubTab, setActiveHistorySubTab] = useState<'activity' | 'watchers' | 'ai_review' | 'chat' | 'process_checklist'>('activity');

  const [activeProjectPlanSubTab, setActiveProjectPlanSubTab] = useState<'plan' | 'gantt'>('plan');
  const [commentCount, setCommentCount] = useState(0);
  const [activityCount, setActivityCount] = useState(0);
  const [timeEntriesCount, setTimeEntriesCount] = useState(0);
  const [relationshipsCount, setRelationshipsCount] = useState(0);
  const [attachmentsCount, setAttachmentsCount] = useState(0);
  const [, setChecklistsCount] = useState(0);
  const [watchersCount, setWatchersCount] = useState(0);
  const [, setSubtasksCount] = useState(0);

  const [workBreakdownCount, setWorkBreakdownCount] = useState(0);
  const [expensesCount, setExpensesCount] = useState(0);
  const [referenceDrawingsCount, setReferenceDrawingsCount] = useState(0);
  const [jobTypes, setJobTypes] = useState<Array<{ key: string; label: string; icon?: string; color?: string }>>([]);
  const [formData, setFormData] = useState<WorkItemFormData>({});
  const [currentUserRole, setCurrentUserRole] = useState<string>('user');
  const [currentOperationsRole, setCurrentOperationsRole] = useState<'user' | 'supervisor' | 'admin'>('user');
  // Config-only keys from my-features (template keys, display mode, industry
  // solution, operations_role). Permission keys come from the offline-backed
  // useEffectivePermissions hook and are merged in the memo below.
  const [operationsConfigSettings, setOperationsConfigSettings] = useState<UnknownRecord>({});
  const [currentUserId, setCurrentUserId] = useState<number | undefined>();
  const [currentUserName, setCurrentUserName] = useState<string>('');
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set(['overview'])); // Track which tabs have been loaded
  const [currentTemplate, setCurrentTemplate] = useState<WorkItemTemplate | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showComprehensiveReport, setShowComprehensiveReport] = useState(false);
  // Live subscription — updates if the mode is toggled while this modal stays
  // mounted (e.g. from a nested appointment modal or the Billing tab).
  const persistedTimerMode = useTimerAggregationMode();
  const [showContextualAssistant, setShowContextualAssistant] = useState(false);
  const [contextualAssistantPrompt, setContextualAssistantPrompt] = useState('');
  const [showAiMenu, setShowAiMenu] = useState(false);
  const aiMenuRef = useRef<HTMLDivElement>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isStatusTransitioning, setIsStatusTransitioning] = useState(false);
  const [activeSupportingDocumentsSection, setActiveSupportingDocumentsSection] = useState<SupportingDocumentsSectionId>(
    getSupportingDocumentsSectionForTab(initialTab),
  );
  const [activeBillingSection, setActiveBillingSection] = useState<BillingSectionId>(
    getBillingSectionForTab(initialTab),
  );
  const [ledgerGenerated, setLedgerGenerated] = useState(false);
  const [teamMemberProfileRefreshKey, setTeamMemberProfileRefreshKey] = useState(0);
  const appliedTeamMemberRefreshKeyRef = useRef(0);
  const incomingWorkItem = primaryWorkItem ?? legacyWorkItem ?? sourceItem ?? null;
  const [resolvedWorkItem, setResolvedWorkItem] = useState<WorkItem | null>(incomingWorkItem);
  const onRefresh = onRefreshProp ?? onUpdate;
  const workItem = resolvedWorkItem ?? incomingWorkItem;

  // ── Merged operations feature settings ──
  // Effective permissions (offline-backed, WebSocket-synced) are the BASE
  // for all permission keys. Config-only keys from my-features (template
  // keys, display mode, industry solution) are overlaid on top. This
  // matches the SmartTask pattern from useSmartTaskConfig.ts.
  const operationsFeatureSettings = useMemo<UnknownRecord>(() => {
    const iamPermissions = effectivePermissions.permissions;
    if (iamPermissions && Object.keys(iamPermissions).length > 0) {
      // operations_action_permissions is a permission artifact, not a
      // registered definition key — so it survives the config-only filters
      // above. Never let the stale my-features/detail-bundle copy replace the
      // resolver's workflow-scoped matrix when IAM is present.
      const configOnly = { ...operationsConfigSettings };
      delete configOnly.operations_action_permissions;
      return { ...iamPermissions, ...configOnly };
    }
    return operationsConfigSettings;
  }, [effectivePermissions.permissions, operationsConfigSettings]);

  // Sync the operations role from effective permissions (authoritative IAM
  // source) when it changes, falling back to the my-features role.
  useEffect(() => {
    if (effectivePermissions.isLoading) return;
    const iamRole = effectivePermissions.role;
    if (iamRole === 'admin' || iamRole === 'supervisor' || iamRole === 'user') {
      setCurrentOperationsRole(iamRole);
    }
  }, [effectivePermissions.role, effectivePermissions.isLoading]);
  const workItemIdForAudit =
    (workItem?.id as string | undefined) || (fallbackWorkItemId as string | undefined) || undefined;

  useEffect(() => {
    if (!isOpen || !workItemIdForAudit) return;
    const stakeholderContactId = workItem ? getStakeholderContactId(workItem) : null;
    void logUiAudit({
      component: 'WorkItemDetailModal',
      action: 'open',
      moduleKey: moduleColorKey || 'total-operations',
      workItemId: String(workItemIdForAudit),
      stakeholderContactId: stakeholderContactId ?? undefined,
      metadata: { initialTab, item_type: workItem?.item_type },
    });
  }, [isOpen, workItemIdForAudit, initialTab, moduleColorKey, workItem?.item_type, workItem]);

  const effectiveModalStatus = formData.status || workItem?.status || '';
  const effectiveAppointmentHeaderStatus = useMemo(() => {
    if (!workItem || workItem.item_type !== 'appointment') {
      return effectiveModalStatus;
    }

    return getAppointmentDisplayStatus({
      ...workItem,
      status: formData.status || workItem.status,
      custom_fields: formData.custom_fields && typeof formData.custom_fields === 'object'
        ? formData.custom_fields
        : workItem.custom_fields,
    }, { privilegedViewer: true }) || effectiveModalStatus;
  }, [effectiveModalStatus, formData.custom_fields, formData.status, workItem]);
  const normalizedEffectiveStatus = normalizeWorkflowStatusKey(effectiveModalStatus);
  const isClosedStatus = normalizedEffectiveStatus === 'closed';
  const isScheduledStatus = normalizedEffectiveStatus === 'scheduled' || normalizedEffectiveStatus === 'open' || normalizedEffectiveStatus === 'new';
  const isAdminReopenOnlyLocked = isAdminReopenLockedStatus(effectiveModalStatus);
  const shouldForceReadOnlyWorkBreakdownForms = workItem?.item_type === 'appointment'
    && ['scheduled', 'on_route', 'resolved', 'closed'].includes(normalizedEffectiveStatus);
  const shouldForceEditableWorkBreakdownForms = workItem?.item_type === 'appointment'
    && ['in_progress', 'admin_reopened'].includes(normalizedEffectiveStatus);

  const lockedBadgeTitle = isClosedStatus
    ? 'This item is locked. Only an Admin Re-opened status change is allowed.'
    : (readOnlyReason || 'This item is locked and cannot be edited.');

  // View-first overlay: when readOnly, open in view mode; readOnlyReason means locked (no edit at all)
  const [isViewMode, setIsViewMode] = useState(readOnly || isAdminReopenOnlyLocked);
  const isLocked = isClosedStatus; // Closed items always show the locked badge
  const workBreakdownFormsReadOnly = shouldForceEditableWorkBreakdownForms
    ? false
    : shouldForceReadOnlyWorkBreakdownForms
      ? true
      : isViewMode;
  const shouldRelaxWorkBreakdownViewModeLock = isViewMode
    && !workBreakdownFormsReadOnly
    && activeTab === 'work_breakdown';

  // Share permalink state
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareUrlLoading, setShareUrlLoading] = useState(false);

  // Action dropdown state (unified complete/convert menu)
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  // Pending BLS doc type — when set, triggers BillingDashboard.handleGenerateBilling via prop
  const [pendingDocType, setPendingDocType] = useState<'quote' | 'proforma' | 'invoice' | null>(null);

  // Subtask detail drawer state
  const [subtaskDrawerId, setSubtaskDrawerId] = useState<string | null>(null);
  const [isSubtaskDrawerOpen, setIsSubtaskDrawerOpen] = useState(false);
  const [showProcessChecklist, setShowProcessChecklist] = useState(false);
  const [processChecklistTargetStatus, setProcessChecklistTargetStatus] = useState<string | null>(null);
  const fieldChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<WorkItemFormData>({});
  const latestFormDataRef = useRef<WorkItemFormData>({});
  const adminReopenLockedRef = useRef(isAdminReopenOnlyLocked);
  /** When false, completing the process checklist overlay does not auto-retry a status change (e.g. opened from invoice flow before user chose to close). */
  const processGateResumeAfterCompleteRef = useRef(true);

  const openProcessChecklistDesigner = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const resolveItemType = workItem?.item_type || 'job';
    const resolveItemLabel = resolveItemType
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

    const params = new URLSearchParams({
      tab: 'builder',
      workflow_mode: 'resolve_checklist',
      purpose: 'operational',
      item_type: resolveItemType,
      title: `${resolveItemLabel} Process Checklist`,
    });

    window.location.href = `/client-zone/modules/total-forms?${params.toString()}`;
  }, [workItem?.item_type]);

  const openProcessChecklist = useCallback(
    (targetStatus?: string, options?: { resumePendingStatusChange?: boolean }) => {
      processGateResumeAfterCompleteRef.current = options?.resumePendingStatusChange !== false;
      setProcessChecklistTargetStatus(targetStatus || null);
      setShowProcessChecklist(true);
    },
    [],
  );

  const closeProcessChecklist = useCallback(() => {
    setShowProcessChecklist(false);
    setProcessChecklistTargetStatus(null);
  }, []);

  useEffect(() => {
    adminReopenLockedRef.current = isAdminReopenOnlyLocked;
  }, [isAdminReopenOnlyLocked]);

  useEffect(() => {
    latestFormDataRef.current = formData;
  }, [formData]);

  const clearPendingFieldSave = useCallback(() => {
    if (fieldChangeTimerRef.current) {
      clearTimeout(fieldChangeTimerRef.current);
      fieldChangeTimerRef.current = null;
    }

    pendingChangesRef.current = {};
  }, []);

  // Clear pending autosave debounce on unmount to prevent
  // a stray API PUT after the component is gone.
  useEffect(() => {
    return () => {
      if (fieldChangeTimerRef.current) {
        clearTimeout(fieldChangeTimerRef.current);
        fieldChangeTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setResolvedWorkItem((previous) => {
      if (!incomingWorkItem) {
        return incomingWorkItem;
      }

      if (!previous || String(previous.id) !== String(incomingWorkItem.id)) {
        return incomingWorkItem;
      }

      const previousCustomFields = getWorkItemCustomFields(previous);
      const incomingCustomFields = getWorkItemCustomFields(incomingWorkItem);
      const shouldPreserveHydratedTeamMemberIdentity = previous.item_type === 'team_member'
        && incomingWorkItem.item_type === 'team_member'
        && !isPlaceholderTeamMemberTitle(previous.title)
        && isPlaceholderTeamMemberTitle(incomingWorkItem.title);
      const shouldPreserveHydratedTeamMemberCustomFields = previous.item_type === 'team_member'
        && incomingWorkItem.item_type === 'team_member'
        && Boolean(previousCustomFields.current_operations)
        && !incomingCustomFields.current_operations;

      const nextResolvedWorkItem: WorkItem = {
        ...previous,
        ...incomingWorkItem,
        title: shouldPreserveHydratedTeamMemberIdentity ? previous.title : incomingWorkItem.title,
        status: formData.status || previous.status || incomingWorkItem.status,
        custom_fields: formData.custom_fields && typeof formData.custom_fields === 'object'
          ? formData.custom_fields
          : shouldPreserveHydratedTeamMemberCustomFields
            ? previous.custom_fields
            : incomingWorkItem.custom_fields ?? previous.custom_fields,
        // Preserve assignees from the previous state — they may have been
        // updated by DetailsSidebar or refreshCurrentWorkItemModal and the
        // incomingWorkItem (e.g. selectedVehicle from FleetManagementView)
        // can be stale, causing newly-added or deleted assignees to revert.
        assignees: Array.isArray(previous.assignees)
          ? previous.assignees
          : incomingWorkItem.assignees ?? previous.assignees,
        // Same for the responsible manager — DetailsSidebar persists it via
        // PUT, but incomingWorkItem is a frozen list snapshot and would
        // stomp the refreshed value back to null on every merge.
        manager_id: previous.manager_id ?? incomingWorkItem.manager_id,
        manager_name: previous.manager_name ?? incomingWorkItem.manager_name,
      };

      if (areShallowEqualRecords(previous as UnknownRecord, nextResolvedWorkItem as UnknownRecord)) {
        return previous;
      }

      return nextResolvedWorkItem;
    });
  }, [formData.custom_fields, formData.status, incomingWorkItem]);

  useEffect(() => {
    if (!isOpen || incomingWorkItem || fallbackWorkItemId == null) {
      return;
    }

    let cancelled = false;

    const loadFallbackWorkItem = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          return;
        }

        const response = await fetch(`/api/v1/work-items?ids=${encodeURIComponent(String(fallbackWorkItemId))}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const fetchedWorkItem = Array.isArray(data.data) ? data.data[0] : data.data;

        if (!cancelled) {
          setResolvedWorkItem((fetchedWorkItem || null) as WorkItem | null);
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('Failed to load work item detail modal fallback item:', error);
      }
    };

    void loadFallbackWorkItem();

    return () => {
      cancelled = true;
    };
  }, [fallbackWorkItemId, incomingWorkItem, isOpen]);

  // Tab configuration from YAML types.yaml default_visible_tabs
  const [configuredTabs, setConfiguredTabs] = useState<string[] | null>(null);
  const [demoConfiguredTabs, setDemoConfiguredTabs] = useState<string[] | null>(null);

  // Parent project for jobs (loaded via work_item_relationships)
  const [parentProject, setParentProject] = useState<{ id: string; item_number: string; title: string } | null>(null);
  const [detailSidebarInitialData, setDetailSidebarInitialData] = useState<DetailsSidebarInitialData | null>(null);
  const [overviewInitialData, setOverviewInitialData] = useState<WorkItemOverviewInitialData | null>(null);
  const [detailBundleStatus, setDetailBundleStatus] = useState<'idle' | 'loading' | 'loaded' | 'failed'>('idle');
  const countsLoadedRef = useRef(false);
  const pendingInitialTabRef = useRef<WorkItemTabId | null>(initialTab || null);
  const modalInitializationPresentationKeyRef = useRef<string | null>(null);
  const activeTabInitializationRef = useRef<{ presentationKey: string | null; requestedInitialTab: WorkItemTabId | null }>({
    presentationKey: null,
    requestedInitialTab: null,
  });
  const initializedFormPresentationKeyRef = useRef<string | null>(null);
  const freshFetchPresentationKeyRef = useRef<string | null>(null);
  const realtimeClientId = useMemo(() => {
    const rawClientId = authService.getUser()?.client_id ?? (workItem as WorkItemWithMetadata | null)?.client_id;
    return rawClientId != null ? String(rawClientId) : null;
  }, [workItem]);
  const modalPresentationKey = useMemo(() => {
    const workItemId = workItem?.id ? String(workItem.id) : 'none';
    return `${workItemId}:default`;
  }, [workItem?.id]);
  const groupedRealtimeRefreshTimeoutRef = useRef<number | null>(null);
  const detailSidebarWorkItem = useMemo(() => {
    if (!workItem) {
      return null;
    }

    return {
      ...workItem,
      status: formData.status || workItem.status,
      custom_fields: formData.custom_fields && typeof formData.custom_fields === 'object'
        ? formData.custom_fields
        : workItem.custom_fields,
    };
  }, [formData.custom_fields, formData.status, workItem]);

  const applyPersistedWorkItemUpdates = useCallback((updates: Partial<WorkItem>) => {
    if (!updates || Object.keys(updates).length === 0) {
      return;
    }

    setResolvedWorkItem((previous) => {
      const baseItem = previous ?? incomingWorkItem;
      if (!baseItem) {
        return previous;
      }

      return {
        ...baseItem,
        ...updates,
        custom_fields: updates.custom_fields ?? baseItem.custom_fields,
      };
    });
  }, [incomingWorkItem]);

  // Related items (edit contact, parent job, linked items) open as a nested
  // child modal so closing them always returns to this modal.
  const [nestedWorkItem, setNestedWorkItem] = useState<WorkItem | null>(null);
  const [nestedWorkItemLoading, setNestedWorkItemLoading] = useState(false);

  const openWorkItemById = useCallback((itemId: string) => {
    if (typeof window === 'undefined' || !itemId) {
      return;
    }

    const paramKey = isUuid(itemId) ? 'id' : 'number';
    setNestedWorkItemLoading(true);
    setNestedWorkItem(null);

    fetch(`/api/v1/work-items/lookup?${paramKey}=${encodeURIComponent(itemId)}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success || !data?.workItem) {
          throw new Error(data?.error || 'Work item not found');
        }

        const item = data.workItem as WorkItem;
        // Finance documents still open in their dedicated DocumentModal via
        // the global opener rather than the read-only preview in this modal.
        if (isFinanceDocType(item.item_type)) {
          emit('openWorkItem', { workItem: item as unknown as Record<string, unknown> });
          return;
        }
        setNestedWorkItem(item);
      })
      .catch((err: unknown) => {
        notify.error(err instanceof Error ? err.message : 'Failed to open item');
      })
      .finally(() => {
        setNestedWorkItemLoading(false);
      });
  }, []);

  const handleOpenGeneratedBillingDocument = useCallback(async (docId: string, docType: string) => {
    if (typeof window === 'undefined') return;
    // Fetch the full document — a sparse { id } payload opens DocumentModal
    // with an empty header (number/date/contact) and zeroed totals.
    let documentPayload: Record<string, unknown> = { id: docId };
    const token = authService.getTokens()?.access_token;
    if (token) {
      try {
        const res = await fetch(`/api/v1/work-items/documents/${encodeURIComponent(docId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => null);
        if (res.ok && json?.success && json.data) {
          documentPayload = json.data;
        }
      } catch {
        // Fall through to the sparse payload — DocumentModal still loads BOM lines.
      }
    }
    window.dispatchEvent(
      new CustomEvent('openDocumentModal', {
        detail: {
          doc_type: docType,
          mode: 'edit',
          document: documentPayload,
        },
      }),
    );
  }, []);

  // Listen for closeWorkItemModal events (from DocumentModal when job is closed on BLS submit)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ work_item_id?: string }>).detail;
      if (detail?.work_item_id && workItem?.id && String(detail.work_item_id) === String(workItem.id)) {
        onClose();
      }
    };
    window.addEventListener('closeWorkItemModal', handler as EventListener);
    return () => window.removeEventListener('closeWorkItemModal', handler as EventListener);
  }, [workItem?.id, onClose]);

  const handleOpenSubtask = useCallback((itemId: string) => {
    setSubtaskDrawerId(itemId);
    setIsSubtaskDrawerOpen(true);
  }, []);

  const handleSubtasksCountChange = useCallback((count: number) => {
    setSubtasksCount(count);
  }, []);

  const handleWorkBreakdownCountsChange = useCallback((counts: {
    subtasks: number;
    checklists: number;
    validations: number;
    dynamicForms: number;
  }) => {
    setWorkBreakdownCount(counts.subtasks + counts.checklists + counts.validations + counts.dynamicForms);
    setSubtasksCount(counts.subtasks);
    setChecklistsCount(counts.checklists);
  }, []);

  const handleExpensesCountChange = useCallback((count: number) => {
    setExpensesCount(count);
  }, []);

  const handleReferenceDrawingsCountChange = useCallback((count: number) => {
    setReferenceDrawingsCount(count);
  }, []);

  // Reference Docs count loads only when user opens that tab — DrawingWorkItemHub
  // fetches and reports via onDrawingsChange (avoids duplicate /drawings on modal open).

  const closeContextualAssistantPanel = useCallback(() => {
    setShowContextualAssistant(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      modalInitializationPresentationKeyRef.current = null;
      activeTabInitializationRef.current = {
        presentationKey: null,
        requestedInitialTab: null,
      };
      initializedFormPresentationKeyRef.current = null;
      freshFetchPresentationKeyRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    closeContextualAssistantPanel();
    setContextualAssistantPrompt('');
  }, [closeContextualAssistantPanel, modalPresentationKey]);

  // Close the contextual AI assistant panel when the onboarding coach opens
  // (mutual exclusivity). This catches cases where the coach is opened via the
  // global 'ai-coach-open' event (e.g. from the sidebar) rather than the modal's
  // own AI sub-menu button.
  useEffect(() => {
    if (!isOpen) return;
    const handleCoachOpen = () => closeContextualAssistantPanel();
    window.addEventListener('ai-coach-open', handleCoachOpen);
    return () => window.removeEventListener('ai-coach-open', handleCoachOpen);
  }, [isOpen, closeContextualAssistantPanel]);

  // Real-time viewer tracking for clash detection (works for any work item type)
  const { viewers: itemViewers, otherViewersCount } = useTaskViewerTracking({
    taskId: workItem?.id?.toString(),
    enabled: isOpen && !!workItem?.id,
    userId: currentUserId,
    userName: currentUserName || 'Unknown User',
    showToasts: true, // Show toast notifications when others join/leave
  });

  const { getTimer, getStatus, seedFromAppointments } = useAppointmentLiveTimers({
    appointmentIds: workItem?.id ? [String(workItem.id)] : undefined,
  });

  // ════════════════════════════════════════════════════════════════════════
  // SSOT STATUS POLL — bypasses cached workItem props to detect status
  // changes that may have been missed by the WebSocket channel.
  // Polls /api/v1/work-items/{id}/status every 10s with cache-busting headers.
  // ════════════════════════════════════════════════════════════════════════
  const [ssotStatus, setSsotStatus] = useState<string | null>(null);

  const ssotApptId = workItem && workItem.item_type === 'appointment' ? String(workItem.id) : null;

  const pollSsotStatus = useCallback(async () => {
    if (!ssotApptId) return;
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`/api/v1/work-items/${ssotApptId}/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) return;
      const json = await response.json();

      if (json?.success && json?.data?.resolved_status) {
        setSsotStatus(json.data.resolved_status);
      }
    } catch {
      // Silently ignore — WebSocket and fallback timer still work
    }
  }, [ssotApptId]);

  // Reset status when the work item changes
  useEffect(() => {
    if (!workItem || workItem.item_type !== 'appointment') {
      setSsotStatus(null);
    }
  }, [workItem]);

  // Background-aware: polls every 10s while an appointment modal is open,
  // pauses while the app is backgrounded.
  useBackgroundAwareInterval(pollSsotStatus, {
    intervalMs: 10_000,
    runImmediately: true,
    enabled: Boolean(ssotApptId),
    deps: [ssotApptId, pollSsotStatus],
  });

  useEffect(() => {
    if (!workItem || workItem.item_type !== 'appointment') {
      return;
    }

    const effectiveStatus = ssotStatus || workItem.status || '';

    seedFromAppointments([
      {
        id: String(workItem.id),
        status: effectiveStatus,
        scheduled_start: workItem.scheduled_start || undefined,
        scheduled_end: workItem.scheduled_end || undefined,
        custom_fields: typeof workItem.custom_fields === 'string'
          ? (() => {
            try {
              return JSON.parse(workItem.custom_fields);
            } catch {
              return undefined;
            }
          })()
          : workItem.custom_fields,
      },
    ]);
  }, [seedFromAppointments, workItem, ssotStatus]);

  // ════════════════════════════════════════════════════════════════════════
  // LIVE APPOINTMENT TIMER — shows running HH:MM:SS in header for active
  // en_route / in_progress appointments. SmartTask is source of truth,
  // now sourced from the shared live timer hook with a local fallback for
  // travel-paused / initial hydration states.
  // ════════════════════════════════════════════════════════════════════════
  const appointmentTimerInfo = useMemo(() => {
    if (!workItem || workItem.item_type !== 'appointment') return null;
    const liveTimer = getTimer(String(workItem.id));
    const status = getStatus(String(workItem.id)) || ssotStatus || effectiveAppointmentHeaderStatus || workItem.status || '';
    const normalizedStatus = normalizeWorkflowStatusKey(status);
    const liveTimerMatchesStatus = liveTimer
      ? (liveTimer.isPaused
        ? normalizedStatus === 'paused'
        : (liveTimer.activeState === 'en_route'
          ? isOnRouteWorkItemStatus(status)
          : isActivelyWorkedStatus(status)))
      : false;
    const cf = typeof workItem.custom_fields === 'string'
      ? (() => { try { return JSON.parse(workItem.custom_fields); } catch { return null; } })()
      : workItem.custom_fields;

    if (liveTimer && liveTimerMatchesStatus) {
      const scheduledTimeStr = liveTimer.scheduledStart
        ? formatTimeSAST(liveTimer.scheduledStart)
        : null;

      return {
        state: liveTimer.activeState === 'en_route'
          ? (liveTimer.isPaused ? 'travel_paused' as const : 'en_route' as const)
          : (liveTimer.isPaused ? 'paused' as const : 'in_progress' as const),
        elapsed: liveTimer.elapsedSeconds,
        formatted: liveTimer.elapsedFormatted,
        isLate: liveTimer.isLate,
        isOverSchedule: liveTimer.isOverSchedule,
        lateSeconds: liveTimer.lateSeconds,
        scheduledTime: scheduledTimeStr,
      };
    }

    if (!cf) return null;

    const now = Date.now();

    if (isOnRouteWorkItemStatus(status) && cf.travel_started_at) {
      const scheduledStartMs = workItem.scheduled_start ? new Date(workItem.scheduled_start).getTime() : 0;
      const scheduledTimeStr = workItem.scheduled_start
        ? formatTimeSAST(workItem.scheduled_start)
        : null;

      // Travel paused — show frozen accumulated time
      if (cf.travel_paused_at && cf.accumulated_travel_seconds != null) {
        const elapsed = cf.accumulated_travel_seconds;
        const h = Math.floor(elapsed / 3600);
        const m = Math.floor((elapsed % 3600) / 60);
        const s = elapsed % 60;
        const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        const isLate = scheduledStartMs > 0 && now > scheduledStartMs;
        const lateSeconds = isLate ? Math.floor((now - scheduledStartMs) / 1000) : 0;
        return {
          state: 'travel_paused' as const,
          elapsed,
          formatted,
          isLate,
          lateSeconds,
          scheduledTime: scheduledTimeStr,
        };
      }

      // Travel active — count up from travel_started_at + any accumulated seconds from prior segments
      const startMs = new Date(cf.travel_started_at).getTime();
      const currentSegment = Math.max(0, Math.floor((now - startMs) / 1000));
      const accum = cf.accumulated_travel_seconds || 0;
      const elapsed = accum + currentSegment;
      const isLate = scheduledStartMs > 0 && now > scheduledStartMs;
      const lateSeconds = isLate ? Math.floor((now - scheduledStartMs) / 1000) : 0;
      const h = Math.floor(elapsed / 3600);
      const m = Math.floor((elapsed % 3600) / 60);
      const s = elapsed % 60;
      const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return {
        state: 'en_route' as const,
        elapsed,
        formatted,
        isLate,
        lateSeconds,
        scheduledTime: scheduledTimeStr,
      };
    }

    if (normalizedStatus === 'paused' && cf.check_in_timestamp) {
      const startMs = new Date(cf.check_in_timestamp).getTime();
      const pausedAtMs = typeof cf.workflow_paused_at === 'string' ? new Date(cf.workflow_paused_at).getTime() : Number.NaN;
      const storedElapsed = Number(cf.accumulated_work_seconds);
      const elapsed = Number.isFinite(storedElapsed) && storedElapsed >= 0
        ? storedElapsed
        : Number.isFinite(startMs) && Number.isFinite(pausedAtMs)
          ? Math.max(0, Math.floor((pausedAtMs - startMs) / 1000))
          : 0;
      const h = Math.floor(elapsed / 3600);
      const m = Math.floor((elapsed % 3600) / 60);
      const s = elapsed % 60;
      const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return {
        state: 'paused' as const,
        elapsed,
        formatted,
        isLate: false,
        isOverSchedule: false,
        lateSeconds: 0,
      };
    }

    if (isActivelyWorkedStatus(status) && cf.check_in_timestamp) {
      const startMs = new Date(cf.check_in_timestamp).getTime();
      const storedElapsed = Number(cf.accumulated_work_seconds);
      const workflowResumedAtMs = typeof cf.workflow_resumed_at === 'string' ? new Date(cf.workflow_resumed_at).getTime() : Number.NaN;
      const workStartedAtMs = typeof cf.work_started_at === 'string' ? new Date(cf.work_started_at).getTime() : Number.NaN;
      const segmentStartMs = Number.isFinite(workStartedAtMs)
        ? workStartedAtMs
        : Number.isFinite(workflowResumedAtMs)
          ? workflowResumedAtMs
          : startMs;
      const currentSegmentSeconds = Number.isFinite(segmentStartMs)
        ? Math.max(0, Math.floor((now - segmentStartMs) / 1000))
        : 0;
      const elapsed = Number.isFinite(storedElapsed) && storedElapsed >= 0
        ? Math.max(0, Math.floor(storedElapsed))
        + (Number.isFinite(workStartedAtMs) || Number.isFinite(workflowResumedAtMs) ? currentSegmentSeconds : 0)
        : Math.max(0, Math.floor((now - startMs) / 1000));
      const scheduledEndMs = workItem.scheduled_end ? new Date(workItem.scheduled_end).getTime() : 0;
      const isOverSchedule = scheduledEndMs > 0 && now > scheduledEndMs;
      const h = Math.floor(elapsed / 3600);
      const m = Math.floor((elapsed % 3600) / 60);
      const s = elapsed % 60;
      const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return {
        state: 'in_progress' as const,
        elapsed,
        formatted,
        isLate: false,
        isOverSchedule,
        lateSeconds: 0,
      };
    }

    return null;
  }, [effectiveAppointmentHeaderStatus, getStatus, getTimer, ssotStatus, workItem]);

  const existingShareToken = (workItem as (WorkItem & { share_token?: string | null }) | null | undefined)?.share_token ?? null;
  const shareRevokedAt = (workItem as (WorkItem & { share_revoked_at?: string | null }) | null | undefined)?.share_revoked_at ?? null;
  const syntheticItemCustomFields = workItem ? getWorkItemCustomFields(workItem) : {};
  const isTeamMember = workItem?.item_type === 'team_member'
    || Boolean(workItem?.id?.startsWith?.('team-member-'));
  const isStakeholder = Boolean(syntheticItemCustomFields.is_virtual_stakeholder)
    || typeof syntheticItemCustomFields.stakeholder_entity_kind === 'string'
    || typeof syntheticItemCustomFields.stakeholder_entity_type === 'string'
    || workItem?.id?.startsWith?.('stakeholder-')
    || parseStakeholderEntityWorkItemId(String(workItem?.id || '')) !== null;
  const isSyntheticItem = workItem?.item_type === 'team_member'
    || workItem?.item_type === 'recurring_job'
    || workItem?.id?.startsWith?.('team-member-')
    || workItem?.id?.startsWith?.('stakeholder-')
    || Boolean(syntheticItemCustomFields.is_virtual_stakeholder)
    || typeof syntheticItemCustomFields.stakeholder_entity_kind === 'string'
    || typeof syntheticItemCustomFields.stakeholder_entity_type === 'string'
    || parseStakeholderEntityWorkItemId(String(workItem?.id || '')) !== null;

  const buildPreferredShareUrl = useCallback((token?: string | null, fallbackUrl?: string | null) => {
    if (token && typeof window !== 'undefined' && window.location.origin) {
      return `${window.location.origin}/wi/${token}`;
    }

    if (fallbackUrl) {
      return fallbackUrl;
    }

    return token ? `/wi/${token}` : null;
  }, []);

  const handleShareLinkAction = useCallback(async () => {
    if (!workItem?.id || shareUrlLoading) {
      return;
    }

    // Team member and stakeholder synthetic items use the permalink API which generates
    // an opaque token stored in document_shares (same as regular work items).
    if (isSyntheticItem && !isTeamMember && !isStakeholder) {
      return;
    }

    if (shareUrl) {
      await copyToClipboard(shareUrl, 'Share link');
      return;
    }

    setShareUrlLoading(true);

    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/work-items/${workItem.id}/permalink`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();

      if (data.success && data.token) {
        const nextShareUrl = buildPreferredShareUrl(data.token, data.url || null);
        if (nextShareUrl) {
          setShareUrl(nextShareUrl);
          await copyToClipboard(nextShareUrl, 'Share link');
          return;
        }
      }

      notify.error('Failed to generate share link');
    } catch {
      notify.error('Failed to generate share link');
    } finally {
      setShareUrlLoading(false);
    }
  }, [buildPreferredShareUrl, isSyntheticItem, isStakeholder, isTeamMember, shareUrl, shareUrlLoading, workItem?.id]);

  // Close action dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (showActionDropdown) {
        const target = e.target as HTMLElement | null;
        if (!target || !target.closest('[data-action-dropdown]')) {
          setShowActionDropdown(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showActionDropdown]);

  // Pre-populate share URL from existing share_token using the current browser origin.
  // This keeps permalinks on the same whitelabel domain the user is currently using.
  useEffect(() => {
    if ((isTeamMember || isStakeholder) && workItem?.id) {
      // Team member and stakeholder share URLs are generated via the permalink API (opaque token).
      // Don't pre-populate from workItem.id — it will be fetched on demand.
      setShareUrl(null);
      setShareUrlLoading(false);
    } else if (existingShareToken && !shareRevokedAt && workItem?.id) {
      setShareUrl(buildPreferredShareUrl(existingShareToken));
      setShareUrlLoading(false);
    } else {
      setShareUrl(null);
      setShareUrlLoading(false);
    }
  }, [buildPreferredShareUrl, existingShareToken, isStakeholder, isTeamMember, shareRevokedAt, workItem?.id]);

  const applyLoadedCounts = useCallback((counts?: WorkItemDetailBundlePayload['counts'] | null) => {
    if (!counts) {
      return;
    }

    setCommentCount(counts.comments || 0);
    setActivityCount(counts.activity || 0);
    setTimeEntriesCount(counts.time_entries || 0);
    setRelationshipsCount(counts.relationships || 0);
    setAttachmentsCount(counts.attachments || 0);
    setChecklistsCount(counts.checklists || 0);
    setWatchersCount(counts.watchers || 0);
    setSubtasksCount(counts.subtasks || 0);
    countsLoadedRef.current = true;
  }, []);

  const applyDetailBundle = useCallback((bundle: WorkItemDetailBundlePayload) => {
    if (bundle.work_item && typeof bundle.work_item === 'object') {
      const bundledWorkItem = bundle.work_item as WorkItem;

      setResolvedWorkItem((previous) => {
        const baseItem = incomingWorkItem ?? previous;

        if (!baseItem) {
          return bundledWorkItem;
        }

        return {
          ...baseItem,
          ...bundledWorkItem,
          custom_fields: bundledWorkItem.custom_fields ?? baseItem.custom_fields,
          assignees: Array.isArray(bundledWorkItem.assignees) ? bundledWorkItem.assignees : baseItem.assignees,
        };
      });
    }

    const hydrationState = buildDetailBundleHydrationState(bundle);

    if (hydrationState.currentUser) {
      setCurrentUserId(hydrationState.currentUser.id);
      setCurrentUserName(hydrationState.currentUser.name);
      setCurrentUserRole(hydrationState.currentUser.role);
      cacheCurrentUser?.(hydrationState.currentUser);
    }

    setCurrentOperationsRole(hydrationState.operationsRole);
    // Filter out permission keys from the cached hydration bundle —
    // permission values come from the offline-backed useEffectivePermissions
    // hook. Only config-specific keys belong in operationsConfigSettings.
    {
      const rawHydrated = hydrationState.operationsFeatureSettings as UnknownRecord;
      const configOnly: UnknownRecord = {};
      for (const [key, value] of Object.entries(rawHydrated)) {
        if (!PERMISSION_DEFINITIONS_BY_KEY.has(key)) {
          configOnly[key] = value;
        }
      }
      setOperationsConfigSettings(configOnly);
    }

    setJobTypes(hydrationState.jobTypes);
    cacheJobTypes?.(hydrationState.jobTypes);

    setConfiguredTabs(hydrationState.configuredTabs);
    if (workItem?.item_type) {
      setCachedTabConfig(workItem.item_type, hydrationState.configuredTabs);
    }

    applyLoadedCounts(hydrationState.counts);
    setDetailSidebarInitialData(hydrationState.sidebarInitialData);
    setOverviewInitialData(hydrationState.overviewInitialData);
    setParentProject(hydrationState.parentProject ?? null);

    if (Array.isArray(hydrationState.operationsUsersToHydrate)) {
      hydrateOperationsUsers?.(hydrationState.operationsUsersToHydrate as OperationsUser[]);
    }
  }, [applyLoadedCounts, cacheCurrentUser, cacheJobTypes, hydrateOperationsUsers, incomingWorkItem, workItem?.item_type]);

  const loadDetailBundle = useCallback(async () => {
    if (!workItem?.id || isSyntheticItem) {
      return false;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        return false;
      }

      const response = await fetch(`/api/v1/work-items/${workItem.id}/detail-bundle`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (!data?.success || !data.data) {
        return false;
      }

      applyDetailBundle(data.data as WorkItemDetailBundlePayload);
      return true;
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('Error loading work item detail bundle:', error);
      return false;
    }
  }, [applyDetailBundle, isSyntheticItem, workItem?.id]);

  // Load parent project for jobs (linked via child_of relationship) as a fallback if the bundle fails
  useEffect(() => {
    if (detailBundleStatus !== 'failed' || !workItem?.id || workItem.item_type !== 'job') {
      return;
    }
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    fetch('/api/v1/work-item-relationships', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getHierarchy', workItemId: workItem.id }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const project = (data.data?.ancestors || []).find((a: WorkItemHierarchyAncestor) => a.item_type === 'project');
          setParentProject(project ? { id: project.id, item_number: project.item_number, title: project.title } : null);
        }
      })
      .catch(() => setParentProject(null));
  }, [detailBundleStatus, workItem?.id, workItem?.item_type]);

  // Sync view mode when readOnly prop changes (e.g., switching between appointments)
  useEffect(() => {
    setIsViewMode(readOnly || isAdminReopenOnlyLocked);
  }, [isAdminReopenOnlyLocked, readOnly, workItem?.id]);

  useEffect(() => {
    if (!isAdminReopenOnlyLocked) {
      return;
    }

    clearPendingFieldSave();
    setHasUnsavedChanges(false);
  }, [clearPendingFieldSave, isAdminReopenOnlyLocked]);

  // Helper: initialize formData from a work-item object
  const initFormDataFromItem = useCallback((item: WorkItem | FormDataSourceItem | null | undefined) => {
    const sourceItem = item || {} as FormDataSourceItem;
    const persistableItem = { ...sourceItem } as WorkItemFormData;
    delete persistableItem.__appointmentDetailMode;
    delete persistableItem.__appointmentGroupContext;

    const tagsValue = sourceItem.tags;
    const tagsString = Array.isArray(tagsValue) ? tagsValue.join(', ') : typeof tagsValue === 'string' ? tagsValue : '';
    const customFields = parseUnknownRecord(sourceItem.custom_fields);
    const resolvedSubtype = sourceItem.item_subtype || (typeof customFields.job_type === 'string' ? customFields.job_type : null);

    setFormData({
      ...persistableItem,
      ...customFields,
      contact_id: toNullableNumber(sourceItem.contact_id) ?? undefined,
      location_id: toNullableNumber(sourceItem.location_id) ?? undefined,
      contact_person_id: toNullableNumber((sourceItem as { contact_person_id?: unknown }).contact_person_id) ?? undefined,
      contact_name: typeof sourceItem.contact_name === 'string' ? sourceItem.contact_name : undefined,
      location_name: typeof sourceItem.location_name === 'string' ? sourceItem.location_name : undefined,
      contact_person: typeof (sourceItem as { contact_person?: unknown }).contact_person === 'string'
        ? (sourceItem as { contact_person?: string }).contact_person
        : undefined,
      item_subtype: resolvedSubtype,
      tags: tagsString
    });
    setHasUnsavedChanges(false);
    if (sourceItem.item_subtype && templates.length > 0) {
      const template = templates.find((t) => t.item_subtype === sourceItem.item_subtype);
      setCurrentTemplate(template || null);
    } else {
      setCurrentTemplate(null);
    }
  }, [templates]);

  // Initialize form data when work item changes (prop-based)
  useEffect(() => {
    if (!isOpen || !workItem) {
      return;
    }

    if (initializedFormPresentationKeyRef.current === modalPresentationKey) {
      return;
    }

    initializedFormPresentationKeyRef.current = modalPresentationKey;
    initFormDataFromItem(workItem);
  }, [initFormDataFromItem, isOpen, modalPresentationKey, workItem]);

  // Fall back to a direct item fetch only when the consolidated bundle could not be loaded.
  useEffect(() => {
    if (!isOpen || !workItem?.id || isSyntheticItem || detailBundleStatus !== 'failed') {
      return;
    }

    if (freshFetchPresentationKeyRef.current === modalPresentationKey) {
      return;
    }

    freshFetchPresentationKeyRef.current = modalPresentationKey;

    let cancelled = false;

    const fetchFresh = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`/api/v1/work-items?ids=${workItem.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const json = await res.json();
          const freshItem = json.data?.[0];
          if (freshItem && !cancelled) {
            const normalizedFreshItem = freshItem as WorkItem;
            setResolvedWorkItem((previous) => {
              const baseItem = incomingWorkItem ?? previous;

              if (!baseItem) {
                return normalizedFreshItem;
              }

              const mergedItem: WorkItem = {
                ...baseItem,
                ...normalizedFreshItem,
                custom_fields: normalizedFreshItem.custom_fields ?? baseItem.custom_fields,
              };

              if ('assignees' in normalizedFreshItem) {
                mergedItem.assignees = normalizedFreshItem.assignees;
              }

              if ('assigned_to' in normalizedFreshItem) {
                mergedItem.assigned_to = normalizedFreshItem.assigned_to;
              }

              if ('assigned_team' in normalizedFreshItem) {
                mergedItem.assigned_team = normalizedFreshItem.assigned_team;
              }

              return mergedItem;
            });
            initFormDataFromItem(freshItem);
          }
        }
      } catch (err) {
        console.error('Fresh-fetch on open error:', err);
      }
    };

    void fetchFresh();

    return () => {
      cancelled = true;
    };
  }, [detailBundleStatus, incomingWorkItem, initFormDataFromItem, isOpen, isSyntheticItem, modalPresentationKey, workItem?.id]);

  // Load job types for the job type selector (with module-level caching)
  const loadJobTypes = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/job-types', {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const types = data.data || [];
          setJobTypes(types);
          cacheJobTypes?.(types);
        }
      }
    } catch (error) {
      console.error('Error loading job types:', error);
    }
  }, [cacheJobTypes]);

  // Load tab configuration for this item type from YAML-backed default-tabs endpoint (with caching)
  const loadTabConfiguration = useCallback(async (itemType: string) => {
    try {
      const response = await fetch('/api/v1/work-item-types/default-tabs');
      if (response.ok) {
        const data = await response.json();
        const tabs = data.data?.[itemType];
        if (Array.isArray(tabs) && tabs.length > 0) {
          setConfiguredTabs(tabs);
          setCachedTabConfig(itemType, tabs);
        } else {
          setConfiguredTabs(null);
          setCachedTabConfig(itemType, null);
        }
      }
    } catch (error) {
      console.error('Error loading tab configuration:', error);
      setConfiguredTabs(null);
    }
  }, []);

  const loadCurrentUser = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/auth/me', {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          const userId = data.user.id;
          const userName = data.user.display_name || data.user.name || data.user.email || 'User';
          const userRole = data.user.role || data.user.system_role || 'user';
          setCurrentUserId(userId);
          setCurrentUserName(userName);
          setCurrentUserRole(userRole);
          cacheCurrentUser?.({ id: userId, name: userName, role: userRole });
        }
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }, [cacheCurrentUser]);

  const loadOperationsFeatureSettings = useCallback(async () => {
    try {
      const response = await fetch(
        getOperationsMyFeaturesPath(),
        getOperationsMyFeaturesFetchInit(getAuthToken()),
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (!data?.success) {
        return;
      }

      const nextRole = String(data.operations_role || 'user').toLowerCase();
      setCurrentOperationsRole(
        nextRole === 'admin' || nextRole === 'supervisor' || nextRole === 'user'
          ? nextRole
          : 'user'
      );
      // Store only config-specific keys from my-features. Permission keys
      // come from the offline-backed useEffectivePermissions hook and are
      // merged in the operationsFeatureSettings memo. This matches the
      // SmartTask pattern (extractConfigKeys in useSmartTaskConfig.ts).
      const rawSettings = (data.feature_settings || data.data || {}) as UnknownRecord;
      const configKeys: UnknownRecord = {};
      for (const [key, value] of Object.entries(rawSettings)) {
        if (!PERMISSION_DEFINITIONS_BY_KEY.has(key)) {
          configKeys[key] = value;
        }
      }
      if (data.operations_role) {
        configKeys.operations_role = data.operations_role;
      }
      setOperationsConfigSettings(configKeys);
    } catch (error) {
      console.error('Error loading operations feature settings:', error);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    const handleOperationsRbacRefresh = (event: Event) => {
      const detail = (event as CustomEvent<OperationsRbacRefreshDetail | undefined>).detail;
      if (!isOperationsRbacRefreshRequestedForUser(detail, currentUserId)) {
        return;
      }

      void loadOperationsFeatureSettings();
    };

    const handleClientContextChanged = () => {
      void loadOperationsFeatureSettings();
    };

    // IAM_PERMISSIONS_CHANGED_EVENT is broadcast by the WebSocket IAM
    // channel handler (lifecycle.ts) and by PermissionsSlideOver after a
    // save. useEffectivePermissions already listens for this and re-fetches
    // automatically; we also reload my-features here to refresh config-only
    // keys (template keys, display mode, etc.) that are not permission data.
    const handleIamPermissionsChanged = () => {
      void loadOperationsFeatureSettings();
    };

    window.addEventListener(OPERATIONS_RBAC_REFRESH_REQUESTED_EVENT, handleOperationsRbacRefresh as EventListener);
    window.addEventListener('client-switched', handleClientContextChanged as EventListener);
    window.addEventListener('activeClientChanged', handleClientContextChanged as EventListener);
    window.addEventListener(IAM_PERMISSIONS_CHANGED_EVENT, handleIamPermissionsChanged as EventListener);

    return () => {
      window.removeEventListener(OPERATIONS_RBAC_REFRESH_REQUESTED_EVENT, handleOperationsRbacRefresh as EventListener);
      window.removeEventListener('client-switched', handleClientContextChanged as EventListener);
      window.removeEventListener('activeClientChanged', handleClientContextChanged as EventListener);
      window.removeEventListener(IAM_PERMISSIONS_CHANGED_EVENT, handleIamPermissionsChanged as EventListener);
    };
  }, [currentUserId, isOpen, loadOperationsFeatureSettings]);

  // Load current user and reset loaded tabs when modal opens.
  // Prefer the consolidated detail bundle, but retain the legacy per-resource fallback.
  useEffect(() => {
    if (!isOpen || !workItem) {
      return;
    }

    if (modalInitializationPresentationKeyRef.current === modalPresentationKey) {
      return;
    }

    modalInitializationPresentationKeyRef.current = modalPresentationKey;

    const defaultOpenTab: WorkItemTabId = initialTab || (workItem.item_type === 'team_member' ? 'team_overview' : 'overview');
    setLoadedTabs(new Set([defaultOpenTab]));
    setReferenceDrawingsCount(0);
    setDetailSidebarInitialData(null);
    setOverviewInitialData(null);
    setParentProject(null);
    countsLoadedRef.current = false;

    const cachedUser = getCachedCurrentUser();
    if (cachedUser) {
      setCurrentUserId(cachedUser.id);
      setCurrentUserName(cachedUser.name);
      setCurrentUserRole(cachedUser.role);
    }

    const cachedTabs = getCachedTabConfig(workItem.item_type);
    setConfiguredTabs(cachedTabs !== undefined ? cachedTabs : null);

    const cachedJT = getCachedJobTypes();
    setJobTypes(cachedJT || []);

    const runLegacyInitTasks = () => {
      const initTasks: Promise<void>[] = [];

      if (!cachedUser) {
        initTasks.push(loadCurrentUser());
      }

      initTasks.push(loadOperationsFeatureSettings());

      if (cachedTabs === undefined) {
        initTasks.push(loadTabConfiguration(workItem.item_type));
      }

      if (!cachedJT) {
        initTasks.push(loadJobTypes());
      }

      if (initTasks.length > 0) {
        Promise.all(initTasks).catch((error) => console.error('Init tasks error:', error));
      }
    };

    if (isSyntheticItem) {
      setDetailBundleStatus('idle');
      runLegacyInitTasks();
      return;
    }

    let cancelled = false;
    setDetailBundleStatus('loading');

    void (async () => {
      const loaded = await loadDetailBundle();
      if (cancelled) {
        return;
      }

      if (loaded) {
        setDetailBundleStatus('loaded');
        return;
      }

      setDetailBundleStatus('failed');
      runLegacyInitTasks();
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- workItem?.id replaces workItem to prevent re-fire when setResolvedWorkItem creates a new reference
  }, [initialTab, isOpen, isSyntheticItem, loadCurrentUser, loadDetailBundle, loadJobTypes, loadOperationsFeatureSettings, loadTabConfiguration, modalPresentationKey, workItem?.id]);

  useEffect(() => {
    if (!isOpen || !workItem?.item_type) {
      setDemoConfiguredTabs(null);
      return;
    }

    if (customVisibleTabs && customVisibleTabs.length > 0) {
      setDemoConfiguredTabs(null);
      return;
    }

    let isCancelled = false;

    const loadDemoTabConfiguration = async () => {
      try {
        const { ok, data } = await fetchDemoSessionResponse(TOTAL_OPERATIONS_DEMO_MODULE_KEY);

        if (!ok || !data.success || !data.session?.is_demo_mode) {
          if (!isCancelled) {
            setDemoConfiguredTabs(null);
          }
          return;
        }

        const demoSession = data.session.source_configuration
          ? data.session
          : await (async () => {
            const hydrated = await fetchDemoSessionResponse(TOTAL_OPERATIONS_DEMO_MODULE_KEY, {
              force: true,
              includeSourceConfiguration: true,
            });

            if (!hydrated.ok || !hydrated.data.success || !hydrated.data.session?.is_demo_mode) {
              return null;
            }

            return hydrated.data.session;
          })();

        if (!demoSession) {
          if (!isCancelled) {
            setDemoConfiguredTabs(null);
          }
          return;
        }

        const sourceTabs = demoSession.source_configuration?.work_item_tabs?.[workItem.item_type];

        if (!isCancelled) {
          setDemoConfiguredTabs(Array.isArray(sourceTabs) && sourceTabs.length > 0 ? sourceTabs : null);
        }
      } catch (error) {
        console.error('Error loading demo tab configuration:', error);
        if (!isCancelled) {
          setDemoConfiguredTabs(null);
        }
      }
    };

    void loadDemoTabConfiguration();

    return () => {
      isCancelled = true;
    };
  }, [customVisibleTabs, isOpen, workItem?.item_type]);

  const previewContactPeople = useMemo(() => {
    if (!workItem?.contact_id) return [];
    if (contactPeopleContactId == null) return [];

    return String(contactPeopleContactId) === String(workItem.contact_id)
      ? (contactPeople || [])
      : [];
  }, [contactPeople, contactPeopleContactId, workItem?.contact_id]);

  const { jobTypes: appointmentTypeOptions } = useJobConfig({
    itemType: 'appointment',
    skip: workItem?.item_type !== 'appointment',
  });
  const { workflowStatuses: jobBillingWorkflowStatuses } = useJobConfig({
    itemType: 'job',
    skip: workItem?.item_type !== 'job',
  });

  useEffect(() => {
    if (!(showPreview || showComprehensiveReport)) return;
    if (!workItem?.contact_id || !loadContactPeople) return;

    const contactId = toNullableNumber(workItem.contact_id);
    if (contactId != null) {
      void loadContactPeople(contactId);
    }
  }, [loadContactPeople, showComprehensiveReport, showPreview, workItem?.contact_id]);

  // Reset per-item data when work item changes
  const prevWorkItemIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentId = workItem?.id ? String(workItem.id) : null;
    if (currentId && currentId !== prevWorkItemIdRef.current) {
      prevWorkItemIdRef.current = currentId;
      resetDetailItemData?.();
    }
  }, [modalPresentationKey, resetDetailItemData, workItem?.id]);

  const isOwnerOrRootUser = currentUserRole === 'owner' || currentUserRole === 'root';
  const isAdminUser = isAdminRole(currentUserRole);
  const activeOperationsSolutionKeys = useMemo(() => {
    const rawActiveSolutionKeys = Array.isArray(operationsFeatureSettings.active_solution_keys)
      ? operationsFeatureSettings.active_solution_keys
      : typeof operationsFeatureSettings.active_solution_keys === 'string'
        ? operationsFeatureSettings.active_solution_keys
          .replace(/[{}]/g, '')
          .split(',')
        : [];

    return rawActiveSolutionKeys
      .map((value) => typeof value === 'string' ? value.trim() : '')
      .filter(Boolean);
  }, [operationsFeatureSettings.active_solution_keys]);
  const canViewAiProposalTab = useMemo(() => (
    operationsFeatureSettings.ai_proposal_view_tab === true
    || operationsFeatureSettings.ai_proposal_view_tab === 'true'
  ), [operationsFeatureSettings.ai_proposal_view_tab]);
  const isAiProposalManagerEnabled = activeOperationsSolutionKeys.includes(AI_PROPOSAL_MANAGER_SOLUTION_KEY);
  const canAccessAiProposalManager = canViewAiProposalTab && isAiProposalManagerEnabled;
  const canViewJobBillingWorkspace = useMemo(
    () => isOperationsJobBillingWorkspaceAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser }),
    [isOwnerOrRootUser, operationsFeatureSettings],
  );
  const canGenerateJobInvoiceFromOperationsHeader = useMemo(
    () => isOperationsJobGenerateInvoiceAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser }),
    [isOwnerOrRootUser, operationsFeatureSettings],
  );
  const canSignoffBillingTimers = useMemo(
    () => isOperationsJobBillingTimersSignoffAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser }),
    [isOwnerOrRootUser, operationsFeatureSettings],
  );
  // ── Action-permission-driven gates for the admin portal (WorkItemDetailModal) ──
  // These complement the legacy team feature flags (operations_job_edit_work_item_comments,
  // operations_job_edit_timers_for_completion, operations_job_billing_timers_signoff).
  // Both the action permission AND the legacy feature flag must allow the action;
  // the Actions tab in PermissionsSlideOver can therefore restrict these even when
  // the legacy flag is left at its default. Defaults are permissive so existing
  // behaviour is unchanged until an admin explicitly toggles the action off.
  // Workflow scope for matrix lookups — the item's persisted module_context
  // (e.g. 'service-operations') selects that workflow's Actions-tab slice;
  // listModuleContext is the fallback for synthetic items. Items with no
  // workflow context fall through to the shared flat matrix defaults.
  const itemActionWorkflowKey = useMemo(() => {
    const persisted = typeof workItem?.module_context === 'string' ? workItem.module_context.trim() : '';
    if (persisted) return persisted;
    const launcher = typeof listModuleContext === 'string' ? listModuleContext.trim() : '';
    return launcher || null;
  }, [workItem?.module_context, listModuleContext]);

  const hasItemActionPermission = useCallback((itemType: string | null | undefined, action: OperationsActionKey) => {
    if (!itemType) {
      return false;
    }

    return getEffectiveOperationsActionPermission(
      operationsFeatureSettings,
      currentOperationsRole,
      itemType,
      action,
      itemActionWorkflowKey,
    );
  }, [currentOperationsRole, itemActionWorkflowKey, operationsFeatureSettings]);
  const canEditCommentsAction = useMemo(
    () => hasItemActionPermission(workItem?.item_type, 'edit_comments'),
    [hasItemActionPermission, workItem?.item_type],
  );
  const canEditTimersAction = useMemo(
    () => hasItemActionPermission(workItem?.item_type, 'edit_timers'),
    [hasItemActionPermission, workItem?.item_type],
  );
  const canEditPhotosAction = useMemo(
    () => hasItemActionPermission(workItem?.item_type, 'edit_photos'),
    [hasItemActionPermission, workItem?.item_type],
  );
  // `appointment.create` governs creating new appointments under a job — resolved
  // against the job's workflow slice (itemActionWorkflowKey) just like the other
  // Actions-matrix permissions above.
  const canCreateAppointmentAction = useMemo(
    () => hasItemActionPermission('appointment', 'create'),
    [hasItemActionPermission],
  );
  const canEditWorkItemComments = useMemo(
    () => isOperationsJobEditWorkItemCommentsAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser, isAdmin: isAdminUser }),
    [isOwnerOrRootUser, isAdminUser, operationsFeatureSettings],
  );
  const canEditFieldServicePhotos = useMemo(
    () => isOperationsJobEditFieldServicePhotosAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser, isAdmin: isAdminUser }),
    [isOwnerOrRootUser, isAdminUser, operationsFeatureSettings],
  );
  const supportingDocumentsCommentsReadOnly = useMemo(() => {
    if (!workItem?.id) {
      return false;
    }
    const appointmentClosed = workItem.item_type === 'appointment' && isClosedCommentWorkItemStatus(workItem.status, workItem.item_type);
    return appointmentClosed || !canEditWorkItemComments;
  }, [workItem?.id, workItem?.item_type, workItem?.status, canEditWorkItemComments]);

  const supportingDocumentsCommentsReadOnlyReason = useMemo(() => {
    if (!workItem?.id) {
      return undefined;
    }
    const appointmentClosed = workItem.item_type === 'appointment' && isClosedCommentWorkItemStatus(workItem.status, workItem.item_type);
    if (appointmentClosed) {
      return 'Comments are read-only because this appointment is closed.';
    }
    if (!canEditWorkItemComments) {
      return 'Your team role does not include editing comments on work items.';
    }
    return undefined;
  }, [workItem?.id, workItem?.item_type, workItem?.status, canEditWorkItemComments]);
  const canFinalizeBilling = useMemo(
    () => isOperationsJobBillingFinalizeAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser }),
    [isOwnerOrRootUser, operationsFeatureSettings],
  );
  const resolvedBillingStrictness = useMemo(
    () => resolveOperationsBillingStrictness(operationsFeatureSettings),
    [operationsFeatureSettings],
  );
  const canEditTimersForCompletion = useMemo(
    () => isOperationsJobEditTimersForCompletionAllowed(operationsFeatureSettings, { isOwnerOrRoot: isOwnerOrRootUser }),
    [isOwnerOrRootUser, operationsFeatureSettings],
  );
  const canManageBillingWorkspace = isOwnerOrRootUser || currentUserRole === 'admin' || currentOperationsRole === 'admin';
  const normalizedEffectiveModalStatus = normalizeWorkflowStatusKey(effectiveModalStatus);
  const canViewJobBillableLedger = useMemo(
    () => workItem?.item_type !== 'job' || canShowJobBillableLedgerForStatus(effectiveModalStatus || workItem?.status, jobBillingWorkflowStatuses),
    [effectiveModalStatus, jobBillingWorkflowStatuses, workItem?.item_type, workItem?.status],
  );

  const canReopenLockedStatusFromSidebar = hasItemActionPermission(workItem?.item_type, 'admin_reopen');
  // `close` (Actions tab matrix) gates "closed" as a selectable status in the
  // DetailsSidebar dropdown. The admin_reopened → closed reclose path is
  // governed by admin_reopen instead (server maps it to that action).
  const canCloseStatusFromSidebar = hasItemActionPermission(workItem?.item_type, 'close');
  const canSubmitForBilling = useMemo(
    () => hasItemActionPermission(workItem?.item_type, 'submit_for_billing'),
    [hasItemActionPermission, workItem?.item_type],
  );
  const canReviewBilling = useMemo(
    () => hasItemActionPermission(workItem?.item_type, 'review_billing'),
    [hasItemActionPermission, workItem?.item_type],
  );
  const canApproveBilling = useMemo(
    () => hasItemActionPermission(workItem?.item_type, 'approve_billing'),
    [hasItemActionPermission, workItem?.item_type],
  );
  // `delete` (Actions tab matrix) controls footer Delete visibility. The
  // established self-service path — creators deleting their own future
  // appointments — is preserved alongside the matrix grant.
  const canDeleteWorkItem = useMemo(() => {
    const isFutureAppointment = workItem?.item_type === 'appointment'
      && Boolean(workItem?.scheduled_start)
      && new Date(String(workItem?.scheduled_start)) > new Date();
    const isCreator = Boolean(currentUserId) && Boolean(workItem?.created_by) && currentUserId === workItem?.created_by;
    return hasItemActionPermission(workItem?.item_type, 'delete') || (isFutureAppointment && isCreator);
  }, [hasItemActionPermission, workItem?.item_type, workItem?.scheduled_start, workItem?.created_by, currentUserId]);
  /** Jobs, appointments, and tasks: after admin re-open, allow any workflow status — not only returning to closed. */
  const allowsFlexibleAdminReopenedStatus =
    ['job', 'appointment', 'task'].includes(String(workItem?.item_type ?? ''));
  const lockedStatusCycleMode = useMemo<'default' | 'closed_only' | 'admin_reopen_only' | 'locked_no_options'>(() => {
    if (normalizedEffectiveModalStatus === 'admin_reopened') {
      return allowsFlexibleAdminReopenedStatus ? 'default' : 'closed_only';
    }

    if (normalizedEffectiveModalStatus === 'closed') {
      return canReopenLockedStatusFromSidebar ? 'admin_reopen_only' : 'locked_no_options';
    }

    return 'default';
  }, [allowsFlexibleAdminReopenedStatus, canReopenLockedStatusFromSidebar, normalizedEffectiveModalStatus]);
  const effectiveOperationalUserRole = useMemo<'user' | 'supervisor' | 'admin' | 'owner' | 'root'>(() => {
    if (currentUserRole === 'owner' || currentUserRole === 'root') {
      return currentUserRole;
    }

    if (currentUserRole === 'admin') {
      return 'admin';
    }

    if (currentOperationsRole === 'admin') {
      return 'admin';
    }

    if (currentUserRole === 'supervisor' || currentOperationsRole === 'supervisor') {
      return 'supervisor';
    }

    return 'user';
  }, [currentOperationsRole, currentUserRole]);

  // ── UNIFIED COUNTS: Single API call replaces 8 separate badge-count fetches ──
  const loadAllCounts = useCallback(async () => {
    if (!workItem) return;
    // Skip API calls for synthetic/external items that don't exist in work_items
    if (isSyntheticItem) return;
    if (countsLoadedRef.current) return; // Already loaded for this item

    try {
      const response = await fetch(`/api/v1/work-items/${workItem.id}/counts`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          applyLoadedCounts(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading work item counts:', error);
    }
  }, [applyLoadedCounts, isSyntheticItem, workItem]);

  // Load counts only if we are using the legacy fallback path.
  useEffect(() => {
    if (!isOpen || !workItem?.id) {
      return;
    }

    if (!isSyntheticItem && detailBundleStatus !== 'failed') {
      return;
    }

    countsLoadedRef.current = false;
    void loadAllCounts();
  }, [detailBundleStatus, isOpen, isSyntheticItem, loadAllCounts, workItem?.id]);

  // Legacy loadTabCount — now a no-op for count tabs (counts loaded upfront).
  // Kept as a stub so tab click handlers don't break.
  const loadTabCount = (tabId: string) => {
    // Counts are now loaded in bulk via /counts endpoint on modal open.
    // Individual tab data loading happens inside each tab component via isActive prop.
    setLoadedTabs(prev => new Set([...prev, tabId]));
  };

  const activateTab = useCallback((tabId: WorkItemTabId) => {
    setLoadedTabs(prev => prev.has(tabId) ? prev : new Set([...prev, tabId]));
    setActiveTab(tabId);
  }, []);

  // Autosave function - used by the field-change debounce path
  const handleAutoSave = useCallback(async (dataToSave: WorkItemFormData) => {
    if (!workItem || isSyntheticItem || adminReopenLockedRef.current) return;

    try {
      setIsSaving(true);

      // Convert tags string back to array
      const tagsArray = typeof dataToSave.tags === 'string'
        ? dataToSave.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag)
        : dataToSave.tags;

      const updates = {
        ...dataToSave,
        tags: tagsArray,
        skip_activity_log: true // Prevent autosave from creating activity events
      };

      const response = await fetch(`/api/v1/work-items/${workItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());


      }
    } catch (error) {
      console.error('Autosave error:', error);
    } finally {
      setIsSaving(false);
    }
  }, [isSyntheticItem, workItem]);

  // Wrapper for UnifiedOverviewTab - converts (field, value) to object format
  // Uses functional state update to properly handle multiple rapid updates
  const handleFieldChange = useCallback((field: string, value: unknown) => {
    if (areEquivalentFieldValues(latestFormDataRef.current[field], value)) {
      return;
    }

    if (isSyntheticItem || isAdminReopenOnlyLocked) {
      setFormData(prev => {
        if (areEquivalentFieldValues(prev[field], value)) {
          return prev;
        }

        return { ...prev, [field]: value };
      });
      return;
    }

    // Track pending changes for autosave
    pendingChangesRef.current[field] = value;

    setFormData(prev => {
      if (areEquivalentFieldValues(prev[field], value)) {
        return prev;
      }

      return { ...prev, [field]: value };
    });
    setHasUnsavedChanges(true);

    // Clear existing timer
    clearPendingFieldSave();

    // Set new debounced autosave
    fieldChangeTimerRef.current = setTimeout(() => {
      void handleAutoSave(latestFormDataRef.current);
      pendingChangesRef.current = {};
    }, 3000);
  }, [clearPendingFieldSave, handleAutoSave, isAdminReopenOnlyLocked, isSyntheticItem]);

  // Handler for updating a single key within custom_fields (used by AssetValuationTab)
  const handleCustomFieldChange = useCallback((key: string, value: unknown) => {
    const currentCf = parseUnknownRecord(latestFormDataRef.current.custom_fields);
    const nextCf = { ...currentCf, [key]: value };
    handleFieldChange('custom_fields', nextCf);
  }, [handleFieldChange]);

  // Merged custom_fields: work item's persisted values overlaid with unsaved form edits
  const mergedCustomFieldsForTabs = useMemo<UnknownRecord>(() => {
    const workItemCustomFields = getWorkItemCustomFields(workItem);
    const formCustomFields = parseUnknownRecord(formData.custom_fields);
    return { ...workItemCustomFields, ...formCustomFields };
  }, [formData.custom_fields, workItem]);

  const mergedAppointmentCustomFields = useMemo<UnknownRecord>(() => {
    const workItemCustomFields = getWorkItemCustomFields(workItem);
    const formCustomFields = parseUnknownRecord(formData.custom_fields);

    return {
      ...workItemCustomFields,
      ...formCustomFields,
    };
  }, [formData.custom_fields, workItem]);

  const appointmentWorkItemForOverrides = useMemo(() => (
    workItem?.item_type === 'appointment'
      ? {
        ...workItem,
        item_type: 'appointment',
        item_subtype: formData.item_subtype || workItem.item_subtype || null,
        custom_fields: mergedAppointmentCustomFields,
      }
      : null
  ), [formData.item_subtype, mergedAppointmentCustomFields, workItem]);

  const handleAppointmentRequirementOverrideChange = useCallback((
    requirementKey: AppointmentCompletionRequirementKey,
    updates: { enabled?: boolean; reason?: string },
  ) => {
    const rawOverrides = mergedAppointmentCustomFields.completion_requirement_overrides;
    const nextOverrides = rawOverrides && typeof rawOverrides === 'object'
      ? { ...(rawOverrides as UnknownRecord) }
      : {};

    const currentValue = nextOverrides[requirementKey];
    const currentOverride = isAppointmentRequirementOverride(currentValue) ? currentValue : null;
    const nextEnabled = updates.enabled ?? (currentOverride ? currentOverride.enabled !== false : currentValue !== undefined);
    const nextReason = updates.reason ?? (typeof currentValue === 'string' ? currentValue : currentOverride?.reason || '');

    if (!nextEnabled) {
      delete nextOverrides[requirementKey];
    } else {
      nextOverrides[requirementKey] = {
        ...(typeof currentValue === 'object' && currentValue ? currentValue : {}),
        enabled: true,
        reason: nextReason,
      };
    }

    const nextCustomFields = { ...mergedAppointmentCustomFields };
    if (Object.keys(nextOverrides).length === 0) {
      delete nextCustomFields.completion_requirement_overrides;
    } else {
      nextCustomFields.completion_requirement_overrides = nextOverrides;
    }

    handleFieldChange('custom_fields', nextCustomFields);
  }, [handleFieldChange, mergedAppointmentCustomFields]);

  const refreshCurrentWorkItemModal = useCallback(async () => {
    if (!workItem?.id || isSyntheticItem) {
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        return;
      }

      const response = await fetch(`/api/v1/work-items?ids=${workItem.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const freshItem = data.data?.[0] as WorkItem | undefined;
      if (!freshItem) {
        return;
      }

      setResolvedWorkItem((previous) => {
        const baseItem = previous ?? incomingWorkItem;

        if (!baseItem) {
          return freshItem;
        }

        return {
          ...baseItem,
          ...freshItem,
          custom_fields: freshItem.custom_fields ?? baseItem.custom_fields,
          assignees: Array.isArray(freshItem.assignees) ? freshItem.assignees : baseItem.assignees,
        };
      });

      setFormData((previous) => ({
        ...previous,
        status: freshItem.status,
        progress_percentage: freshItem.progress_percentage,
        custom_fields: freshItem.custom_fields ?? previous.custom_fields,
      }));
    } catch (error) {
      console.error('Error refreshing work item detail modal:', error);
    }
  }, [incomingWorkItem, isSyntheticItem, workItem]);

  const scheduleAppointmentRealtimeRefresh = useCallback(() => {
    if (workItem?.item_type !== 'appointment' || typeof window === 'undefined') {
      return;
    }

    if (groupedRealtimeRefreshTimeoutRef.current) {
      window.clearTimeout(groupedRealtimeRefreshTimeoutRef.current);
    }

    groupedRealtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      groupedRealtimeRefreshTimeoutRef.current = null;
      void refreshCurrentWorkItemModal();
    }, 120);
  }, [refreshCurrentWorkItemModal, workItem?.item_type]);

  const scheduleCurrentWorkItemRealtimeRefresh = useCallback(() => {
    if (!workItem?.id || workItem.item_type === 'appointment' || typeof window === 'undefined') {
      return;
    }

    if (groupedRealtimeRefreshTimeoutRef.current) {
      window.clearTimeout(groupedRealtimeRefreshTimeoutRef.current);
    }

    groupedRealtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      groupedRealtimeRefreshTimeoutRef.current = null;
      void refreshCurrentWorkItemModal();
    }, 120);
  }, [refreshCurrentWorkItemModal, workItem?.id, workItem?.item_type]);

  useWebSocketChannel({
    channel: realtimeClientId ? `operations:client:${realtimeClientId}` : undefined,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
    onMessage: (data) => {
      if (workItem?.item_type !== 'appointment' || !workItem?.id) {
        return;
      }

      const message = isUnknownRecord(data) ? data : {};
      const type = typeof message.type === 'string' ? message.type : '';
      if (!matchesAppointmentRealtimeMessage(data, workItem.id)) {
        return;
      }

      if (type === 'operations_refresh' || type === 'appointment_status_changed' || type.startsWith('work_item_')) {
        scheduleAppointmentRealtimeRefresh();
      }
    },
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleAppointmentRealtimeEvent = (event: Event) => {
      if (workItem?.item_type !== 'appointment' || !workItem?.id) {
        return;
      }

      const detail = (event as CustomEvent<{
        workItemId?: string;
        workItemIds?: string[];
        latestMessage?: unknown;
        message?: unknown;
      }>).detail;

      const latestMessage = detail?.latestMessage ?? detail?.message;
      const matchesCurrentAppointment = latestMessage
        ? matchesAppointmentRealtimeMessage(latestMessage, workItem.id)
        : detail?.workItemId
          ? String(detail.workItemId) === String(workItem.id)
          : Array.isArray(detail?.workItemIds)
            ? detail.workItemIds.some((id) => String(id) === String(workItem.id))
            : false;

      if (!matchesCurrentAppointment) {
        return;
      }

      scheduleAppointmentRealtimeRefresh();
    };

    window.addEventListener('workItemStatusChange', handleAppointmentRealtimeEvent as EventListener);
    window.addEventListener('workItemTimeEntriesChanged', handleAppointmentRealtimeEvent as EventListener);
    window.addEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleAppointmentRealtimeEvent as EventListener);

    return () => {
      window.removeEventListener('workItemStatusChange', handleAppointmentRealtimeEvent as EventListener);
      window.removeEventListener('workItemTimeEntriesChanged', handleAppointmentRealtimeEvent as EventListener);
      window.removeEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleAppointmentRealtimeEvent as EventListener);
    };
  }, [scheduleAppointmentRealtimeRefresh, workItem?.id, workItem?.item_type]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleWorkItemRealtimeEvent = (event: Event) => {
      if (!workItem?.id || workItem.item_type === 'appointment') {
        return;
      }

      const detail = (event as CustomEvent<{
        workItemId?: string;
        workItemIds?: string[];
        latestMessage?: unknown;
        message?: unknown;
      }>).detail;

      const latestMessage = detail?.latestMessage ?? detail?.message;
      const latestPayload = isUnknownRecord(latestMessage) && isUnknownRecord(latestMessage.payload)
        ? latestMessage.payload
        : null;

      const matchesCurrentWorkItem = detail?.workItemId
        ? String(detail.workItemId) === String(workItem.id)
        : Array.isArray(detail?.workItemIds)
          ? detail.workItemIds.some((id) => String(id) === String(workItem.id))
          : latestPayload
            ? [
              latestPayload.work_item_id,
              latestPayload.vehicle_id,
            ].some((value) => value != null && String(value) === String(workItem.id))
            : false;

      if (!matchesCurrentWorkItem) {
        return;
      }

      scheduleCurrentWorkItemRealtimeRefresh();
    };

    window.addEventListener('workItemStatusChange', handleWorkItemRealtimeEvent as EventListener);
    window.addEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleWorkItemRealtimeEvent as EventListener);

    return () => {
      window.removeEventListener('workItemStatusChange', handleWorkItemRealtimeEvent as EventListener);
      window.removeEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleWorkItemRealtimeEvent as EventListener);
    };
  }, [scheduleCurrentWorkItemRealtimeRefresh, workItem?.id, workItem?.item_type]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && groupedRealtimeRefreshTimeoutRef.current) {
        window.clearTimeout(groupedRealtimeRefreshTimeoutRef.current);
        groupedRealtimeRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  // Handle manual save
  const handleSave = async () => {
    if (!workItem || !hasUnsavedChanges || isSyntheticItem || isAdminReopenOnlyLocked) return;

    setIsSaving(true);

    try {
      // Convert tags string back to array
      const tagsArray = typeof formData.tags === 'string'
        ? formData.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag)
        : formData.tags;

      const updates = {
        ...formData,
        tags: tagsArray
      };

      const response = await fetch(`/api/v1/work-items/${workItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setHasUnsavedChanges(false);
        onRefresh?.(); // Refresh the list


      } else {
        console.error('Error saving changes');
      }
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================================================
  // FLUSH & CLOSE — Immediately save any pending changes before closing
  // Prevents data loss from 3-second autosave debounce when user closes quickly
  // ============================================================================
  const handleClose = useCallback(async () => {
    // Cancel any pending debounced autosave
    clearPendingFieldSave();
    // If there are unsaved changes, flush them synchronously before closing
    if (hasUnsavedChanges && workItem && !isSyntheticItem && !isAdminReopenOnlyLocked) {
      try {
        const tagsArray = typeof formData.tags === 'string'
          ? formData.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag)
          : formData.tags;

        const updates = { ...formData, tags: tagsArray };

        await fetch(`/api/v1/work-items/${workItem.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        });
        // Don't bother updating state — we're about to unmount
      } catch (error) {
        console.error('Flush-on-close error:', error);
      }
    }

    // Always refresh parent list so next open gets fresh data
    await onRefresh?.();
    onClose();
  }, [clearPendingFieldSave, formData, hasUnsavedChanges, isAdminReopenOnlyLocked, isSyntheticItem, onClose, onRefresh, workItem]);

  const handleCloseButtonPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    void handleClose();
  }, [handleClose]);

  // Register Capacitor back handler to close modal and restore parent view
  useEffect(() => {
    if (isOpen) {
      const backHandler = (): boolean => {
        void handleClose();
        return true; // Indicate we handled the back press
      };
      registerBackHandler('work-item-detail-modal', backHandler);
      return () => unregisterBackHandler('work-item-detail-modal');
    }
  }, [isOpen, handleClose, registerBackHandler, unregisterBackHandler]);

  const handleDelete = async () => {
    // Visibility and permission share canDeleteWorkItem: the `delete` matrix
    // cell (with owner/root bypass) plus the future-appointment creator path.
    const isFutureAppointment = workItem?.item_type === 'appointment' && workItem?.scheduled_start && new Date(workItem.scheduled_start) > new Date();
    const FINANCIAL_TYPES = ['invoice', 'quote', 'expense', 'credit_note', 'delivery_note', 'grn', 'proforma', 'purchase_order', 'sales_order'];
    const isFinancialOrJob = FINANCIAL_TYPES.includes(workItem?.item_type || '') || workItem?.item_type === 'job';
    const canDelete = canDeleteWorkItem;

    if (!canDelete) {
      if (isFinancialOrJob) {
        notify.error('You do not have permission to delete this work item type');
      } else if (workItem?.item_type === 'appointment') {
        notify.error(isFutureAppointment
          ? 'You do not have permission to delete this appointment'
          : 'Only permitted roles can delete past appointments');
      } else {
        notify.error('You do not have permission to delete this item');
      }
      return;
    }

    const virtualStakeholderDelete = resolveVirtualStakeholderRecordDelete(workItem);

    const confirmMessage = virtualStakeholderDelete
      ? `Are you sure you want to delete this ${workItem?.item_type} record? This action cannot be undone.`
      : `Are you sure you want to delete this ${workItem?.item_type}? This action cannot be undone.`;

    await confirm({
      title: `Delete ${workItem?.item_type}`,
      message: confirmMessage,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          if (virtualStakeholderDelete) {
            const response = await fetch(virtualStakeholderDelete.url, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json'
              },
            });

            if (!response.ok) {
              const data = await response.json();
              throw new Error(data.error || 'Failed to delete stakeholder record');
            }

            notify.success('Record deleted successfully');
          } else {
            const response = await fetch(`/api/v1/work-items`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ ids: [workItem!.id] })
            });

            if (!response.ok) {
              const data = await response.json();
              throw new Error(data.error || 'Failed to delete');
            }

            notify.success(`${workItem?.item_type} deleted successfully`);
          }
          // Let parents sync selection/local state before list refresh (e.g. skip re-fetching a
          // virtual stakeholder record that was just deleted).
          onDelete?.(String(workItem!.id));
          await onRefresh?.();
          onClose();
        } catch (error: unknown) {
          notify.error(getErrorMessage(error, 'Failed to delete'));
        }
      }
    });
  };

  // ============================================================================
  // WORKFLOW ACTION HANDLERS
  // ============================================================================

  // Handle status change from workflow actions
  const handleWorkflowStatusChange = async (newStatus: string) => {
    if (!workItem) return;

    try {
      setIsStatusTransitioning(true);

      // Gate preflight: check process-gates before transitioning to a terminal status
      if (isTerminalWorkItemStatus(newStatus, workItem.item_type)) {
        try {
          const gateRes = await fetch(
            getApiUrl(`/api/v1/work-items/${workItem.id}/process-gates?target_status=${encodeURIComponent(newStatus)}`),
            { headers: { Authorization: `Bearer ${getAuthToken()}` } },
          );
          const gateData = await gateRes.json();
          if (gateRes.ok && gateData.success && gateData.data?.is_blocked) {
            const gateType = gateData.data?.has_process_gate_templates
              ? 'process checklist'
              : 'required form';
            await confirm({
              title: 'Cannot Complete Job',
              message: `The ${gateType} must be completed before this item can be moved to "${newStatus.replace(/_/g, ' ')}".\n\nPlease complete the required items in the Work Breakdown tab.`,
              confirmText: 'Understood',
              cancelText: '',
              variant: 'warning',
              onConfirm: () => { }
            });
            return;
          }
        } catch (gateError) {
          // If the gate check fails, allow the transition (server will also validate)
          console.error('[WorkItemDetailModal] Gate preflight error:', gateError);
        }
      }

      const response = await fetch(getApiUrl(`/api/v1/work-items/${workItem.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      // Handle validation requirements (signature, photos, checklist)
      if (data.validation_required && data.errors) {
        const errorList = Array.isArray(data.errors) ? data.errors : [data.error];
        await confirm({
          title: 'Cannot Complete Job',
          message: `The following requirements must be met before completing this job:\n\n• ${errorList.join('\n• ')}\n\nPlease capture the required data using the SmartTask mobile app or the Field Service tabs.`,
          confirmText: 'Understood',
          cancelText: '',
          variant: 'warning',
          onConfirm: () => { }
        });
        return;
      }

      if (data.success) {
        // Update local form data
        setFormData(prev => ({ ...prev, status: newStatus }));
        notify.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
        onRefresh?.();
      } else {
        notify.error(data.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      notify.error('Failed to update status');
    } finally {
      setIsStatusTransitioning(false);
    }
  };

  // Handle Convert Ticket/Email Task to Job workflow action
  const handleConvertToJob = async () => {
    if (!workItem || !['ticket', 'email_task'].includes(workItem.item_type)) return;

    try {
      // Determine the action based on item type
      let action: string;
      let idField: string;
      let itemLabel: string;

      if (workItem.item_type === 'email_task') {
        action = 'convertEmailTaskToJob';
        idField = 'emailTaskId';
        itemLabel = 'email task';
      } else {
        action = 'convertTicketToJob';
        idField = 'ticketId';
        itemLabel = 'ticket';
      }

      const response = await fetch('/api/v1/service-workflow', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          [idField]: workItem.id
        })
      });

      const data = await response.json();

      if (data.success) {
        const jobNumber = data.job?.item_number || 'New Job';
        notify.success(`Created job: ${jobNumber}`);

        // Ask user what to do with the current item
        notify.confirm({
          title: `Job ${jobNumber} Created`,
          message: `What would you like to do with this ${itemLabel}?`,
          confirmText: 'Move to Resolved',
          cancelText: 'Keep Current Status',
          variant: 'info',
          onConfirm: async () => {
            try {
              const token = getAuthToken();
              const patchResponse = await fetch(`/api/v1/work-items/${workItem.id}`, {
                method: 'PATCH',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: 'resolved' })
              });
              if (patchResponse.ok) {
                notify.success(`${itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1)} moved to Resolved`);
                // Notify parent about status change so kanban board updates
                onStatusChange?.(workItem.id, 'resolved', workItem);
              }
            } catch (err) {
              console.error('Error updating status:', err);
              notify.error('Failed to update status');
            }
            onRefresh?.();
          },
          onCancel: () => {
            // Keep current status - just refresh
            onRefresh?.();
          }
        });
      } else {
        notify.error(data.error || 'Failed to convert to job');
      }
    } catch (error) {
      console.error('Error converting to job:', error);
      notify.error('Failed to convert to job');
    }
  };

  // Handle Generate Billing Submission from job header action — redirect to the
  // billing tab where BillingDashboard.handleGenerateBilling constructs proper
  // line items from timers, BOM, and expenses before creating the draft BLS.
  // The doc type (quote/proforma/invoice) is passed via pendingDocType prop.
  const handleGenerateBillingSubmission = (docType?: 'quote' | 'proforma' | 'invoice') => {
    if (!workItem) return;
    activateTab('billing');
    setActiveBillingSection('submissions');
    if (docType) {
      setPendingDocType(docType);
    } else {
      notify.info('Open the Billing tab and select a document type to generate a billing submission.');
    }
  };

  // Handle Create Invoice workflow action (legacy — kept for backward compat)
  const handleCreateInvoice = async () => {
    if (!workItem) return;

    if (!hasItemActionPermission('invoice', 'create')) {
      notify.error('You do not have permission to create invoices from this workflow');
      return;
    }

    try {
      const response = await fetch('/api/v1/service-workflow', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'createInvoice',
          workItemId: workItem.id
        })
      });

      const data = await response.json();

      if (data.success) {
        notify.success(`Created invoice: ${data.invoice?.item_number || 'New Invoice'}`);
        onRefresh?.();
      } else {
        notify.error(data.error || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      notify.error('Failed to create invoice');
    }
  };

  // Check if workflow actions should be shown (operational item types only)
  const currentWorkItem = workItem;
  const itemType = currentWorkItem?.item_type || '';
  const itemSubtype = currentWorkItem?.item_subtype || '';

  const showReferenceDocsTab = useMemo(() => {
    if (itemType !== 'job' && itemType !== 'task' && itemType !== 'asset') {
      return false;
    }

    // Only show for manufacturing contexts (advanced-manufacturing)
    const launcher = typeof listModuleContext === 'string' ? listModuleContext.trim() : '';
    const persistedCtx = typeof currentWorkItem?.module_context === 'string'
      ? currentWorkItem.module_context.trim()
      : '';

    const isManufacturingContext =
      launcher === ADVANCED_MANUFACTURING_MODULE_CONTEXT ||
      launcher.includes('manufacturing') ||
      persistedCtx === ADVANCED_MANUFACTURING_MODULE_CONTEXT ||
      persistedCtx.includes('manufacturing');

    return isManufacturingContext;
  }, [currentWorkItem?.module_context, itemType, listModuleContext]);

  const showWorkflowActions = ['job', 'ticket', 'task', 'email_task', 'inspection', 'maintenance', 'service_call', 'work_order'].includes(itemType);
  const stakeholderFinancePreviewDefinitions = useMemo(
    () => getStakeholderFinancePreviewOptions(currentWorkItem),
    [currentWorkItem],
  );
  const stakeholderFinanceReportOptions = useMemo<FinanceReportPreviewOption[]>(
    () => stakeholderFinancePreviewDefinitions.map((option) => ({
      id: option.id,
      label: option.label,
      documentType: option.documentType,
    })),
    [stakeholderFinancePreviewDefinitions],
  );
  const stakeholderDefaultFinancePreviewOptionId = useMemo(
    () => getPreferredStakeholderFinancePreviewOptionId(currentWorkItem, stakeholderFinancePreviewDefinitions),
    [currentWorkItem, stakeholderFinancePreviewDefinitions],
  );
  const stakeholderDefaultFinancePreviewOption = useMemo(
    () => stakeholderFinanceReportOptions.find((option) => option.id === stakeholderDefaultFinancePreviewOptionId) || stakeholderFinanceReportOptions[0] || null,
    [stakeholderDefaultFinancePreviewOptionId, stakeholderFinanceReportOptions],
  );
  const stakeholderFinancePreviewPreferenceKey = useMemo(
    () => getStakeholderFinancePreviewPreferenceKey(currentWorkItem),
    [currentWorkItem],
  );
  const stakeholderFinanceTemplatePreferenceNamespace = useMemo(
    () => getStakeholderFinanceTemplatePreferenceNamespace(currentWorkItem),
    [currentWorkItem],
  );
  const stakeholderContactPreviewId = useMemo(
    () => getStakeholderContactId(currentWorkItem),
    [currentWorkItem],
  );
  const stakeholderLinkedAccountId = useMemo(
    () => getStakeholderLinkedAccountId(currentWorkItem),
    [currentWorkItem],
  );
  const stakeholderFinancePreviewRange = useMemo(() => {
    const to = getTodayStrSAST();
    const [year, month] = to.split('-').map(Number);
    const fromYear = month <= 1 ? year - 1 : year;
    const fromMonth = month <= 1 ? 12 : month - 1;
    const from = `${fromYear}-${String(fromMonth).padStart(2, '0')}-01`;

    return { from, to };
  }, []);
  const canOpenStakeholderFinancePreview = stakeholderFinanceReportOptions.length > 0;
  const resolveStakeholderFinancePreviewContext = useCallback(async (optionId: string): Promise<FinanceReportPreviewContext> => {
    const option = stakeholderFinancePreviewDefinitions.find((candidate) => candidate.id === optionId) || stakeholderFinancePreviewDefinitions[0];

    if (!option) {
      throw new Error('No finance reports are available for this stakeholder.');
    }

    const accessToken = authService.getTokens()?.access_token;
    if (!accessToken) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    const response = await fetch('/api/v1/finance-reports', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: option.action,
        ...option.buildParams({
          contactId: stakeholderContactPreviewId,
          linkedAccountId: stakeholderLinkedAccountId,
          dateFrom: stakeholderFinancePreviewRange.from,
          dateTo: stakeholderFinancePreviewRange.to,
        }),
      }),
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || `Failed to load ${option.label.toLowerCase()}.`);
    }

    const period = option.documentType === 'aged_receivables' || option.documentType === 'aged_payables'
      ? { from: stakeholderFinancePreviewRange.to, to: stakeholderFinancePreviewRange.to }
      : stakeholderFinancePreviewRange;

    return {
      documentType: option.documentType,
      reportData: payload.data,
      period,
    };
  }, [stakeholderContactPreviewId, stakeholderFinancePreviewDefinitions, stakeholderFinancePreviewRange, stakeholderLinkedAccountId]);

  const canOpenPrintPreview = useMemo(() => {
    if (canOpenStakeholderFinancePreview) {
      return true;
    }

    if (isSyntheticItem) {
      return false;
    }

    if (itemType !== 'job' && itemType !== 'appointment') {
      return true;
    }

    if (isOwnerOrRootUser || currentUserRole === 'admin' || currentOperationsRole === 'admin') {
      return true;
    }

    return Boolean(
      operationsFeatureSettings.enable_document_generation
      || operationsFeatureSettings.enable_service_reports
      || operationsFeatureSettings.generate_pdf_on_completion
      || operationsFeatureSettings.email_pdf_on_completion
    );
  }, [
    currentOperationsRole,
    currentUserRole,
    isOwnerOrRootUser,
    isSyntheticItem,
    itemType,
    operationsFeatureSettings.email_pdf_on_completion,
    operationsFeatureSettings.enable_document_generation,
    operationsFeatureSettings.enable_service_reports,
    operationsFeatureSettings.generate_pdf_on_completion,
    canOpenStakeholderFinancePreview,
  ]);

  const handleStatusTransitionStart = useCallback(() => {
    setIsStatusTransitioning(true);
  }, []);

  const handleStatusTransitionEnd = useCallback(() => {
    setIsStatusTransitioning(false);
  }, []);

  const handleSidebarStatusChange = useCallback(async (newStatus: string) => {
    const nextIsAdminReopenOnlyLocked = isAdminReopenLockedStatus(newStatus);

    adminReopenLockedRef.current = nextIsAdminReopenOnlyLocked;
    clearPendingFieldSave();
    setHasUnsavedChanges(false);
    setLastSavedAt(new Date());
    setIsViewMode(readOnly || nextIsAdminReopenOnlyLocked);

    const progressPercentage = getSuggestedProgressFromStatus(newStatus, toNullableNumber(formData.progress_percentage));

    applyPersistedWorkItemUpdates({
      status: newStatus,
      progress_percentage: progressPercentage ?? undefined,
    });
    setFormData(prev => ({ ...prev, status: newStatus, progress_percentage: progressPercentage }));

    const stakeholderFields = workItem ? getWorkItemCustomFields(workItem) : {};
    const stakeholderEntityKind = typeof stakeholderFields.stakeholder_entity_kind === 'string'
      ? stakeholderFields.stakeholder_entity_kind
      : typeof stakeholderFields.stakeholder_entity_type === 'string'
        ? stakeholderFields.stakeholder_entity_type
        : null;
    const stakeholderContactId = workItem ? getStakeholderContactId(workItem) : null;

    // Stakeholder contacts are virtual detail items backed by the contacts API,
    // not rows in work_items. Persist their status through the contact record.
    const isStakeholderContact = stakeholderEntityKind === 'contact' && stakeholderContactId !== null;
    if (isStakeholderContact && workItem) {
      try {
        const { mapStakeholderStatusToAccountStatus } = await import('@/lib/work-items/stakeholderStatusConfig');
        const { invalidateContactAccountCache } = await import('@/lib/contacts/accountWarning');
        const { isActiveContactAccountStatus } = await import('@/lib/contacts/accountStanding');
        const accountStatus = mapStakeholderStatusToAccountStatus(newStatus);

        if (accountStatus && stakeholderContactId !== null) {
          const token = authService.getTokens()?.access_token;
          if (token) {
            await fetch(`/api/v1/contacts/${stakeholderContactId}`, {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                account_status: accountStatus,
                is_active: isActiveContactAccountStatus(accountStatus),
              }),
            }).catch(() => null);
            invalidateContactAccountCache(stakeholderContactId);
          }
        }
      } catch (err) {
        console.error('[WorkItemDetailModal] Failed to persist stakeholder status:', err);
      }
    }

    if (workItem) {
      onStatusChange?.(workItem.id, newStatus, workItem);
    }

    await onRefresh?.();
  }, [applyPersistedWorkItemUpdates, clearPendingFieldSave, formData.progress_percentage, onRefresh, onStatusChange, readOnly, workItem]);

  // ============================================================================
  // ITEM TYPE CLASSIFICATION
  // Inventory items (products, services, bundles) should NOT show task-related tabs
  // Financial documents (invoices, quotes, etc.) should NOT show task-related tabs
  // Task/Job items should show all collaboration and field service tabs
  // ============================================================================

  // Check if this is an inventory/catalog item (NOT a task/job)
  const inventorySubtypes = ['product', 'service', 'bundle', 'kit', 'consumable', 'non-inventory'];
  const isInventoryItem = itemType === 'inventory' ||
    inventorySubtypes.includes(itemSubtype);

  // Check if this is a financial document (invoices, quotes, orders, etc.)
  const financialDocumentTypes = [
    'invoice', 'quote', 'expense', 'credit_note', 'delivery_note', 'grn',
    'proforma', 'purchase_order', 'sales_order', 'supplier_credit', 'supplier_quote'
  ];
  const isFinancialDocument = financialDocumentTypes.includes(itemType);

  // Check if this is a team member virtual work item
  const workItemCustomFields = workItem ? getWorkItemCustomFields(workItem) : {};
  const stakeholderEntityKind = typeof workItemCustomFields.stakeholder_entity_kind === 'string'
    ? workItemCustomFields.stakeholder_entity_kind
    : typeof workItemCustomFields.stakeholder_entity_type === 'string'
      ? workItemCustomFields.stakeholder_entity_type
      : null;
  const isStakeholderEntity = Boolean(workItemCustomFields.is_virtual_stakeholder) || Boolean(stakeholderEntityKind);
  // Stakeholder contacts (top-level customer/supplier/etc.) get the standard
  // DetailsSidebar so users can change account/workflow status, see assignees, etc.
  const isStakeholderContactEntity = stakeholderEntityKind === 'contact';

  // SmartTask access gate — config is non-null when SmartTask is enabled for the client
  const { config: smartTaskConfig } = useSmartTaskConfig(isTeamMember);
  const hasTeamSmartTaskAccess = isTeamMember && smartTaskConfig !== null;

  const teamMemberUserId = useMemo(() => {
    if (!isTeamMember || !workItem) {
      return undefined;
    }

    const rawUserId = getWorkItemCustomFields(workItem).user_id;
    if (typeof rawUserId === 'number') {
      return rawUserId;
    }

    if (typeof rawUserId === 'string' && rawUserId.trim().length > 0) {
      const parsedUserId = Number(rawUserId);
      if (Number.isFinite(parsedUserId)) {
        return parsedUserId;
      }
    }

    const parsedFallbackId = Number(workItem.id.replace('team-member-', ''));
    return Number.isFinite(parsedFallbackId) ? parsedFallbackId : undefined;
  }, [isTeamMember, workItem]);

  useEffect(() => {
    if (!isOpen || !isTeamMember || !teamMemberUserId) {
      return;
    }

    const currentTitle = typeof workItem?.title === 'string' ? workItem.title.trim() : '';
    const needsLiveHydration = !currentTitle
      || /^team member\s+\d+$/i.test(currentTitle)
      || !getWorkItemCustomFields(workItem).current_operations;
    const refreshRequested = teamMemberProfileRefreshKey !== appliedTeamMemberRefreshKeyRef.current;

    if (!needsLiveHydration && !refreshRequested) {
      return;
    }
    appliedTeamMemberRefreshKeyRef.current = teamMemberProfileRefreshKey;

    let cancelled = false;

    const loadTeamMemberProfile = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          return;
        }

        const response = await fetch(`/api/v1/team/members/${teamMemberUserId}/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const payload = await response.json().catch(() => null);
        if (!payload?.success || !payload.data || cancelled) {
          return;
        }

        const hydratedTeamMember = payload.data as WorkItem;

        setResolvedWorkItem((previous) => {
          const baseItem = previous ?? incomingWorkItem;

          if (!baseItem) {
            return hydratedTeamMember;
          }

          return {
            ...baseItem,
            ...hydratedTeamMember,
            custom_fields: hydratedTeamMember.custom_fields ?? baseItem.custom_fields,
            assignees: Array.isArray(hydratedTeamMember.assignees) ? hydratedTeamMember.assignees : baseItem.assignees,
          };
        });

        if (needsLiveHydration) {
          initFormDataFromItem(hydratedTeamMember);
        } else {
          // Refresh path: merge only the refreshed custom fields into formData
          // so unrelated unsaved edits elsewhere in the modal are preserved.
          const hydratedCustomFields = parseUnknownRecord(hydratedTeamMember.custom_fields);
          setFormData((previous) => ({
            ...previous,
            ...(hydratedCustomFields as Partial<WorkItemFormData>),
            custom_fields: hydratedTeamMember.custom_fields ?? previous.custom_fields,
          }));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to hydrate team member detail modal profile:', error);
        }
      }
    };

    void loadTeamMemberProfile();

    return () => {
      cancelled = true;
    };
  }, [incomingWorkItem, initFormDataFromItem, isOpen, isTeamMember, teamMemberProfileRefreshKey, teamMemberUserId, workItem]);

  const teamMemberEmployeeId = useMemo(() => {
    if (!isTeamMember || !workItem) {
      return undefined;
    }

    const rawEmployeeId = getWorkItemCustomFields(workItem).employee_id;
    if (typeof rawEmployeeId === 'number') {
      return rawEmployeeId;
    }

    if (typeof rawEmployeeId === 'string' && rawEmployeeId.trim().length > 0) {
      const parsedEmployeeId = Number(rawEmployeeId);
      if (Number.isFinite(parsedEmployeeId)) {
        return parsedEmployeeId;
      }
    }

    return undefined;
  }, [isTeamMember, workItem]);

  // Legal work items get a curated, document-centric default tab set.
  const isLegalWorkItem = LEGAL_WORK_ITEM_TYPES.includes(itemType as LegalWorkItemType);

  // Purpose-built overview routing flags
  const launcherContext = typeof listModuleContext === 'string' ? listModuleContext.trim().toLowerCase() : '';
  const persistedContext = typeof currentWorkItem?.module_context === 'string'
    ? currentWorkItem.module_context.trim().toLowerCase()
    : '';
  const isSoftwareDevelopmentContext = launcherContext === 'software-development' || persistedContext === 'software-development';
  const isSoftwareAgileWorkItem = isSoftwareDevelopmentContext && ['bug', 'feature', 'story', 'epic', 'sprint', 'request', 'milestone', 'task', 'code_review', 'development_task'].includes(itemType);
  const isLogisticsDeliveryContext = launcherContext === 'logistics-delivery' || persistedContext === 'logistics-delivery' || launcherContext === 'waste-management' || persistedContext === 'waste-management';
  const isProject = itemType === 'project';
  const isTask = itemType === 'task';
  const isFollowUp = itemType === 'follow_up';
  const isRecurringJob = itemType === 'recurring_job';
  // Visitor passes (item_type='visit', or the legacy appointment/visitor
  // mirror rows) get a booking card above the standard overview — PIN, QR,
  // host, unit, approval/access status.
  const isVisitorBooking =
    itemType === 'visit' || (itemType === 'appointment' && itemSubtype === 'visitor');
  // Voting sessions ARE their work item (shared UUID since the work-items
  // migration) — the overview hosts the full session workspace.
  const isVotingWorkItem = itemType === 'voting';
  const isGeneratedRecurringJob = itemType === 'job' && Boolean(workItemCustomFields.recurring_job_id);
  const isVehicleAsset = itemType === 'asset' && itemSubtype === 'vehicle';
  // Property parents (units, buildings, tenancies, enquiries, maintenance
  // requests, property inspections) carry scheduled visits — surface the
  // Appointments tab there too, labelled "Visits".
  const isPropertyVisitParent = (currentWorkItem?.module_context || '').trim() === 'property-management' && (
    (itemType === 'asset' && ['unit', 'property', 'building'].includes(itemSubtype))
    || (itemType === 'ticket' && ['tenancy', 'enquiry', 'maintenance_request', 'listing'].includes(itemSubtype))
    || (itemType === 'inspection' && itemSubtype === 'property_inspection')
  );
  const isSharedDetailShellLoading = detailBundleStatus === 'loading' && !isSyntheticItem;

  // Check if this is a task/job type that supports collaboration features
  // Excludes inventory items, financial documents, AND team members
  const isTaskOrJob = !isInventoryItem && !isFinancialDocument && !isTeamMember && !isRecurringJob && !isStakeholderEntity;
  const primaryOverviewTabId: WorkItemTabId = isTeamMember ? 'team_overview' : 'overview';
  const supportsDesktopDetailSidebar = isTaskOrJob || isFinancialDocument || itemType === 'appointment' || isVehicleAsset || isStakeholderContactEntity || isTeamMember || isRecurringJob;
  const supportsMobileDetailSidebar = supportsDesktopDetailSidebar || isTeamMember || isStakeholderEntity;

  // Determine which conditional tabs to show based on subtype
  const showTimeSeriesTab = itemSubtype === 'sensor';
  const showInventoryTab = itemSubtype === 'product' || isInventoryItem;
  const showAppointmentBillingWorkspace = itemType === 'appointment';
  const includeRelatedOperationalData = itemType !== 'appointment';
  const showPipelineTab = itemSubtype === 'opportunity';
  // Assets have item_type='asset' with subtypes like 'boiler', 'heat_pump', 'fire_extinguisher', etc.
  const showMaintenanceTab = itemType === 'asset';
  // Valuation tab — finance-facing depreciation and purchase info for assets
  const showValuationTab = itemType === 'asset';
  const showRecipeTab = itemSubtype === 'recipe';
  // ── History-tab RBAC ─────────────────────────────────────────────────────
  // A user can see the History tab (and its admin-only sub-tabs like Activity
  // / Chat) when they are an admin or above on EITHER axis:
  //   1. Client-level role (currentUserRole)         — owner / admin / root
  //   2. Operations module role (currentOperationsRole) — 'admin'
  // The operations module role is sourced from the team-RBAC system
  // (managed in the Admin Hub IAM Console) and means the user is an
  // admin of at least one operations team — they should be able to audit
  // history on items they have access to even if their *client-level* role
  // is just 'user'.
  const normalizedClientRole = String(currentUserRole || '').toLowerCase();
  const isClientAdminOrAbove = normalizedClientRole === 'admin'
    || normalizedClientRole === 'owner'
    || normalizedClientRole === 'root';
  const isOperationsAdmin = currentOperationsRole === 'admin';
  const canViewHistoryAdmin = isClientAdminOrAbove || isOperationsAdmin;
  const canViewAiReview = (
    ['supervisor', 'admin', 'owner', 'root'].includes(normalizedClientRole)
    || currentOperationsRole === 'supervisor'
    || currentOperationsRole === 'admin'
  ) && canShowAiOperationsReviewTabForItemType(itemType);
  const canViewProcessChecklist = ['supervisor', 'admin', 'owner', 'root'].includes(normalizedClientRole)
    || currentOperationsRole === 'supervisor'
    || currentOperationsRole === 'admin';
  const showHistoryTab = !isTeamMember && (
    isFollowUp
    || canViewAiReview
    || canViewProcessChecklist
    || ((isTaskOrJob || isFinancialDocument || isStakeholderEntity || isVehicleAsset) && canViewHistoryAdmin)
  );

  const hiddenTaskTabIds = useMemo<Set<WorkItemTabId>>(() => (
    isTask
      ? new Set<WorkItemTabId>(['work_breakdown', 'billing', 'time', 'expenses', 'document'])
      : new Set<WorkItemTabId>()
  ), [isTask]);

  const showBillingDashboardTab = isTaskOrJob && !isTask && !showAppointmentBillingWorkspace && !isVehicleAsset;
  const showOperationalBillingTab = showBillingDashboardTab || showAppointmentBillingWorkspace;
  const shouldMapInventoryToBilling = showOperationalBillingTab && !showInventoryTab;
  const defaultBillingSection = useMemo(() => getBillingSectionForTab(initialTab), [initialTab]);
  const defaultAppointmentBillingSection = useMemo(() => getAppointmentBillingSectionForTab(initialTab), [initialTab]);
  // Live appointment-billing section — the tour (and other deep links) drive
  // this so a navigation to a legacy tab id ('time', 'expenses') lands on the
  // matching section inside the appointment billing workspace.
  const [activeAppointmentBillingSection, setActiveAppointmentBillingSection] =
    useState<AppointmentBillingSectionId>(defaultAppointmentBillingSection);
  useEffect(() => {
    setActiveAppointmentBillingSection(defaultAppointmentBillingSection);
  }, [defaultAppointmentBillingSection]);
  const normalizedBillingWorkspaceStatus = normalizeWorkflowStatusKey(workItem?.status);
  const billingWorkspaceReadOnly = (
    (workItem?.item_type === 'job' && isReadOnlyJobBillingStatus(workItem?.status))
    || (['closed', 'archived'].includes(normalizedBillingWorkspaceStatus) && !canManageBillingWorkspace)
  );

  useEffect(() => {
    if (workItem?.item_type !== 'job' || canViewJobBillingWorkspace) {
      return;
    }
    setActiveBillingSection((section) => (['ledger', 'submissions', 'materials', 'expenses', 'costing'].includes(section) ? 'time' : section));
  }, [canViewJobBillingWorkspace, workItem?.item_type]);

  useEffect(() => {
    if ((activeBillingSection as string) !== 'materials') {
      return;
    }

    setActiveBillingSection(canViewJobBillableLedger ? 'submissions' : 'time');
  }, [activeBillingSection, canViewJobBillableLedger]);

  // Product Images tab - Show for inventory items that can have product images
  const showProductImagesTab = ['product', 'bundle', 'kit'].includes(itemSubtype);

  // Field Service tabs - Show for ANY work item that could use mobile data capture
  // This includes: jobs, tickets, inspections, deliveries, collections, maintenance, 
  // waste manifests, logistics runs, installations, repairs, etc.
  // Essentially any work item where photos, signatures, or GPS tracking makes sense
  // BUT NOT for inventory items (products, services, etc.)
  const fieldServiceTypes = [
    'job', 'ticket', 'inspection', 'delivery', 'collection', 'pickup',
    'installation', 'repair', 'maintenance', 'service_call', 'work_order',
    'manifest', 'route', 'delivery_note', 'goods_received', 'transfer',
    'audit', 'incident', 'hazard', 'permit', 'certification',
    'custom', 'task', 'milestone', 'project', 'appointment',
    'asset' // Assets need photos for inspections, condition documentation, compliance evidence
  ];
  const showFieldServiceTabs = isTaskOrJob && fieldServiceTypes.includes(itemType);
  const showSupportingDocumentComments = !isTeamMember && (isTaskOrJob || isFinancialDocument);
  const showSupportingDocumentAttachments = !isTeamMember && isTaskOrJob;
  const showSupportingDocumentPhotos = !isTeamMember && !isTask && (showFieldServiceTabs || isFinancialDocument);
  const showSupportingDocumentSignatures = !isTeamMember && !isTask && showFieldServiceTabs;
  const showSupportingDocumentQuotationRequests = !isTeamMember && (itemType === 'job' || itemType === 'appointment');
  const showStandaloneVehicleAttachmentsTab = isVehicleAsset && showSupportingDocumentAttachments;
  const showSupportingDocumentsTab = !isVehicleAsset && !workspaceItemMode && (
    showSupportingDocumentComments
    || showSupportingDocumentAttachments
    || showSupportingDocumentPhotos
    || showSupportingDocumentSignatures
    || showSupportingDocumentQuotationRequests
  );
  // In workspace item mode, comments and attachments are promoted to top-level tabs
  const showStandaloneCommentsTab = workspaceItemMode && showSupportingDocumentComments;
  const showStandaloneAttachmentsTab = workspaceItemMode && showSupportingDocumentAttachments;
  const historySubTabs = useMemo<Array<{ id: 'activity' | 'watchers' | 'ai_review' | 'process_checklist' | 'chat'; label: string; icon: React.ElementType; count?: number }>>(() => [
    ...(((isTaskOrJob || isFinancialDocument || isStakeholderEntity || isVehicleAsset) && canViewHistoryAdmin) ? [{ id: 'activity' as const, label: 'Activity', icon: Activity, count: activityCount }] : []),
    ...(isTaskOrJob && itemType !== 'appointment' && (canViewHistoryAdmin || isFollowUp) ? [{ id: 'watchers' as const, label: 'Watchers', icon: Bell, count: watchersCount }] : []),
    ...(canViewAiReview ? [{ id: 'ai_review' as const, label: 'AI Review', icon: Sparkles }] : []),
    ...(canViewProcessChecklist ? [{ id: 'process_checklist' as const, label: 'Process Checklist', icon: ClipboardCheck }] : []),
    ...(showFieldServiceTabs && itemType !== 'project' && canViewHistoryAdmin ? [{ id: 'chat' as const, label: 'Chat History', icon: MessagesSquare }] : []),
  ], [activityCount, canViewAiReview, canViewHistoryAdmin, canViewProcessChecklist, isFinancialDocument, isFollowUp, isStakeholderEntity, isTaskOrJob, isVehicleAsset, itemType, showFieldServiceTabs, watchersCount]);

  const supportingDocumentsSections = useMemo(() => {
    const sections: Array<{
      id: SupportingDocumentsSectionId;
      label: string;
      icon: React.ElementType;
      count?: number;
    }> = [];

    if (showSupportingDocumentComments) {
      sections.push({ id: 'comments', label: 'Comments', icon: MessageSquare, count: commentCount });
    }

    if (showSupportingDocumentAttachments) {
      sections.push({ id: 'attachments', label: 'Attachments', icon: Paperclip, count: attachmentsCount });
    }

    if (showSupportingDocumentPhotos) {
      sections.push({
        id: 'photos',
        label: isFinancialDocument ? 'Photos' : 'Field Photos',
        icon: Camera,
      });
    }

    if (showSupportingDocumentSignatures) {
      sections.push({ id: 'signatures', label: 'Signatures', icon: PenTool });
    }

    if (showSupportingDocumentQuotationRequests) {
      sections.push({ id: 'quotation_requests', label: 'Quotation Requests', icon: FileText });
    }

    return sections;
  }, [
    attachmentsCount,
    commentCount,
    isFinancialDocument,
    showSupportingDocumentAttachments,
    showSupportingDocumentComments,
    showSupportingDocumentPhotos,
    showSupportingDocumentSignatures,
    showSupportingDocumentQuotationRequests,
  ]);

  const supportingDocumentsTabCount = commentCount + attachmentsCount;

  const resolveRequestedTabId = useCallback((tabId?: string | null): WorkItemTabId | null => {
    if (!tabId) {
      return null;
    }

    const requestedTab = tabId as WorkItemTabId;

    if (requestedTab === 'drawings' && !showReferenceDocsTab) {
      return null;
    }

    if (showSupportingDocumentsTab && LEGACY_SUPPORTING_DOCUMENT_TAB_IDS.includes(requestedTab)) {
      return 'supporting_documents';
    }

    if (requestedTab === 'subtasks' || requestedTab === 'checklists') {
      return 'work_breakdown';
    }

    if (!showOperationalBillingTab) {
      return requestedTab;
    }

    // 'submissions' is a semantic id for the billing workspace's Submissions
    // section — tour/deep-link navigation lands on the billing host tab and
    // getBillingSectionForTab selects the inner section.
    if (requestedTab === 'time' || requestedTab === 'expenses' || requestedTab === 'maintenance' || tabId === 'submissions') {
      return 'billing';
    }

    if (requestedTab === 'inventory' && shouldMapInventoryToBilling) {
      return 'billing';
    }

    return requestedTab;
  }, [shouldMapInventoryToBilling, showOperationalBillingTab, showSupportingDocumentsTab, showReferenceDocsTab]);

  const tabListIncludes = useCallback((tabIds: readonly string[] | null | undefined, targetTabId: WorkItemTabId) => {
    if (!tabIds || tabIds.length === 0) {
      return false;
    }

    if (targetTabId === 'details') {
      if (tabIds.includes('details')) {
        return true;
      }

      return supportsMobileDetailSidebar && tabIds.includes(primaryOverviewTabId);
    }

    if (targetTabId === 'supporting_documents') {
      if (tabIds.includes('supporting_documents')) {
        return true;
      }

      if (!showSupportingDocumentsTab) {
        return false;
      }

      return LEGACY_SUPPORTING_DOCUMENT_TAB_IDS.some((legacyTabId) => legacyTabId !== 'supporting_documents' && tabIds.includes(legacyTabId));
    }

    if (targetTabId === 'work_breakdown') {
      return tabIds.includes('work_breakdown') || tabIds.includes('subtasks') || tabIds.includes('checklists');
    }

    if (targetTabId !== 'billing') {
      return tabIds.includes(targetTabId);
    }

    if (tabIds.includes('billing')) {
      return true;
    }

    if (!showOperationalBillingTab) {
      return false;
    }

    return LEGACY_BILLING_TAB_IDS.some((legacyTabId) => {
      if (legacyTabId === 'inventory') {
        return shouldMapInventoryToBilling && tabIds.includes(legacyTabId);
      }

      return tabIds.includes(legacyTabId);
    });
  }, [primaryOverviewTabId, shouldMapInventoryToBilling, showOperationalBillingTab, showSupportingDocumentsTab, supportsMobileDetailSidebar]);

  // Reset to a predictable default tab whenever a different item opens.
  useEffect(() => {
    if (!isOpen || !workItem) return;

    const requestedInitialTab = resolveRequestedTabId(initialTab);
    const isNewPresentation = activeTabInitializationRef.current.presentationKey !== modalPresentationKey;
    const didRequestedTabChangeWithinPresentation = !isNewPresentation
      && requestedInitialTab !== null
      && activeTabInitializationRef.current.requestedInitialTab !== requestedInitialTab;

    if (!isNewPresentation && !didRequestedTabChangeWithinPresentation) {
      return;
    }

    activeTabInitializationRef.current = {
      presentationKey: modalPresentationKey,
      requestedInitialTab,
    };

    if (requestedInitialTab === 'supporting_documents') {
      setActiveSupportingDocumentsSection(getSupportingDocumentsSectionForTab(initialTab));
    }

    const defaultOpenTab: WorkItemTabId = requestedInitialTab || (workItem.item_type === 'team_member' ? 'team_overview' : 'overview');
    pendingInitialTabRef.current = requestedInitialTab;
    setActiveTab(prev => prev === defaultOpenTab ? prev : defaultOpenTab);
  }, [initialTab, isOpen, modalPresentationKey, resolveRequestedTabId, workItem, workItem?.item_type]);

  const tabs: TabConfig[] = [
    { id: 'overview', label: 'Overview', icon: Edit, visible: !isTeamMember },
    // Voting session panels — agenda/motions, voter roll, quorum posture and
    // results host the bespoke voting editors inside the modal.
    { id: 'voting_agenda', label: 'Agenda', icon: ListOrdered, visible: isVotingWorkItem },
    { id: 'voting_participants', label: 'Participants', icon: Users, visible: isVotingWorkItem },
    { id: 'voting_quorum', label: 'Quorum', icon: Scale, visible: isVotingWorkItem },
    { id: 'voting_results', label: 'Results', icon: Trophy, visible: isVotingWorkItem },
    { id: 'invocation_history', label: 'Invocation History', icon: History, visible: isRecurringJob },
    // Stakeholder-specific tabs - People, Locations, Documents
    { id: 'stakeholder_people', label: 'People', icon: Users, visible: isStakeholderEntity },
    { id: 'stakeholder_locations', label: 'Locations', icon: MapPin, visible: isStakeholderEntity },
    { id: 'stakeholder_documents', label: 'Documents', icon: FileText, visible: isStakeholderEntity },
    { id: 'stakeholder_hierarchy', label: 'Hierarchy', icon: Users, visible: isStakeholderEntity },
    { id: 'stakeholder_time_tracking', label: 'Time Tracking', icon: Clock, visible: isStakeholderEntity },
    { id: 'stakeholder_assets', label: 'Assets', icon: Package, visible: isStakeholderEntity },
    // Email context tab - only for email_task items
    { id: 'email', label: 'Correspondence', icon: Mail, visible: itemType === 'email_task' },
    // Appointments/Deliveries tab - jobs use it for child appointments or deliveries
    { id: 'appointments', label: isLogisticsDeliveryContext ? 'Deliveries' : isPropertyVisitParent ? 'Visits' : 'Appointments', icon: isLogisticsDeliveryContext ? Truck : Calendar, count: 0, visible: (itemType === 'job' || isPropertyVisitParent) && !isLogisticsDeliveryContext },
    { id: 'deliveries', label: 'Deliveries', icon: Truck, count: 0, visible: itemType === 'job' && isLogisticsDeliveryContext },
    { id: 'proposal', label: 'Proposals', icon: Sparkles, visible: itemType === 'job' && canAccessAiProposalManager },
    // Jobs tab - only for projects
    { id: 'jobs', label: 'Jobs', icon: Briefcase, visible: itemType === 'project' },
    { id: 'project_plan', label: 'Project Plan', icon: Target, visible: itemType === 'project' },
    { id: 'gantt', label: 'Gantt Chart', icon: GanttChart, visible: false }, // Gantt now lives as sub-tab of Project Plan
    // Task/Job specific tabs - hidden for inventory items, financial documents, and trimmed task detail modals
    { id: 'work_breakdown', label: 'Work Breakdown', icon: CheckSquare, count: workBreakdownCount, visible: isTaskOrJob && itemType !== 'project' && !isTask },
    { id: 'billing', label: 'Billing', icon: DollarSign, count: timeEntriesCount + expensesCount, visible: showOperationalBillingTab },
    // Legacy split tabs are superseded by the unified Billing workspace for operational items.
    { id: 'time', label: 'Time Tracking', icon: Clock, count: timeEntriesCount, visible: isTaskOrJob && !isTask && !showOperationalBillingTab },
    { id: 'inventory', label: 'Billing', icon: Package, visible: showInventoryTab },
    { id: 'expenses', label: 'Expenses', icon: Receipt, count: expensesCount, visible: (itemType === 'job' || itemType === 'appointment' || itemType === 'asset' || itemType === 'project') && !showOperationalBillingTab },
    // Reference Drawings tab — tasks, jobs, and assets (equipment manuals, schematics).
    { id: 'drawings', label: 'Reference Docs', icon: FileText, count: referenceDrawingsCount, visible: showReferenceDocsTab },
    // Budget tab - projects only (job budget insights now live in Billing)
    // { id: 'budget', label: 'Budget', icon: DollarSign, visible: itemType === 'project' },
    // Team Performance tab - projects only
    { id: 'project_team', label: 'Team Performance', icon: Users, visible: itemType === 'project' },
    // Supporting documents consolidates comments, attachments, field photos, signatures, and finance photos
    { id: 'supporting_documents', label: 'Supporting Documents', icon: Paperclip, count: supportingDocumentsTabCount, visible: showSupportingDocumentsTab },
    // Workspace item mode: comments and attachments as top-level tabs (not consolidated)
    { id: 'comments', label: 'Comments', icon: MessageSquare, count: commentCount, visible: showStandaloneCommentsTab },
    { id: 'attachments', label: 'Attachments', icon: Paperclip, count: attachmentsCount, visible: showStandaloneAttachmentsTab },
    // Field Service tabs - only for tasks/jobs (not projects)
    { id: 'chat', label: 'Chat History', icon: MessagesSquare, visible: false },
    { id: 'ai_review', label: 'AI Review', icon: Sparkles, visible: false },
    // Legacy subtasks/checklists tabs removed - consolidated into work_breakdown
    { id: 'watchers', label: 'Watchers', icon: Bell, count: watchersCount, visible: false },
    { id: 'activity', label: 'Activity', icon: Activity, count: activityCount, visible: isGeneratedRecurringJob },
    // History tab - groups Activity, Watchers, AI Review, and Chat History
    { id: 'history', label: 'History', icon: History, count: activityCount, visible: showHistoryTab },
    { id: 'attachments', label: 'Attachments', icon: Paperclip, count: attachmentsCount, visible: showStandaloneVehicleAttachmentsTab },
    // Specialized tabs based on subtype (not for jobs)
    { id: 'timeseries', label: 'Time Series', icon: Activity, visible: showTimeSeriesTab },
    { id: 'productImages', label: 'Images', icon: ImageIcon, visible: showProductImagesTab },
    { id: 'pipeline', label: 'Sales Pipeline', icon: Target, visible: showPipelineTab },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench, visible: showMaintenanceTab && !showOperationalBillingTab },
    { id: 'valuation', label: 'Valuation', icon: TrendingDown, visible: showValuationTab },
    { id: 'recipe', label: 'Recipe BOM', icon: ChefHat, visible: showRecipeTab },
    { id: 'relationships', label: 'Relationships', icon: Link2, count: relationshipsCount, visible: isTaskOrJob && itemType !== 'appointment' && itemType !== 'job' && itemType !== 'project' },
    // Document viewer tab - shows attached documents inline with preview
    { id: 'document', label: 'Document', icon: FileText, visible: isTaskOrJob && !isTask && itemType !== 'appointment' && itemType !== 'job' && itemType !== 'project' },
    // Team Member specific tabs (virtual work item for person management)
    { id: 'team_overview', label: 'Profile', icon: User, visible: isTeamMember },
    { id: 'team_schedule', label: 'Schedule', icon: Calendar, visible: isTeamMember },
    { id: 'team_capacity', label: 'Capacity & Budget', icon: DollarSign, visible: isTeamMember },
    { id: 'team_performance', label: 'Performance', icon: Target, visible: isTeamMember },
    { id: 'team_licenses', label: 'Licenses', icon: Shield, visible: isTeamMember },
    { id: 'team_vehicle_inspections', label: 'Vehicle Inspections', icon: Camera, visible: isTeamMember },
    { id: 'team_reconciliation', label: 'Time vs Vehicle', icon: GitCompareArrows, visible: isTeamMember },
    { id: 'team_mobile_warehouse', label: 'Van Stock', icon: Truck, visible: isTeamMember },
    { id: 'team_smart_task', label: 'Smart Task', icon: Activity, visible: hasTeamSmartTaskAccess },
    // Details tab - mobile only (replaces sidebar)
    { id: 'details', label: 'Details', icon: User, visible: supportsMobileDetailSidebar, mobileOnly: true },
  ];

  const allTabs: TabConfig[] = (() => {
    if (!customTabs || customTabs.length === 0) {
      return tabs;
    }

    const tabsById = new Map<WorkItemTabId, TabConfig>();
    tabs.forEach((tab) => {
      tabsById.set(tab.id, tab);
    });

    customTabs.forEach((tab) => {
      const existing = tabsById.get(tab.id);
      tabsById.set(tab.id, {
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        count: tab.count ?? existing?.count,
        visible: tab.visible ?? existing?.visible ?? true,
        mobileOnly: tab.mobileOnly ?? existing?.mobileOnly,
      });
    });

    return Array.from(tabsById.values());
  })();

  const renderCustomContent = useCallback((content?: WorkItemTabContentRenderer | null) => {
    if (!content || !workItem) {
      return null;
    }

    return typeof content === 'function'
      ? (content as (currentWorkItem: WorkItem) => React.ReactNode)(workItem)
      : content;
  }, [workItem]);

  const renderedOverviewContent = renderCustomContent(overviewContent);
  const renderedMaintenanceContent = renderCustomContent(customTabContent?.maintenance);
  const renderedExpensesContent = renderCustomContent(customTabContent?.expenses);
  const renderedInspectionContent = renderCustomContent(customTabContent?.inspection);
  const renderedLicenseContent = renderCustomContent(customTabContent?.license);
  const renderedHistoryContent = renderCustomContent(customTabContent?.history);
  const renderedTrackingSecurityContent = renderCustomContent(customTabContent?.tracking_security);
  const renderedDashboardContent = renderCustomContent(customTabContent?.dashboard);

  const inferredVisibleTabs = isLegalWorkItem ? LEGAL_DEFAULT_VISIBLE_TABS : null;

  // Filter tabs based on configuration
  // Priority: customVisibleTabs (prop) > demoConfiguredTabs (source client mirror) >
  // legal curated defaults > default hardcoded visibility
  const visibleTabs = allTabs.filter(tab => {
    if (hiddenTaskTabIds.has(tab.id)) {
      return false;
    }

    // Follow-ups get a deliberately lean tab set — checked first so no
    // configured/legacy tab settings can leak supporting docs, billing, etc.
    if (isFollowUp) {
      return FOLLOW_UP_VISIBLE_TAB_IDS.has(tab.id) && tab.visible;
    }

    // Voting sessions get a curated tab set — overview + the voting panels +
    // history. Generic operational tabs (work breakdown, billing, documents,
    // relationships) don't apply to lifecycle-managed ballot sessions.
    if (isVotingWorkItem) {
      return VOTING_VISIBLE_TAB_IDS.has(tab.id) && tab.visible;
    }

    if (tab.id === 'supporting_documents' && showSupportingDocumentsTab) {
      if (customVisibleTabs && customVisibleTabs.length > 0) {
        return tabListIncludes(customVisibleTabs, 'supporting_documents');
      }
      if (demoConfiguredTabs !== null) {
        return tabListIncludes(demoConfiguredTabs, 'supporting_documents');
      }
      if (inferredVisibleTabs && inferredVisibleTabs.length > 0) {
        return tabListIncludes(inferredVisibleTabs, 'supporting_documents') && tab.visible;
      }
      return tab.visible;
    }
    // If customVisibleTabs is provided (prop override from parent), use that exclusively —
    // an explicit curated set (e.g. visit detail) outranks inferred billing tabs.
    if (customVisibleTabs && customVisibleTabs.length > 0) {
      return tabListIncludes(customVisibleTabs, tab.id as WorkItemTabId);
    }
    // billing is new, but older configs may still reference time / expenses / maintenance / inventory.
    // When showOperationalBillingTab is true, always show the billing tab — it's a core operational
    // feature and should not be hidden by legacy tab configurations that predate the unified billing workspace.
    if (tab.id === 'billing' && showOperationalBillingTab) {
      return true;
    }
    if ((tab.id === 'inventory' || tab.id === 'time' || tab.id === 'expenses' || tab.id === 'maintenance') && showOperationalBillingTab) {
      return false;
    }
    // In demo mode, prefer the mirrored source client's tab config over sandbox defaults.
    if (demoConfiguredTabs !== null) {
      if (tab.id === 'overview') return true;
      if (tab.id === 'team_overview' && isTeamMember) return true;
      if (tab.id === 'team_licenses' && isTeamMember) return true;
      if (tab.id === 'team_smart_task' && hasTeamSmartTaskAccess) return true;
      return tabListIncludes(demoConfiguredTabs, tab.id as WorkItemTabId);
    }
    // Legal items should open with a trimmed, role-aware default tab set.
    if (inferredVisibleTabs && inferredVisibleTabs.length > 0) {
      if (tab.id === 'overview') return true;
      return tabListIncludes(inferredVisibleTabs, tab.id as WorkItemTabId) && tab.visible;
    }
    // Fall back to default visibility (legacy behavior - no config saved for this item type)
    return tab.visible;
  });

  const showMobileOverviewDetailsToggle = visibleTabs.some((tab) => tab.id === primaryOverviewTabId)
    && visibleTabs.some((tab) => tab.id === 'details');

  useEffect(() => {
    if (!isOpen || visibleTabs.length === 0) return;
    const normalizedPendingTab = resolveRequestedTabId(pendingInitialTabRef.current);
    if (normalizedPendingTab && visibleTabs.some(tab => tab.id === normalizedPendingTab)) {
      const requestedTab = normalizedPendingTab;
      pendingInitialTabRef.current = null;
      if (requestedTab && activeTab !== requestedTab) {
        setActiveTab(requestedTab);
        setLoadedTabs(prev => new Set([...prev, requestedTab]));
      }
      return;
    }

    if (!visibleTabs.some(tab => tab.id === activeTab)) {
      const fallbackTab = (
        resolveRequestedTabId(initialTab) && visibleTabs.some(tab => tab.id === resolveRequestedTabId(initialTab))
          ? resolveRequestedTabId(initialTab)
          : visibleTabs[0]?.id
      ) as WorkItemTabId | undefined;

      if (fallbackTab) {
        setActiveTab(fallbackTab);
        setLoadedTabs(prev => new Set([...prev, fallbackTab]));
      }
    }
  }, [activeTab, initialTab, isOpen, resolveRequestedTabId, visibleTabs]);

  useEffect(() => {
    if (!showSupportingDocumentsTab || supportingDocumentsSections.length === 0) {
      return;
    }

    if (!supportingDocumentsSections.some((section) => section.id === activeSupportingDocumentsSection)) {
      setActiveSupportingDocumentsSection(supportingDocumentsSections[0].id);
    }
  }, [activeSupportingDocumentsSection, showSupportingDocumentsTab, supportingDocumentsSections]);

  useEffect(() => {
    if (!showHistoryTab || historySubTabs.length === 0) {
      return;
    }

    if (!historySubTabs.some((subTab) => subTab.id === activeHistorySubTab)) {
      setActiveHistorySubTab(historySubTabs[0].id);
    }
  }, [activeHistorySubTab, historySubTabs, showHistoryTab]);

  // Guided-tour navigation. Tour steps carry the *semantic* tab ids the flow
  // authors against ('time', 'photos', 'comments', 'activity', ...), but several
  // of those no longer exist as top-level tabs: comments/photos/etc. fold into
  // Supporting Documents, time/expenses fold into Billing, and activity lives
  // under History. Resolve the real host tab, activate it, and select the
  // matching inner section so the step's focus_target is actually mounted.
  const handleTourNavigate = useCallback((tab: string) => {
    const requested = tab as WorkItemTabId;
    const historySubTabIds: ReadonlySet<string> = new Set([
      'activity', 'watchers', 'ai_review', 'process_checklist', 'chat',
    ]);

    const resolved: WorkItemTabId | null = (() => {
      if (visibleTabs.some((t) => t.id === requested)) {
        return requested;
      }
      const mapped = resolveRequestedTabId(requested);
      if (mapped && visibleTabs.some((t) => t.id === mapped)) {
        return mapped;
      }
      if (historySubTabIds.has(requested) && visibleTabs.some((t) => t.id === 'history')) {
        return 'history';
      }
      return null;
    })();

    if (!resolved) {
      return;
    }

    activateTab(resolved);

    if (resolved === 'supporting_documents') {
      setActiveSupportingDocumentsSection(getSupportingDocumentsSectionForTab(requested));
    } else if (resolved === 'billing') {
      if (showAppointmentBillingWorkspace) {
        setActiveAppointmentBillingSection(getAppointmentBillingSectionForTab(requested));
      } else {
        setActiveBillingSection(getBillingSectionForTab(requested));
      }
    } else if (resolved === 'history' && historySubTabIds.has(requested)) {
      setActiveHistorySubTab(requested as typeof activeHistorySubTab);
    }
  }, [activateTab, resolveRequestedTabId, showAppointmentBillingWorkspace, visibleTabs]);

  const isDark = currentTheme.mode === 'dark';
  const viewModeNavigationProps = { 'data-view-mode-nav': 'true' } as const;

  // ================================================================
  // ITEM TYPE IDENTITY SYSTEM
  // Each work item type gets a unique color, icon, and label so users
  // can instantly identify what they're looking at.
  // ================================================================
  // Theme colors - matching DocumentModal pattern
  const primaryColor = clientBranding?.primary_color || currentTheme.colors.primary;
  const stakeholderModuleColorKey = isStakeholderEntity ? (moduleColorKey || 'total-connect') : '';
  const { moduleColor: stakeholderModuleColor } = useModuleColor(stakeholderModuleColorKey, primaryColor);

  // Type-specific identity
  const typeIdentity = getWorkItemTypeIdentity(itemType, primaryColor);
  const TypeIcon = CustomHeaderIcon || typeIdentity.icon;
  // For appointments, use status-based header color for dynamic theming
  const appointmentStatusHeaderColor = itemType === 'appointment' ? getAppointmentStatusHeaderColor(currentWorkItem?.status) : null;
  const typeHeaderColor = isStakeholderEntity ? stakeholderModuleColor : (appointmentStatusHeaderColor || typeIdentity.color);
  const typeHeaderGradient = isStakeholderEntity || appointmentStatusHeaderColor ? null : typeIdentity.gradient;
  const teamMemberDisplayName = useMemo(() => {
    if (!isTeamMember) {
      return null;
    }

    const candidateTitles = [
      currentWorkItem?.title,
      workItem?.title,
      incomingWorkItem?.title,
      customTitle,
    ];

    const resolvedTitle = candidateTitles
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .find((value) => value.length > 0 && !isPlaceholderTeamMemberTitle(value));

    return resolvedTitle || typeIdentity.label;
  }, [currentWorkItem?.title, customTitle, incomingWorkItem?.title, isTeamMember, typeIdentity.label, workItem?.title]);
  const detailModalTitle = isTeamMember
    ? (teamMemberDisplayName || typeIdentity.label)
    : (customTitle || typeIdentity.label);
  const canShowContextualAssistant = !isFinancialDocument && !isInventoryItem && !isStakeholderEntity;
  const contextualAssistantStatus = formData.status || currentWorkItem?.status || '';
  const isTerminalSchedulingItem = ['job', 'appointment', 'project'].includes(itemType) && isTerminalOrArchivedStatus(contextualAssistantStatus, itemType);
  const shouldShowContextualAssistant = canShowContextualAssistant && !isTerminalSchedulingItem;

  const contextualAssistantDefaultPrompt = useMemo(() => {
    if (!isTeamMember) {
      return '';
    }

    return 'Review this team member across Profile, Schedule, Capacity & Budget, Licenses, Vehicle Inspections, and Smart Task. Summarise any linked vehicle, license, schedule, workload, or compliance issues, and recommend the next best action.';
  }, [isTeamMember]);

  const contextualAssistantWorkItem = useMemo<WorkItemAssistantContext>(() => {
    const assigneeNames = Array.isArray(currentWorkItem?.assignees)
      ? currentWorkItem.assignees
        .map((assignee) => `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim())
        .filter(Boolean)
      : [];
    const progressValue = Number(formData.progress_percentage ?? currentWorkItem?.progress_percentage ?? 0);

    return {
      workItemId: currentWorkItem?.id || '',
      workItemNumber: currentWorkItem?.item_number || '',
      title: String(formData.title || currentWorkItem?.title || ''),
      itemType,
      itemSubtype: itemSubtype || null,
      status: String(formData.status || currentWorkItem?.status || ''),
      priority: String(formData.priority || currentWorkItem?.priority || ''),
      scheduledStart: (formData.scheduled_start || currentWorkItem?.scheduled_start || null) as string | null,
      scheduledEnd: (formData.scheduled_end || currentWorkItem?.scheduled_end || null) as string | null,
      dueDate: (formData.due_date || currentWorkItem?.due_date || null) as string | null,
      estimatedHours: formData.estimated_hours ?? currentWorkItem?.estimated_hours ?? null,
      progressPercentage: Number.isFinite(progressValue) ? progressValue : null,
      contactName: String(formData.contact_name || currentWorkItem?.contact_name || '') || null,
      locationName: String(formData.location_name || currentWorkItem?.location_name || '') || null,
      assigneeNames,
      activeTab,
    };
  }, [activeTab, currentWorkItem, formData, itemSubtype, itemType]);

  const contextualAssistantJobId = useMemo(() => {
    if (itemType === 'job') {
      return currentWorkItem?.id;
    }

    if (itemType === 'appointment' && currentWorkItem?.parent_id) {
      return currentWorkItem.parent_id;
    }

    return undefined;
  }, [currentWorkItem?.id, currentWorkItem?.parent_id, itemType]);

  const contextualAssistantProjectId = useMemo(() => {
    return itemType === 'project' ? currentWorkItem?.id : undefined;
  }, [currentWorkItem?.id, itemType]);

  const financePreviewDocument = useMemo<React.ComponentProps<typeof FinanceDocumentPreview>['document'] | null>(() => {
    if (!currentWorkItem || !isFinanceDocType(currentWorkItem.item_type)) {
      return null;
    }

    return {
      id: currentWorkItem.id,
      item_type: currentWorkItem.item_type,
      item_number: currentWorkItem.item_number,
      doc_number: currentWorkItem.item_number,
      title: currentWorkItem.title ?? undefined,
      status: currentWorkItem.status || '',
      payment_status: currentWorkItem.payment_status ?? undefined,
      contact_id: toNullableNumber(currentWorkItem.contact_id) ?? undefined,
      contact_name: currentWorkItem.contact_name ?? undefined,
      contact_email: currentWorkItem.contact_email ?? undefined,
      contact_phone: currentWorkItem.contact_phone ?? undefined,
      due_date: currentWorkItem.due_date ?? undefined,
      created_at: currentWorkItem.created_at ?? undefined,
      updated_at: currentWorkItem.updated_at ?? undefined,
      custom_fields: parseUnknownRecord(currentWorkItem.custom_fields) as React.ComponentProps<typeof FinanceDocumentPreview>['document']['custom_fields'],
      parent_id: currentWorkItem.parent_id ?? undefined,
    };
  }, [currentWorkItem]);

  /** Effective customer for billing flows: matches Overview (form first), then stakeholder-derived contact when columns are empty */
  const billingContactContext = useMemo(() => {
    if (!currentWorkItem) {
      return null;
    }

    const mergedCustomFields = {
      ...parseUnknownRecord(currentWorkItem.custom_fields),
      ...(formData.custom_fields && typeof formData.custom_fields === 'object'
        ? (formData.custom_fields as UnknownRecord)
        : {}),
    };

    const probe = {
      ...currentWorkItem,
      contact_id: formData.contact_id ?? currentWorkItem.contact_id,
      custom_fields: mergedCustomFields,
    } as WorkItem;

    const contactId =
      toNullableNumber(formData.contact_id)
      ?? toNullableNumber(currentWorkItem.contact_id)
      ?? getStakeholderContactId(probe);

    const contactName =
      typeof formData.contact_name === 'string' && formData.contact_name.trim()
        ? formData.contact_name.trim()
        : currentWorkItem.contact_name ?? undefined;
    const contactEmail =
      typeof formData.contact_email === 'string' && formData.contact_email.trim()
        ? formData.contact_email.trim()
        : currentWorkItem.contact_email ?? undefined;
    const contactPhone =
      typeof formData.contact_phone === 'string' && formData.contact_phone.trim()
        ? formData.contact_phone.trim()
        : currentWorkItem.contact_phone ?? undefined;

    const billingAddressRaw =
      typeof (formData as UnknownRecord).billing_address === 'string'
        ? (formData as UnknownRecord).billing_address
        : typeof currentWorkItem.billing_address === 'string'
          ? currentWorkItem.billing_address
          : undefined;
    const billingAddress =
      typeof billingAddressRaw === 'string' && billingAddressRaw.trim()
        ? billingAddressRaw.trim()
        : undefined;

    return {
      contactId,
      contactName,
      contactEmail,
      contactPhone,
      billingAddress,
    };
  }, [currentWorkItem, formData]);

  const billingWorkspaceItem = useMemo<NonNullable<BillingDashboardProps['workItem']> | undefined>(() => {
    if (!currentWorkItem) {
      return undefined;
    }

    return {
      id: currentWorkItem.id,
      item_number: currentWorkItem.item_number,
      title: currentWorkItem.title ?? undefined,
      item_type: currentWorkItem.item_type,
      item_subtype: currentWorkItem.item_subtype ?? undefined,
      contact_id: billingContactContext?.contactId ?? undefined,
      contact_name: billingContactContext?.contactName ?? undefined,
      status: currentWorkItem.status || undefined,
      assigned_to: currentWorkItem.assigned_to ?? undefined,
      assigned_to_name: currentWorkItem.assigned_to_name ?? undefined,
      assignee_name: currentWorkItem.assignee_name ?? undefined,
      custom_fields: parseUnknownRecord(currentWorkItem.custom_fields),
      assignees: Array.isArray(currentWorkItem.assignees)
        ? currentWorkItem.assignees.map((assignee) => ({
          user_id: (assignee as { user_id?: number | string | null }).user_id,
          first_name: assignee.first_name,
          last_name: assignee.last_name,
          email: (assignee as { email?: string | null }).email,
        }))
        : undefined,
    };
  }, [billingContactContext, currentWorkItem]);

  const appointmentsTabInitialAppointments = useMemo(() => {
    const linkedAppointments = detailSidebarInitialData?.linkedAppointments;
    if (!linkedAppointments || linkedAppointments.length === 0) {
      return undefined;
    }

    return linkedAppointments.map((appointment) => ({
      ...appointment,
      status: appointment.status || 'scheduled',
    }));
  }, [detailSidebarInitialData?.linkedAppointments]);

  const mergeFormDataUpdates = useCallback((updates: Partial<WorkItem>) => {
    setFormData(prev => ({
      ...prev,
      ...(updates as Partial<WorkItemFormData>),
    }));
  }, []);

  const handleAppointmentsTabParentWorkItemUpdate = useCallback((updates: Partial<WorkItem>) => {
    if (!updates || Object.keys(updates).length === 0) {
      return;
    }

    clearPendingFieldSave();
    applyPersistedWorkItemUpdates(updates);
    setFormData((previous) => ({
      ...previous,
      ...(updates as Partial<WorkItemFormData>),
    }));
    setHasUnsavedChanges(false);
    setLastSavedAt(new Date());
  }, [applyPersistedWorkItemUpdates, clearPendingFieldSave]);

  // The billing tab persists the toggle to the work item via PATCH; mirror it
  // into local state so preview/download/email pick it up immediately.
  const handleTimerAggregationModeChange = useCallback((mode: 'individual' | 'team') => {
    handleAppointmentsTabParentWorkItemUpdate({
      custom_fields: {
        ...getWorkItemCustomFields(currentWorkItem),
        timer_aggregation_mode: mode,
      },
    });
  }, [handleAppointmentsTabParentWorkItemUpdate, currentWorkItem]);

  // Per-item stored mode wins; otherwise fall back to the live global preference.
  const previewTimerAggregationMode = getTimerAggregationModeFromCustomFields(currentWorkItem?.custom_fields)
    ?? persistedTimerMode;

  const headerReferenceNumber = isTeamMember
    ? (teamMemberUserId != null ? `USR${teamMemberUserId}` : '')
    : (currentWorkItem?.item_number || '');

  const headerReferenceLabel = isTeamMember
    ? 'User ID'
    : 'Item number';

  const parentBadgeMatchesProject = Boolean(
    workItem?.parent_id
    && parentProject
    && (
      String(workItem.parent_id) === String(parentProject.id)
      || (workItem.parent_job_number && workItem.parent_job_number === parentProject.item_number)
    )
  );

  const shouldShowGenericParentBadge = Boolean(workItem?.parent_id) && !parentBadgeMatchesProject;

  const openContextualAssistant = useCallback((prompt?: string | null) => {
    if (!shouldShowContextualAssistant) return;
    setContextualAssistantPrompt(prompt ?? contextualAssistantDefaultPrompt ?? '');
    setShowContextualAssistant(true);
  }, [contextualAssistantDefaultPrompt, shouldShowContextualAssistant]);

  const supportingDocumentsHeaderActionLabel = shareUrl ? 'Copy share link' : 'Generate and copy share link';

  useEffect(() => {
    if (!shouldShowContextualAssistant && showContextualAssistant) {
      setShowContextualAssistant(false);
    }
  }, [shouldShowContextualAssistant, showContextualAssistant]);

  // Close the AI sub-menu when clicking outside
  useEffect(() => {
    if (!showAiMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target as Node)) {
        setShowAiMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAiMenu]);

  if (!isOpen || !currentWorkItem) return null;

  return (
    <>
    <div className="fixed inset-0 isolate overflow-hidden" style={{ zIndex: containerZIndex }} data-tour-id={containerTourId}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal Container - Full screen on both mobile and desktop */}
      <div className={`absolute inset-0 ${isDark ? 'bg-gray-900' : 'bg-white'} shadow-2xl overflow-hidden flex flex-col`}>
        {isStatusTransitioning && (
          <div className="absolute inset-0 z-[140] bg-black/30 backdrop-blur-sm flex items-center justify-center">
            <div className="w-full max-w-sm px-6">
              <CardLoadingRow
                label="Updating status…"
                primaryColor={primaryColor}
              />
            </div>
          </div>
        )}
        <div
          className={`flex-shrink-0 ${isDark ? 'bg-gray-900' : 'bg-white'}`}
          style={{ height: 'max(env(safe-area-inset-top, 0px), var(--safe-area-top, 0px))' }}
          aria-hidden="true"
        />
        <div
          className="h-1 flex-shrink-0"
          style={{ backgroundColor: typeHeaderColor }}
          aria-hidden="true"
        />
        {/* Header - Type-specific color identity */}
        <div
          className={`px-3 sm:px-6 pt-2 sm:pt-3 pb-1.5 sm:pb-2 text-white ${typeHeaderGradient ? `bg-gradient-to-r ${typeHeaderGradient}` : ''}`}
          style={typeHeaderGradient ? undefined : { backgroundColor: typeHeaderColor }}
        >
          <div className="flex items-center justify-between gap-2">
            {/* Left: Document Info */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-white/18 backdrop-blur shadow-lg flex-shrink-0">
                <TypeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <h2 className="text-sm sm:text-lg font-semibold text-white truncate">
                    {detailModalTitle}
                  </h2>
                  <UserDocsLink
                    docsPath={isTeamMember
                      ? '/total-operations/workflows/service-operations/team-member-detail'
                      : '/total-operations/workflows/service-operations/job-details'}
                    label={isTeamMember ? 'User guide: Team member profile' : 'User guide: Job details'}
                  />
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-[11px] sm:text-xs text-white/80 font-mono flex items-center gap-1">
                    # {headerReferenceNumber}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(headerReferenceNumber, headerReferenceLabel);
                      }}
                      className="p-1 hover:bg-white/10 rounded transition-colors"
                      title={`Copy ${headerReferenceLabel.toLowerCase()}`}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </span>
                  <span className="text-white/40 text-xs">·</span>
                  <span className="text-[11px] sm:text-xs text-white/90 font-medium truncate max-w-[160px] sm:max-w-xs">
                    {isTeamMember ? detailModalTitle : workItem.title}
                  </span>
                </div>
              </div>
            </div>

            {/* Center: Live Appointment Timer */}
            {appointmentTimerInfo && (
              <div className="hidden sm:flex flex-col items-center justify-center px-6 py-1.5 flex-shrink-0 flex-1 animate-in fade-in duration-500">
                {(appointmentTimerInfo.state === 'en_route' || appointmentTimerInfo.state === 'travel_paused') ? (
                  <>
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${appointmentTimerInfo.state === 'travel_paused'
                      ? 'bg-amber-500/30 ring-1 ring-amber-400/50'
                      : appointmentTimerInfo.isLate
                        ? 'bg-orange-500/30 ring-1 ring-orange-400/50'
                        : 'bg-violet-500/30 ring-1 ring-violet-400/50'
                      }`}>
                      {appointmentTimerInfo.state === 'travel_paused' ? (
                        <Pause className="w-4 h-4 text-amber-200" />
                      ) : (
                        <Navigation className={`w-4 h-4 ${appointmentTimerInfo.isLate ? 'text-orange-200 animate-pulse' : 'text-violet-200'}`} />
                      )}
                      <span className="text-xs font-semibold text-white/90">
                        {appointmentTimerInfo.state === 'travel_paused'
                          ? 'Travel Paused'
                          : appointmentTimerInfo.isLate ? 'On Route · Late' : 'On Route'}
                      </span>
                    </div>
                    <span className={`text-3xl sm:text-4xl font-mono font-bold text-white tracking-wider mt-1 ${appointmentTimerInfo.state === 'travel_paused' ? 'opacity-60' : ''}`}>
                      {appointmentTimerInfo.formatted}
                    </span>
                    {appointmentTimerInfo.scheduledTime && (
                      <span className="text-xs text-white/60 mt-0.5">
                        Appt: {appointmentTimerInfo.scheduledTime}
                      </span>
                    )}
                  </>
                ) : appointmentTimerInfo.state === 'paused' ? (
                  <>
                    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/30 ring-1 ring-amber-400/50">
                      <Pause className="w-4 h-4 text-amber-200" />
                      <span className="text-sm font-bold text-white/90">Paused</span>
                    </div>
                    <span className="text-3xl sm:text-4xl font-mono font-bold text-white tracking-wider mt-1 opacity-60">
                      {appointmentTimerInfo.formatted}
                    </span>
                  </>
                ) : (
                  <>
                    <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full ${appointmentTimerInfo.isOverSchedule
                      ? 'bg-red-600 ring-2 ring-red-300 shadow-lg shadow-red-500/40'
                      : 'bg-emerald-500/30 ring-1 ring-emerald-400/50'
                      }`}>
                      <span className={`text-base ${appointmentTimerInfo.isOverSchedule ? 'animate-pulse' : ''}`}>
                        {appointmentTimerInfo.isOverSchedule ? '⏰' : '🔨'}
                      </span>
                      <span className={`text-sm font-bold ${appointmentTimerInfo.isOverSchedule ? 'text-white' : 'text-white/90'}`}>
                        {appointmentTimerInfo.isOverSchedule ? 'Over Schedule' : 'Working'}
                      </span>
                    </div>
                    <span className="text-3xl sm:text-4xl font-mono font-bold text-white tracking-wider mt-1">
                      {appointmentTimerInfo.formatted}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Right: Actions - Compact on mobile */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* 1. Copy share link */}
              {(isTeamMember || (itemType !== 'recurring_job' && !isSyntheticItem)) && (
                <button
                  onClick={handleShareLinkAction}
                  disabled={shareUrlLoading}
                  className="p-2 sm:p-2.5 bg-white/10 hover:bg-white/20 rounded-lg sm:rounded-xl transition-colors disabled:cursor-not-allowed disabled:opacity-70"
                  title={supportingDocumentsHeaderActionLabel}
                >
                  {shareUrlLoading ? (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  )}
                </button>
              )}

              {/* 2. Parent work_item # + link (if applicable) */}
              {shouldShowGenericParentBadge && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (workItem.parent_id) {
                      openWorkItemById(workItem.parent_id);
                    }
                  }}
                  className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
                  title={`Open parent: ${workItem.parent_job_number || 'Parent Item'}`}
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>{workItem.parent_job_number || 'Parent'}</span>
                </button>
              )}
              {parentProject && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openWorkItemById(parentProject.id);
                  }}
                  className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-500/30 text-blue-100 hover:bg-blue-500/50 transition-colors cursor-pointer"
                  title={`Open project: ${parentProject.item_number} — ${parentProject.title}`}
                >
                  <Briefcase className="w-3 h-3" />
                  <span>{parentProject.item_number}</span>
                </button>
              )}

              {/* Other Viewers Badge - Clash Detection */}
              {otherViewersCount > 0 && (
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-500/30 text-amber-100 rounded-lg text-xs font-medium animate-pulse"
                  title={itemViewers.map(v => formatViewerDisplayName(v)).join(', ')}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">
                    {otherViewersCount === 1
                      ? `${formatViewerDisplayName(itemViewers[0]) || '1 other'} viewing`
                      : `${otherViewersCount} others viewing`
                    }
                  </span>
                  <span className="sm:hidden">{otherViewersCount}</span>
                </div>
              )}

              {/* 3. Print */}
              {canOpenPrintPreview && (
                <button
                  onClick={() => setShowPreview(true)}
                  className="hidden sm:flex p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                  title={canOpenStakeholderFinancePreview ? 'Finance report preview' : 'Print / Full Report Preview'}
                >
                  <Printer className="w-5 h-5 text-white" />
                </button>
              )}

              {/* 4. Save / Saving / Saved button and state */}
              {isViewMode && isLocked ? (
                /* Locked badge — item cannot be edited (e.g. missed/closed appointment) */
                <div
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg text-white/80 text-sm"
                  title={lockedBadgeTitle}
                >
                  <Ban className="w-4 h-4" />
                  <span>Locked</span>
                </div>
              ) : isViewMode ? (
                /* Edit button — click to enter edit mode (RBAC-linkable).
                   When readOnly with an onEdit callback, delegate to parent
                   (e.g. RecurringJobsList opens its own edit modal). */
                <button
                  onClick={() => {
                    if (readOnly && onEdit) {
                      onEdit(workItem);
                    } else {
                      setIsViewMode(false);
                    }
                  }}
                  className="px-2 sm:px-4 py-1.5 sm:py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium shadow-sm transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              ) : (
                /* Save button — normal edit mode */
                <button
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges}
                  className={`p-2 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl font-medium flex items-center gap-1 sm:gap-2 transition-all ${hasUnsavedChanges
                    ? 'bg-white text-gray-900 hover:bg-gray-100'
                    : 'bg-white/20 text-white/60 cursor-not-allowed'
                    }`}
                >
                  {isSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : !hasUnsavedChanges && lastSavedAt ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">
                    {isSaving ? 'Saving...' : !hasUnsavedChanges && lastSavedAt ? 'Saved' : 'Save'}
                  </span>
                </button>
              )}

              {/* 5. Final Action Dropdown Button */}
              {showWorkflowActions && (
                <div className="relative shrink-0" data-action-dropdown>
                  <button
                    onClick={() => setShowActionDropdown(!showActionDropdown)}
                    disabled={isStatusTransitioning}
                    className="inline-flex h-9 items-center justify-center gap-1 rounded-xl bg-white px-2.5 text-gray-900 shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-50"
                    title={isStatusTransitioning ? 'Working…' : 'Actions'}
                    aria-label={isStatusTransitioning ? 'Working' : 'Actions'}
                  >
                    <ArrowRight className="w-4 h-4" />
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showActionDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showActionDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-gray-200 bg-white py-1.5 shadow-2xl z-[90]">
                      {/* Convert to section */}
                      {(['ticket', 'email_task'].includes(itemType) && workItem.status !== 'closed' && workItem.status !== 'resolved' && workItem.status !== 'completed' && workItem.status !== 'done') && (
                        <>
                          <div className="px-3 py-2">
                            <span className="text-[10px] font-semibold uppercase text-gray-500">Convert to</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setShowActionDropdown(false);
                              handleConvertToJob();
                            }}
                            className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Briefcase className="w-4 h-4 mt-0.5 text-green-600" />
                            <span>
                              <span className="block">Job</span>
                              <span className="mt-0.5 block text-xs text-gray-500">Convert this {itemType === 'ticket' ? 'ticket' : 'email task'} to a job</span>
                            </span>
                          </button>
                        </>
                      )}
                      {itemType === 'job' && !isHardBlockedJobBillingStatus(workItem?.status) && (
                        <>
                          <div className="px-3 py-2">
                            <span className="text-[10px] font-semibold uppercase text-gray-500">Billing Submission</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setShowActionDropdown(false);
                              handleGenerateBillingSubmission('quote');
                            }}
                            className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <FileText className="w-4 h-4 mt-0.5 text-blue-600" />
                            <span>
                              <span className="block">Generate Quote</span>
                              <span className="mt-0.5 block text-xs text-gray-500">Create a quote billing submission for finance review</span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowActionDropdown(false);
                              handleGenerateBillingSubmission('proforma');
                            }}
                            className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <FileText className="w-4 h-4 mt-0.5 text-purple-600" />
                            <span>
                              <span className="block">Generate Pro-forma</span>
                              <span className="mt-0.5 block text-xs text-gray-500">Create a pro-forma billing submission for finance review</span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowActionDropdown(false);
                              handleGenerateBillingSubmission('invoice');
                            }}
                            className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <FileText className="w-4 h-4 mt-0.5 text-cyan-600" />
                            <span>
                              <span className="block">Generate Invoice</span>
                              <span className="mt-0.5 block text-xs text-gray-500">Create an invoice billing submission for finance review</span>
                            </span>
                          </button>
                        </>
                      )}

                      {/* Actions section */}
                      <div className="mx-2 border-t border-gray-100" />
                      <div className="px-3 py-2">
                        <span className="text-[10px] font-semibold uppercase text-gray-500">Actions</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionDropdown(false);
                          void handleWorkflowStatusChange('completed');
                          handleShareLinkAction();
                        }}
                        disabled={isStatusTransitioning}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Send className="w-4 h-4 text-blue-600" />
                        <span className="leading-tight">Complete & Send</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionDropdown(false);
                          void handleWorkflowStatusChange('completed');
                        }}
                        disabled={isStatusTransitioning}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span>Complete</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Close Button */}
              <button
                type="button"
                onPointerDown={handleCloseButtonPointerDown}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleClose();
                }}
                className="p-2 sm:p-2.5 hover:bg-white/10 rounded-lg sm:rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Tab navigation — ModuleHeader typeFilterTabs pill-toggle style, left-aligned */}
          {(() => {
            const headerTabs = visibleTabs.filter(t => !t.mobileOnly);
            return (
              <div
                className="mt-1.5 flex items-center gap-0 bg-white/10 overflow-x-auto scrollbar-thin scrollbar-thumb-white/30 rounded-xl p-1 self-start"
                data-tour-id={tabsTourId || 'work-item-detail-tabs'}
              >
                {headerTabs.map(tab => {
                  const TabIcon = tab.icon;
                  const isActiveTab = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => activateTab(tab.id)}
                      className={`flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap transition-colors px-2.5 py-1 text-xs sm:text-sm ${isActiveTab ? 'bg-white text-gray-800 shadow-lg' : 'text-white/90 hover:bg-white/20'
                        }`}
                    >
                      <TabIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      {tab.count !== undefined && tab.count > 0 && (
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${isActiveTab ? 'bg-gray-100 text-gray-700' : 'bg-white/20 text-white'
                          }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}

        </div>

        {/* Title Bar removed — title now lives inside the gradient header above */}

        {/* SLA Timers for Support Tickets - Full Display */}
        {workItem.item_subtype === 'support-desk' && (
          <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
            <DualSLATimer
              firstResponseDeadline={workItem.sla_first_response_deadline ?? null}
              resolutionDeadline={workItem.sla_resolution_deadline ?? null}
              firstResponseBreached={workItem.sla_response_breached || false}
              resolutionBreached={workItem.sla_resolution_breached || false}
              firstResponseBreachedAt={workItem.sla_response_breached_at}
              resolutionBreachedAt={workItem.sla_resolution_breached_at}
              firstRespondedAt={workItem.sla_first_responded_at}
              isPaused={workItem.status === 'waiting_on_customer'}
              variant="full"
            />
          </div>
        )}

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
          {/* Tab Content - Keep all tabs mounted to prevent re-rendering */}
          <div
            className={`flex-1 overflow-y-auto overscroll-contain ${activeTab === 'billing' ? 'px-0 pt-0 pb-3 sm:pb-6' : 'p-3 pb-3 sm:p-6'
              } [&>div]:min-h-0 ${isViewMode && !shouldRelaxWorkBreakdownViewModeLock
                ? '[&_input]:pointer-events-none [&_select]:pointer-events-none [&_textarea]:pointer-events-none [&_button:not([data-view-mode-nav])]:pointer-events-none [&_[contenteditable]]:pointer-events-none [&_input]:opacity-80 [&_select]:opacity-80 [&_textarea]:opacity-80'
                : ''
              } ${isDark ? 'bg-gray-900' : 'bg-white'}`}
            style={workItemTabViewportStyle}
          >
            {showMobileOverviewDetailsToggle && (
              <div
                className={`sticky top-0 z-20 mb-4 lg:hidden ${activeTab === 'billing'
                  ? 'border-b p-0 sm:px-6 sm:pt-4'
                  : '-mx-3 -mt-3 border-b p-0 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-4'
                  } ${isDark ? 'border-gray-800/80 bg-gray-900/95' : 'border-gray-200/80 bg-white/95'} supports-[backdrop-filter]:backdrop-blur-md max-w-full`}
              >
                <div className={`grid grid-cols-2 gap-1 rounded-xl p-1 ${isDark ? 'bg-white/10' : 'bg-gray-100/90'}`}>
                  {[
                    {
                      id: primaryOverviewTabId,
                      label: 'Overview',
                      icon: FileText,
                    },
                    { id: 'details' as WorkItemTabId, label: 'Details', icon: User },
                  ].map((tab) => {
                    const ToggleIcon = tab.icon;
                    const isActiveMobileToggle = activeTab === tab.id;

                    return (
                      <button
                        key={`mobile-toggle-${tab.id}`}
                        type="button"
                        {...viewModeNavigationProps}
                        onClick={() => activateTab(tab.id)}
                        className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActiveMobileToggle
                          ? 'bg-white text-gray-800 shadow-lg'
                          : isDark
                            ? 'text-white/90 hover:bg-white/10'
                            : 'text-gray-700 hover:bg-white'
                          }`}
                      >
                        <ToggleIcon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className={activeTab === 'overview' && !isTeamMember ? 'block' : 'hidden'}>
              {isSharedDetailShellLoading ? (
                <div className="flex min-h-[55vh] items-center justify-center">
                  {renderBrandedTabLoader('Loading work item details...', '#2563eb', 'py-0')}
                </div>
              ) : (
                <>
                  {/* Stakeholder Overview - for virtual stakeholder entities */}
                  {isStakeholderEntity ? (
                    <StakeholderOverviewTab
                      workItem={workItem}
                      isDark={isDark}
                      onViewPeople={() => {
                        const peopleTab = visibleTabs.find(t => t.id === 'stakeholder_people');
                        if (peopleTab) activateTab('stakeholder_people');
                      }}
                      onViewLocations={() => {
                        const locTab = visibleTabs.find(t => t.id === 'stakeholder_locations');
                        if (locTab) activateTab('stakeholder_locations');
                      }}
                      onViewDocuments={() => {
                        const docTab = visibleTabs.find(t => t.id === 'stakeholder_documents');
                        if (docTab) activateTab('stakeholder_documents');
                      }}
                      onManagePricing={(contactId) => {
                        if (typeof window !== 'undefined') {
                          window.open(`/client-zone/modules/total-finance?section=price-lists&contact=${contactId}`, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      onViewInventory={(contactId) => {
                        if (typeof window !== 'undefined') {
                          window.open(`/client-zone/modules/total-operations?section=inventory&contact=${contactId}`, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      onContactUpdated={() => {
                        // Bubble updates so parent (e.g. ContactManagementHub)
                        // refreshes its contacts list and account caches.
                        onRefresh?.();
                      }}
                      onMergeContact={onMergeContact}
                    />
                  ) : renderedOverviewContent ? (
                    renderedOverviewContent
                  ) : isFinanceDocType(workItem.item_type) ? (
                    /* Finance Document Preview - for invoices, quotes, bills, etc. */
                    <FinanceDocumentPreview
                      document={financePreviewDocument ?? {
                        id: workItem.id,
                        item_type: workItem.item_type,
                        status: workItem.status || '',
                      }}
                      onEdit={() => onEdit?.(workItem)}
                    />
                  ) : isSoftwareAgileWorkItem ? (
                    <SoftwareOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      jobTypes={jobTypes}
                      initialData={overviewInitialData}
                      initialDataLoading={detailBundleStatus === 'loading'}
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                      moduleContext={launcherContext || persistedContext || null}
                    />
                  ) : isProject ? (
                    /* Project-specific overview with financial snapshot, jobs, subtasks */
                    <ProjectOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      workItemId={workItem.id}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                    />
                  ) : isFollowUp ? (
                    /* Follow-up/query items get a lean overview — basics + inline comments */
                    <FollowUpOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      workItemId={workItem.id}
                      onOpenItem={openWorkItemById}
                      onCommentsCountChange={setCommentCount}
                    />
                  ) : isTask ? (
                    /* Task-specific overview with effort tracking, parent context, dependencies, subtasks */
                    <TaskOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      jobTypes={jobTypes}
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                    />
                  ) : isLegalWorkItem ? (
                    /* Legal-specific overview with inline document workspace */
                    <LegalOverviewTab
                      workItem={workItem}
                      formData={formData}
                      isDark={isDark}
                      workItemId={workItem.id}
                      onOpenItem={openWorkItemById}
                    />
                  ) : workspaceItemMode ? (
                    /* Workspace (Jira-like) items use TaskOverviewTab for a leaner overview */
                    <TaskOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      jobTypes={jobTypes}
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                    />
                  ) : isRecurringJob ? (
                    /* Recurring job schedules use a dedicated overview tab with schedule-specific editing */
                    <RecurringJobOverviewTab
                      workItem={workItem}
                      formData={formData}
                      isDark={isDark}
                      onRefresh={onRefresh}
                    />
                  ) : isVotingWorkItem ? (
                    /* Voting session — agenda, voter roll, results and
                       lifecycle actions live inside the overview tab */
                    <VotingOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      jobTypes={jobTypes}
                      initialData={overviewInitialData}
                      initialDataLoading={detailBundleStatus === 'loading'}
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                      onRefresh={onRefresh}
                    />
                  ) : isVisitorBooking ? (
                    /* Visitor booking — booking card (PIN/QR/host/status) + standard overview */
                    <VisitorOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      jobTypes={jobTypes}
                      initialData={overviewInitialData}
                      initialDataLoading={detailBundleStatus === 'loading'}
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                    />
                  ) : (
                    /* Unified Static Overview - for jobs, appointments, and other operational types */
                    <UnifiedOverviewTab
                      workItem={workItem}
                      formData={formData}
                      onDataChange={handleFieldChange}
                      isDark={isDark}
                      jobTypes={jobTypes}
                      initialData={overviewInitialData}
                      initialDataLoading={detailBundleStatus === 'loading'}
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      onOpenItem={openWorkItemById}
                      onOpenSubtask={handleOpenSubtask}
                      onSubtasksCountChange={handleSubtasksCountChange}
                    />
                  )}

                  {/* Equipment & Safety Requirements — for appointments and jobs */}
                  {(workItem.item_type === 'appointment' || workItem.item_type === 'job') &&
                    workItem.custom_fields &&
                    (workItem.custom_fields.equipment_required || workItem.custom_fields.safety_requirements) && (
                      <div className={`pt-6 border-t mt-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          🛠️ Equipment & Safety
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {workItem.custom_fields.equipment_required && (
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                              <dt className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Equipment Required
                              </dt>
                              <dd className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {workItem.custom_fields.equipment_required}
                              </dd>
                            </div>
                          )}
                          {workItem.custom_fields.safety_requirements && (
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                              <dt className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                PPE / Safety Requirements
                              </dt>
                              <dd className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {workItem.custom_fields.safety_requirements}
                              </dd>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Custom Fields (if template exists) */}
                  {currentTemplate && currentTemplate.field_definitions && workItem.custom_fields && Object.keys(workItem.custom_fields).length > 0 && (
                    <div className={`pt-6 border-t mt-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        <span>{currentTemplate.icon}</span>
                        <span>{currentTemplate.template_name} Details</span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentTemplate.field_definitions.map(field => {
                          const value = workItem.custom_fields?.[field.key];
                          if (value === null || value === undefined || value === '') return null;

                          return (
                            <div key={field.key} className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                              <dt className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {field.label}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </dt>
                              <dd className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {field.type === 'boolean' ? (
                                  <span className={value ? 'text-green-600' : 'text-gray-500'}>
                                    {value ? '✓ Yes' : '✗ No'}
                                  </span>
                                ) : field.type === 'multi-select' && Array.isArray(value) ? (
                                  <div className="flex flex-wrap gap-1">
                                    {value.map((item, idx) => (
                                      <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                        {item}
                                      </span>
                                    ))}
                                  </div>
                                ) : field.type === 'number' && (field.prefix || field.suffix) ? (
                                  <span>
                                    {field.prefix}{value}{field.suffix}
                                  </span>
                                ) : (
                                  <span>{String(value)}</span>
                                )}
                              </dd>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Activity Tab - for generated recurring jobs (schedule IDs don't exist in work_items) */}
            {isGeneratedRecurringJob && (
              <div className={activeTab === 'activity' ? 'block' : 'hidden'}>
                <ActivityTimeline
                  workItemId={workItem.id}
                  isActive={activeTab === 'activity'}
                  includeRelated={includeRelatedOperationalData}
                  groupId={null}
                  onActivityCountChange={setActivityCount}
                />
              </div>
            )}

            {/* Invocation History Tab - generation history for recurring job schedules */}
            {isRecurringJob && (
              <div className={activeTab === 'invocation_history' ? 'block' : 'hidden'}>
                <RecurringJobInvocationHistoryTab
                  workItem={workItem}
                  isDark={isDark}
                  isActive={activeTab === 'invocation_history'}
                />
              </div>
            )}

            {!isRecurringJob && (
              <>
                {/* ── Team Member Tabs (virtual work item) ── */}
                {isTeamMember && (
                  <>
                    <div className={activeTab === 'overview' || activeTab === 'team_overview' ? 'block' : 'hidden'}>
                      <TeamMemberOverviewTab
                        workItem={currentWorkItem}
                        isDark={isDark}
                        refreshKey={teamMemberProfileRefreshKey}
                        isActive={activeTab === 'overview' || activeTab === 'team_overview'}
                      />
                    </div>
                    {typeof teamMemberUserId === 'number' && (
                      <>
                        <div className={activeTab === 'team_schedule' ? 'block' : 'hidden'}>
                          <React.Suspense fallback={renderBrandedTabLoader('Loading team schedule...', '#3b82f6')}>
                            <TeamMemberScheduleTab
                              userId={teamMemberUserId}
                              userName={currentWorkItem.title || detailModalTitle || 'Team member'}
                              isActive={activeTab === 'team_schedule'}
                              isDark={isDark}
                              onOpenItem={openWorkItemById}
                            />
                          </React.Suspense>
                        </div>
                        <div className={activeTab === 'team_capacity' ? 'block' : 'hidden'}>
                          <React.Suspense fallback={renderBrandedTabLoader('Loading capacity and budget...', '#f59e0b')}>
                            <TeamMemberCapacityBudgetTab
                              userId={teamMemberUserId}
                              isActive={activeTab === 'team_capacity'}
                              isDark={isDark}
                              onOpenItem={openWorkItemById}
                            />
                          </React.Suspense>
                        </div>
                        <div className={activeTab === 'team_performance' ? 'block' : 'hidden'}>
                          <React.Suspense fallback={renderBrandedTabLoader('Loading performance...', '#10b981')}>
                            <TeamMemberPerformanceTab
                              userId={teamMemberUserId}
                              isActive={activeTab === 'team_performance'}
                              isDark={isDark}
                            />
                          </React.Suspense>
                        </div>
                        <div className={activeTab === 'team_licenses' ? 'block' : 'hidden'}>
                          <TeamMemberLicensesTab
                            userId={teamMemberUserId}
                            employeeId={teamMemberEmployeeId}
                            isActive={activeTab === 'team_licenses'}
                            isDark={isDark}
                            initialIsDriver={Boolean(getWorkItemCustomFields(currentWorkItem).is_driver)}
                            initialIsNonDriver={Boolean((getWorkItemCustomFields(currentWorkItem).current_operations as UnknownRecord | undefined)?.is_non_driver)}
                            initialDriverLicenseNumber={toDisplayString(getWorkItemCustomFields(currentWorkItem).drivers_license_number, '') || null}
                            initialDriverLicenseExpiry={toDisplayString(getWorkItemCustomFields(currentWorkItem).drivers_license_expiry, '') || null}
                            onProfileRefresh={async (patch) => {
                              // Merge the just-persisted driver flags into the displayed
                              // work item immediately so DetailsSidebar (vehicle vs
                              // "Non Driver") and the Overview badge reflect the change
                              // without waiting on the authoritative profile refetch.
                              if (patch && currentWorkItem) {
                                const baseFields = getWorkItemCustomFields(currentWorkItem);
                                const baseOperations = baseFields.current_operations
                                  && typeof baseFields.current_operations === 'object'
                                  && !Array.isArray(baseFields.current_operations)
                                  ? baseFields.current_operations as UnknownRecord
                                  : {};
                                const nextCustomFields = {
                                  ...baseFields,
                                  ...(patch.is_driver !== undefined ? { is_driver: patch.is_driver } : {}),
                                  current_operations: {
                                    ...baseOperations,
                                    ...(patch.is_non_driver !== undefined ? { is_non_driver: patch.is_non_driver } : {}),
                                  },
                                };
                                applyPersistedWorkItemUpdates({ custom_fields: nextCustomFields });
                                setFormData((previous) => ({
                                  ...previous,
                                  ...(nextCustomFields as Partial<WorkItemFormData>),
                                  custom_fields: nextCustomFields,
                                }));
                              }
                              setTeamMemberProfileRefreshKey((current) => current + 1);
                            }}
                          />
                        </div>
                        <div className={activeTab === 'team_vehicle_inspections' ? 'block' : 'hidden'}>
                          <React.Suspense fallback={renderBrandedTabLoader('Loading vehicle inspections...', '#10b981')}>
                            <TeamMemberVehicleInspectionsTab
                              userId={teamMemberUserId}
                              userName={teamMemberDisplayName || currentWorkItem.title || 'Team member'}
                              isActive={activeTab === 'team_vehicle_inspections'}
                              isDark={isDark}
                              currentOperations={workItemCustomFields.current_operations}
                            />
                          </React.Suspense>
                        </div>
                        <div className={activeTab === 'team_reconciliation' ? 'block' : 'hidden'}>
                          <React.Suspense fallback={renderBrandedTabLoader('Loading time vs vehicle reconciliation...', '#8b5cf6')}>
                            <TeamMemberReconciliationTab
                              userId={teamMemberUserId}
                              userName={teamMemberDisplayName || currentWorkItem.title || 'Team member'}
                              isActive={activeTab === 'team_reconciliation'}
                              isDark={isDark}
                            />
                          </React.Suspense>
                        </div>
                        <div className={activeTab === 'team_mobile_warehouse' ? 'block' : 'hidden'}>
                          <React.Suspense fallback={renderBrandedTabLoader('Loading van stock...', '#3b82f6')}>
                            <TeamMemberMobileWarehouseTab
                              userId={teamMemberUserId}
                              userName={teamMemberDisplayName || currentWorkItem.title || 'Team member'}
                              isActive={activeTab === 'team_mobile_warehouse'}
                              isDark={isDark}
                              onOpenItem={openWorkItemById}
                            />
                          </React.Suspense>
                        </div>
                        {hasTeamSmartTaskAccess && (
                          <div className={activeTab === 'team_smart_task' ? 'block' : 'hidden'}>
                            <TeamMemberSmartTaskTab
                              workItem={currentWorkItem}
                              userId={teamMemberUserId}
                              isDark={isDark}
                              refreshKey={teamMemberProfileRefreshKey}
                              isActive={activeTab === 'team_smart_task'}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Stakeholder People Tab */}
                {isStakeholderEntity && loadedTabs.has('stakeholder_people') && (
                  <div className={activeTab === 'stakeholder_people' ? 'block' : 'hidden'}>
                    <StakeholderPeopleTab
                      workItem={workItem}
                      isDark={isDark}
                      onRefresh={onRefresh}
                    />
                  </div>
                )}

                {/* Stakeholder Locations Tab */}
                {isStakeholderEntity && loadedTabs.has('stakeholder_locations') && (
                  <div className={activeTab === 'stakeholder_locations' ? 'block' : 'hidden'}>
                    <StakeholderLocationsTab
                      workItem={workItem}
                      isDark={isDark}
                      onRefresh={onRefresh}
                    />
                  </div>
                )}

                {/* Stakeholder Documents Tab */}
                {isStakeholderEntity && loadedTabs.has('stakeholder_documents') && (
                  <div className={activeTab === 'stakeholder_documents' ? 'block' : 'hidden'}>
                    <StakeholderDocumentsTab
                      workItem={workItem}
                      isDark={isDark}
                      onRefresh={onRefresh}
                    />
                  </div>
                )}

                {/* Voting session tabs — agenda/participants/quorum/results host
                    the bespoke voting editors for item_type='voting' */}
                {isVotingWorkItem && loadedTabs.has('voting_agenda') && (
                  <div className={activeTab === 'voting_agenda' ? 'block' : 'hidden'}>
                    <VotingAgendaTab workItem={workItem} isDark={isDark} />
                  </div>
                )}
                {isVotingWorkItem && loadedTabs.has('voting_participants') && (
                  <div className={activeTab === 'voting_participants' ? 'block' : 'hidden'}>
                    <VotingParticipantsTab workItem={workItem} isDark={isDark} />
                  </div>
                )}
                {isVotingWorkItem && loadedTabs.has('voting_quorum') && (
                  <div className={activeTab === 'voting_quorum' ? 'block' : 'hidden'}>
                    <VotingQuorumTab workItem={workItem} isDark={isDark} />
                  </div>
                )}
                {isVotingWorkItem && loadedTabs.has('voting_results') && (
                  <div className={activeTab === 'voting_results' ? 'block' : 'hidden'}>
                    <VotingResultsTab workItem={workItem} isDark={isDark} />
                  </div>
                )}

                {/* Stakeholder Hierarchy Tab */}
                {isStakeholderEntity && loadedTabs.has('stakeholder_hierarchy') && (
                  <div className={activeTab === 'stakeholder_hierarchy' ? 'block' : 'hidden'}>
                    <React.Suspense fallback={renderBrandedTabLoader('Loading hierarchy...', '#6366f1')}>
                      <StakeholderHierarchyTab
                        workItem={workItem}
                        isDark={isDark}
                        onRefresh={onRefresh}
                      />
                    </React.Suspense>
                  </div>
                )}

                {/* Stakeholder Time Tracking Tab */}
                {isStakeholderEntity && loadedTabs.has('stakeholder_time_tracking') && (
                  <div className={activeTab === 'stakeholder_time_tracking' ? 'block' : 'hidden'}>
                    <StakeholderTimeTrackingTab
                      workItem={workItem}
                      isDark={isDark}
                      onRefresh={onRefresh}
                    />
                  </div>
                )}

                {/* Stakeholder Assets Tab */}
                {isStakeholderEntity && loadedTabs.has('stakeholder_assets') && (
                  <div className={activeTab === 'stakeholder_assets' ? 'block' : 'hidden'}>
                    <StakeholderAssetsTab
                      workItem={workItem}
                      isDark={isDark}
                      onRefresh={onRefresh}
                    />
                  </div>
                )}

                {/* Email Context Tab (conditional - only for email_task items) */}
                {workItem.item_type === 'email_task' && loadedTabs.has('email') && (
                  <div className={activeTab === 'email' ? 'block' : 'hidden'}>
                    <EmailContextTab
                      workItemId={workItem.id}
                      isActive={activeTab === 'email'}
                      isDark={isDark}
                      onReply={onReply ? (data) => {
                        onReply({ ...data, emailTaskId: workItem.id });
                      } : undefined}
                    />
                  </div>
                )}

                {/* Time Series Tab (conditional - default for sensors, or admin-configured) */}
                {(showTimeSeriesTab || configuredTabs?.includes('timeseries')) && loadedTabs.has('timeseries') && (
                  <div className={activeTab === 'timeseries' ? 'block' : 'hidden'}>
                    <TimeSeriesTab workItemId={workItem.id} isActive={activeTab === 'timeseries'} />
                  </div>
                )}

                {/* Unified Billing Tab - timers, billable lines, inventory movement, expenses, maintenance */}
                {showOperationalBillingTab && loadedTabs.has('billing') && (
                  <div className={activeTab === 'billing' ? 'block' : 'hidden'}>
                    {showAppointmentBillingWorkspace ? (
                      <AppointmentBillingTab
                        workItemId={workItem.id}
                        workItem={billingWorkspaceItem}
                        isActive={activeTab === 'billing'}
                        readOnly={billingWorkspaceReadOnly}
                        timerEditAccess={canSignoffBillingTimers && canEditTimersAction}
                        defaultSection={activeAppointmentBillingSection}
                        onExpensesChange={handleExpensesCountChange}
                        onWorkItemPatched={mergeFormDataUpdates}
                      />
                    ) : (
                      <>
                        {/* Billing sub-tab flat strip navigation */}
                        {(() => {
                          const isJobType = workItem.item_type === 'job';
                          const billingSectionTabs = isJobType
                            ? (
                              canViewJobBillingWorkspace
                                ? [
                                    { id: 'time' as BillingSectionId, label: 'Timers', icon: Clock },
                                    { id: 'costing' as BillingSectionId, label: 'Dashboard', icon: Activity },
                                    { id: 'submissions' as BillingSectionId, label: 'Submissions', icon: FileText },
                                  ]
                                : [
                                    { id: 'time' as BillingSectionId, label: 'Timers', icon: Clock },
                                  ]
                            )
                            : [
                              { id: 'costing' as BillingSectionId, label: 'Dashboard', icon: DollarSign },
                              { id: 'time' as BillingSectionId, label: 'Timers', icon: Clock },
                              { id: 'submissions' as BillingSectionId, label: 'Submissions', icon: FileText },
                            ];
                          return (
                            <div
                              className={`flex gap-1 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                              data-tour-id="work-item-billing-section-nav"
                            >
                              {billingSectionTabs.map((section) => {
                                const SectionIcon = section.icon;
                                const isActiveSection = activeBillingSection === section.id;
                                return (
                                  <button
                                    key={section.id}
                                    {...viewModeNavigationProps}
                                    onClick={() => setActiveBillingSection(section.id)}
                                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${isActiveSection
                                      ? 'border-orange-500 text-orange-500'
                                      : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
                                      }`}
                                  >
                                    <SectionIcon className="w-3.5 h-3.5" />
                                    {section.label}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                        <BillingDashboard
                          workItemId={workItem.id}
                          workItemType={workItem.item_type}
                          workItem={billingWorkspaceItem}
                          isActive={activeTab === 'billing'}
                          includeRelated={includeRelatedOperationalData}
                          readOnly={billingWorkspaceReadOnly}
                          onExpensesChange={handleExpensesCountChange}
                          includeDescendants={workItem.item_type === 'job' || workItem.item_type === 'project' || workItem.item_type === 'task'}
                          defaultSection={activeBillingSection}
                          title="Billing"
                          embedded
                          showHeaderControls={false}
                          jobBillingWorkspaceAccess={canViewJobBillingWorkspace}
                          operationsJobGenerateInvoiceAccess={canGenerateJobInvoiceFromOperationsHeader}
                          timersSignoffAccess={canSignoffBillingTimers && canEditTimersAction}
                          billingFinalizeAccess={canFinalizeBilling}
                          billingStrictness={resolvedBillingStrictness}
                          canSubmitForBilling={canSubmitForBilling}
                          canReviewBilling={canReviewBilling}
                          canApproveBilling={canApproveBilling}
                          pendingDocType={pendingDocType}
                          onTimerAggregationModeChange={handleTimerAggregationModeChange}
                          onUpdateWorkItemStatus={handleWorkflowStatusChange}
                          onOpenDocumentModal={handleOpenGeneratedBillingDocument}
                          ledgerGenerated={ledgerGenerated}
                          onLedgerGenerated={() => {
                            setLedgerGenerated(true);
                            setActiveBillingSection('costing');
                          }}
                          onCreateExpense={(prefill) => {
                            // Open ExpenseManagement in a new tab or window with create mode
                            const url = new URL(window.location.href);
                            url.searchParams.set('create_expense', '1');
                            if (prefill?.job_id) url.searchParams.set('job_id', prefill.job_id);
                            window.open(url.toString(), '_blank');
                          }}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Billing / Inventory Tab (conditional - default for products, or admin-configured) */}
                {showInventoryTab && loadedTabs.has('inventory') && (
                  <div className={activeTab === 'inventory' ? 'block' : 'hidden'}>
                    <BillingDashboard
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      workItem={billingWorkspaceItem}
                      isActive={activeTab === 'inventory'}
                      includeRelated={includeRelatedOperationalData}
                      readOnly={billingWorkspaceReadOnly}
                      embedded
                      showHeaderControls={false}
                      onExpensesChange={handleExpensesCountChange}
                      operationsJobGenerateInvoiceAccess={canGenerateJobInvoiceFromOperationsHeader}
                      onTimerAggregationModeChange={handleTimerAggregationModeChange}
                    />
                  </div>
                )}

                {/* Product Images Tab (conditional - default for products/bundles/kits, or admin-configured) */}
                {(showProductImagesTab || configuredTabs?.includes('productImages')) && loadedTabs.has('productImages') && (
                  <div className={activeTab === 'productImages' ? 'block' : 'hidden'}>
                    <ProductImagesTab workItemId={workItem.id} isActive={activeTab === 'productImages'} />
                  </div>
                )}

                {/* Pipeline Tab (conditional - default for opportunities, or admin-configured) */}
                {(showPipelineTab || configuredTabs?.includes('pipeline')) && loadedTabs.has('pipeline') && (
                  <div className={activeTab === 'pipeline' ? 'block' : 'hidden'}>
                    <OpportunityPipelineTab workItemId={workItem.id} isActive={activeTab === 'pipeline'} />
                  </div>
                )}

                {/* Maintenance Tab (conditional - default for assets, or admin-configured) */}
                {showMaintenanceTab && !showOperationalBillingTab && loadedTabs.has('maintenance') && (
                  <div className={activeTab === 'maintenance' ? 'block' : 'hidden'}>
                    {renderedMaintenanceContent || (
                      <AssetMaintenanceTab workItemId={workItem.id} isActive={activeTab === 'maintenance'} />
                    )}
                  </div>
                )}

                {/* Valuation Tab (conditional - finance-facing depreciation for assets) */}
                {showValuationTab && loadedTabs.has('valuation') && (
                  <div className={activeTab === 'valuation' ? 'block' : 'hidden'}>
                    <AssetValuationTab
                      workItemId={workItem.id}
                      customFields={mergedCustomFieldsForTabs}
                      onCustomFieldChange={handleCustomFieldChange}
                      isSaving={isSaving}
                    />
                  </div>
                )}

                {/* Recipe BOM Tab (conditional - default for recipes, or admin-configured) */}
                {(showRecipeTab || configuredTabs?.includes('recipe')) && loadedTabs.has('recipe') && (
                  <div className={activeTab === 'recipe' ? 'block' : 'hidden'}>
                    <RecipeBOMTab workItemId={workItem.id} isActive={activeTab === 'recipe'} />
                  </div>
                )}

                {/* Field Service Tabs (conditional - for field service work items) */}
                {showFieldServiceTabs && (
                  <>
                    {/* Chat History moved here - GPS check-ins now in Time Tracking tab */}
                  </>
                )}

                {!isTeamMember && showSupportingDocumentsTab && (
                  <div className={activeTab === 'supporting_documents' ? 'block' : 'hidden'}>
                    <div className="space-y-5">
                      {supportingDocumentsSections.length > 1 && (
                        <div className={`flex gap-1 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          {supportingDocumentsSections.map((section) => {
                            const SectionIcon = section.icon;
                            const isActiveSection = activeSupportingDocumentsSection === section.id;

                            return (
                              <a
                                key={section.id}
                                {...viewModeNavigationProps}
                                href="#"
                                role="button"
                                onClick={(event) => {
                                  event.preventDefault();
                                  setActiveSupportingDocumentsSection(section.id);
                                }}
                                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${isActiveSection
                                  ? 'border-orange-500 text-orange-500'
                                  : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
                                  }`}
                              >
                                <SectionIcon className="w-3.5 h-3.5" />
                                {section.label}
                                {section.count !== undefined && section.count > 0 && (
                                  <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                    {section.count}
                                  </span>
                                )}
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {showSupportingDocumentComments && (
                        <div className={activeSupportingDocumentsSection === 'comments' ? 'block' : 'hidden'}>
                          <CommentsSection
                            workItemId={workItem.id}
                            isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'comments'}
                            includeRelated={includeRelatedOperationalData}
                            onCommentsChange={setCommentCount}
                            readOnly={supportingDocumentsCommentsReadOnly}
                            readOnlyReason={supportingDocumentsCommentsReadOnlyReason}
                            allowCommentModeration={canEditCommentsAction}
                            enableExternalCommentType
                            operationsFeatureSettings={operationsFeatureSettings}
                            appointmentWorkItem={appointmentWorkItemForOverrides}
                            appointmentTypeOptions={appointmentTypeOptions}
                            onAppointmentRequirementOverrideChange={handleAppointmentRequirementOverrideChange}
                          />
                        </div>
                      )}

                      {showSupportingDocumentAttachments && (
                        <div className={activeSupportingDocumentsSection === 'attachments' ? 'block' : 'hidden'}>
                          {(itemType === 'bug' || itemType === 'feature') ? (
                            <DocViewerOverviewTab
                              workItemId={workItem.id}
                              isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'attachments'}
                              isDark={isDark}
                            />
                          ) : (
                            <AttachmentsTab
                              workItemId={workItem.id}
                              isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'attachments'}
                              showFieldServiceTabs={showFieldServiceTabs}
                              workItem={workItem}
                            />
                          )}
                        </div>
                      )}

                      {showSupportingDocumentPhotos && (
                        <div className={activeSupportingDocumentsSection === 'photos' ? 'block' : 'hidden'}>
                          {isFinancialDocument ? (
                            <SupportingPhotosTab
                              workItemId={workItem.id}
                              isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'photos'}
                            />
                          ) : (
                            <FieldServicePhotosTab
                              workItemId={workItem.id}
                              isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'photos'}
                              includeRelated={includeRelatedOperationalData}
                              allowPhotoEdits={canEditFieldServicePhotos}
                              allowPhotoDetailEdits={canEditPhotosAction}
                              currentUserId={currentUserId}
                              photosLocked={isViewMode || isClosedCommentWorkItemStatus(workItem?.status, workItem?.item_type)}
                              appointmentWorkItem={appointmentWorkItemForOverrides}
                              appointmentTypeOptions={appointmentTypeOptions}
                              onAppointmentRequirementOverrideChange={handleAppointmentRequirementOverrideChange}
                            />
                          )}
                        </div>
                      )}

                      {showSupportingDocumentSignatures && (
                        <div className={activeSupportingDocumentsSection === 'signatures' ? 'block' : 'hidden'}>
                          <FieldServiceSignatureTab
                            key={`signature-tab-${workItem.id}-${includeRelatedOperationalData ? 'related' : 'direct'}`}
                            workItemId={workItem.id}
                            isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'signatures'}
                            includeRelated={includeRelatedOperationalData}
                            contactId={workItem.contact_id || null}
                            appointmentWorkItem={appointmentWorkItemForOverrides}
                            appointmentTypeOptions={appointmentTypeOptions}
                            onAppointmentRequirementOverrideChange={handleAppointmentRequirementOverrideChange}
                          />
                        </div>
                      )}

                      {showSupportingDocumentQuotationRequests && (
                        <div className={activeSupportingDocumentsSection === 'quotation_requests' ? 'block' : 'hidden'}>
                          <QuotationRequestsTab
                            workItemId={workItem.id}
                            itemType={workItem.item_type === 'appointment' ? 'appointment' : 'job'}
                            isActive={activeTab === 'supporting_documents' && activeSupportingDocumentsSection === 'quotation_requests'}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!isTeamMember && showStandaloneVehicleAttachmentsTab && (
                  <div className={activeTab === 'attachments' ? 'block' : 'hidden'}>
                    <AttachmentsTab
                      workItemId={workItem.id}
                      isActive={activeTab === 'attachments'}
                      showFieldServiceTabs={showFieldServiceTabs}
                      workItem={workItem}
                    />
                  </div>
                )}

                {/* Workspace item mode: standalone Comments tab */}
                {showStandaloneCommentsTab && (
                  <div className={activeTab === 'comments' ? 'block' : 'hidden'}>
                    <CommentsSection
                      workItemId={workItem.id}
                      isActive={activeTab === 'comments'}
                      onCommentsChange={setCommentCount}
                      readOnly={supportingDocumentsCommentsReadOnly}
                      readOnlyReason={supportingDocumentsCommentsReadOnlyReason}
                      allowCommentModeration={canEditCommentsAction}
                    />
                  </div>
                )}

                {/* Follow-ups get a top-level Watchers tab (not admin-only like the History sub-tab) */}
                {isFollowUp && (
                  <div className={activeTab === 'watchers' ? 'block' : 'hidden'}>
                    <WatchersTab workItemId={workItem.id} currentUserId={currentUserId} isActive={activeTab === 'watchers'} />
                  </div>
                )}

                {/* Workspace item mode: standalone Attachments tab */}
                {showStandaloneAttachmentsTab && (
                  <div className={activeTab === 'attachments' ? 'block' : 'hidden'}>
                    <AttachmentsTab
                      workItemId={workItem.id}
                      isActive={activeTab === 'attachments'}
                      workItem={workItem}
                    />
                  </div>
                )}

                {/* Work Breakdown Tab - forms only */}
                {!isTeamMember && loadedTabs.has('work_breakdown') && (
                  <div className={activeTab === 'work_breakdown' ? 'block' : 'hidden'}>
                    <WorkBreakdownTab
                      workItemId={workItem.id}
                      workItemType={workItem.item_type}
                      workItemSubtype={workItem.item_subtype}
                      isActive={activeTab === 'work_breakdown'}
                      includeRelated={false}
                      userRole={effectiveOperationalUserRole}
                      readOnly={isViewMode}
                      formsReadOnly={workBreakdownFormsReadOnly}
                      workItemStatus={workItem.status}
                      onCountsChange={handleWorkBreakdownCountsChange}
                      enableAiAssistant
                    />
                  </div>
                )}

                {/* Appointments/Visits Tab — jobs and property visit parents */}
                {(workItem.item_type === 'job' || isPropertyVisitParent) && loadedTabs.has('appointments') && (
                  <div className={activeTab === 'appointments' ? 'block' : 'hidden'}>
                    <AppointmentsTab
                      key={`appointments-${workItem.id}`}
                      workItemId={workItem.id}
                      workItem={{ ...workItem, ...formData } as WorkItem}
                      isActive={activeTab === 'appointments'}
                      onUpdate={onRefresh}
                      onParentWorkItemUpdate={handleAppointmentsTabParentWorkItemUpdate}
                      userRole={effectiveOperationalUserRole}
                      canCreateAppointment={canCreateAppointmentAction}
                      initialAppointments={appointmentsTabInitialAppointments}
                    />
                  </div>
                )}

                {/* Deliveries Tab (conditional - only for jobs in logistics/waste contexts) */}
                {workItem.item_type === 'job' && loadedTabs.has('deliveries') && (
                  <div className={activeTab === 'deliveries' ? 'block' : 'hidden'}>
                    <DeliveriesTab
                      key={`deliveries-${workItem.id}`}
                      workItemId={workItem.id}
                      workItem={workItem}
                      isActive={activeTab === 'deliveries'}
                      onUpdate={onRefresh}
                      onParentWorkItemUpdate={handleAppointmentsTabParentWorkItemUpdate}
                      userRole={effectiveOperationalUserRole}
                      activeSolutionKey={activeOperationsSolutionKeys.length > 0 ? activeOperationsSolutionKeys[0] : null}
                    />
                  </div>
                )}

                {/* Proposal Tab (conditional - only for jobs) */}
                {workItem.item_type === 'job' && canAccessAiProposalManager && loadedTabs.has('proposal') && (
                  <div className={activeTab === 'proposal' ? 'block' : 'hidden'}>
                    <ProposalTab
                      workItemId={String(workItem.id)}
                      workItemNumber={workItem.item_number ?? null}
                      contactId={toNullableNumber(workItem.contact_id)}
                      contactName={workItem.contact_name ?? null}
                      isDark={isDark}
                    />
                  </div>
                )}

                {/* Jobs Tab (conditional - only for projects) */}
                {workItem.item_type === 'project' && loadedTabs.has('jobs') && (
                  <div className={activeTab === 'jobs' ? 'block' : 'hidden'}>
                    <JobsTab
                      key={`jobs-${workItem.id}`}
                      workItemId={workItem.id}
                      workItem={workItem}
                      isActive={activeTab === 'jobs'}
                      onUpdate={onRefresh}
                    />
                  </div>
                )}

                {/* Project Plan Tab (conditional - only for projects) — Gantt Chart is a sub-tab here */}
                {workItem.item_type === 'project' && loadedTabs.has('project_plan') && (
                  <div className={activeTab === 'project_plan' ? 'block' : 'hidden'}>
                    {/* Sub-tab nav: Project Plan / Gantt Chart */}
                    <div className={`grid grid-cols-2 gap-0.5 p-0.5 rounded-xl mx-3 sm:mx-4 mt-3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      {([
                        { id: 'plan' as const, label: 'Project Plan', icon: Target },
                        { id: 'gantt' as const, label: 'Gantt Chart', icon: GanttChart },
                      ] as const).map(sub => {
                        const SubIcon = sub.icon;
                        const isActive = activeProjectPlanSubTab === sub.id;
                        return (
                          <button
                            key={sub.id}
                            {...viewModeNavigationProps}
                            onClick={() => setActiveProjectPlanSubTab(sub.id)}
                            className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${isActive
                              ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                              }`}
                          >
                            <SubIcon className="w-3.5 h-3.5" />
                            {sub.label}
                          </button>
                        );
                      })}
                    </div>
                    {activeProjectPlanSubTab === 'plan' && (
                      <ProjectPlanTab
                        key={`project-plan-${workItem.id}`}
                        workItemId={workItem.id}
                        projectTitle={workItem.title}
                        isActive={activeTab === 'project_plan' && activeProjectPlanSubTab === 'plan'}
                        onUpdate={onRefresh}
                        onOpenItem={openWorkItemById}
                      />
                    )}
                    {activeProjectPlanSubTab === 'gantt' && (
                      <React.Suspense fallback={renderBrandedTabLoader('Loading Gantt chart...', '#0ea5e9')}>
                        <ProjectGanttTab
                          key={`gantt-${workItem.id}`}
                          workItemId={workItem.id}
                          projectTitle={workItem.title}
                          isActive={activeTab === 'project_plan' && activeProjectPlanSubTab === 'gantt'}
                          onUpdate={onRefresh}
                          onOpenItem={openWorkItemById}
                        />
                      </React.Suspense>
                    )}
                  </div>
                )}

                {/* Gantt Chart now lives as sub-tab inside Project Plan — kept as stub to preserve loadedTabs */}
                {workItem.item_type === 'project' && loadedTabs.has('gantt') && (
                  <div className="hidden" aria-hidden="true" />
                )}

                {/* Reference Drawings Tab (conditional - for jobs and tasks) */}
                {showReferenceDocsTab && loadedTabs.has('drawings') && (
                  <div className={activeTab === 'drawings' ? 'block' : 'hidden'}>
                    <WorkItemDrawingsTab
                      key={`drawings-${workItem.id}`}
                      workItemId={workItem.id}
                      workItem={workItem}
                      isActive={activeTab === 'drawings'}
                      onDrawingsChange={handleReferenceDrawingsCountChange}
                    />
                  </div>
                )}

                {/* Expenses Tab (conditional - for jobs, tasks, appointments, and projects) */}
                {!showOperationalBillingTab && loadedTabs.has('expenses') ? (
                  <div className={activeTab === 'expenses' ? 'block' : 'hidden'}>
                    {renderedExpensesContent || (
                      workItem.item_type === 'project' ? (
                        <ProjectExpensesTab
                          key={`expenses-project-${workItem.id}`}
                          workItem={workItem}
                        />
                      ) : (workItem.item_type === 'job' || workItem.item_type === 'task' || workItem.item_type === 'appointment') ? (
                        <JobExpensesTab
                          key={`expenses-${workItem.id}`}
                          workItemId={workItem.id}
                          workItem={workItem}
                          isActive={activeTab === 'expenses'}
                          readOnly={billingWorkspaceReadOnly}
                          onExpensesChange={handleExpensesCountChange}
                        />
                      ) : null
                    )}
                  </div>
                ) : null}

                {renderedInspectionContent && (
                  <div className={activeTab === 'inspection' ? 'block' : 'hidden'}>
                    {renderedInspectionContent}
                  </div>
                )}

                {renderedLicenseContent && (
                  <div className={activeTab === 'license' ? 'block' : 'hidden'}>
                    {renderedLicenseContent}
                  </div>
                )}

                {renderedHistoryContent && (
                  <div className={activeTab === 'history' ? 'block' : 'hidden'}>
                    {renderedHistoryContent}
                  </div>
                )}

                {renderedTrackingSecurityContent && (
                  <div className={activeTab === 'tracking_security' ? 'block' : 'hidden'}>
                    {renderedTrackingSecurityContent}
                  </div>
                )}

                {renderedDashboardContent && (
                  <div className={activeTab === 'dashboard' ? 'block' : 'hidden'}>
                    {renderedDashboardContent}
                  </div>
                )}

                {/* Budget Tab (conditional - projects only) */}
                {workItem.item_type === 'project' && loadedTabs.has('budget') && (
                  <div className={activeTab === 'budget' ? 'block' : 'hidden'}>
                    <ProjectBudgetTab
                      workItem={workItem}
                      onUpdate={handleFieldChange}
                    />
                  </div>
                )}

                {/* Team Performance Tab (conditional - projects only) */}
                {workItem.item_type === 'project' && loadedTabs.has('project_team') && (
                  <div className={activeTab === 'project_team' ? 'block' : 'hidden'}>
                    <ProjectTeamPerformanceTab
                      workItem={workItem}
                      onOpenItem={openWorkItemById}
                    />
                  </div>
                )}

                {/* History Tab - groups Activity, Watchers, AI Review, Process Checklist, and Chat History */}
                {showHistoryTab && loadedTabs.has('history') && (() => {
                  return (
                    <div className={activeTab === 'history' ? 'block' : 'hidden'}>
                      {/* Sub-tab flat strip navigation */}
                      {historySubTabs.length > 1 && (
                        <div className={`flex gap-1 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          {historySubTabs.map((subTab) => {
                            const Icon = subTab.icon;
                            const isActive = activeHistorySubTab === subTab.id;
                            return (
                              <button
                                key={subTab.id}
                                {...viewModeNavigationProps}
                                onClick={() => setActiveHistorySubTab(subTab.id)}
                                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${isActive
                                  ? 'border-orange-500 text-orange-500'
                                  : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
                                  }`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                                {subTab.label}
                                {subTab.count !== undefined && subTab.count > 0 && (
                                  <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                    {subTab.count}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="pt-2">
                        {activeHistorySubTab === 'activity' && ((isTaskOrJob || isFinancialDocument || isStakeholderEntity || isVehicleAsset) && canViewHistoryAdmin) && (
                          <ActivityTimeline
                            workItemId={workItem.id}
                            isActive={activeTab === 'history' && activeHistorySubTab === 'activity'}
                            includeRelated={includeRelatedOperationalData}
                            groupId={null}
                            onActivityCountChange={setActivityCount}
                          />
                        )}
                        {activeHistorySubTab === 'watchers' && isTaskOrJob && itemType !== 'appointment' && (canViewHistoryAdmin || isFollowUp) && (
                          <WatchersTab workItemId={workItem.id} currentUserId={currentUserId} isActive={activeTab === 'history' && activeHistorySubTab === 'watchers'} />
                        )}
                        {activeHistorySubTab === 'ai_review' && canViewAiReview && (
                          <React.Suspense fallback={renderBrandedTabLoader('Loading AI review...', '#a855f7')}>
                            <AIOperationsReviewTab
                              workItem={workItem}
                              isActive={activeTab === 'history' && activeHistorySubTab === 'ai_review'}
                              currentUserId={currentUserId}
                              userRole={currentUserRole}
                            />
                          </React.Suspense>
                        )}
                        {activeHistorySubTab === 'process_checklist' && canViewProcessChecklist && (
                          <React.Suspense fallback={renderBrandedTabLoader('Loading process checklist...', '#2563eb')}>
                            <ProcessChecklistTab
                              workItem={workItem}
                              isActive={activeTab === 'history' && activeHistorySubTab === 'process_checklist'}
                            />
                          </React.Suspense>
                        )}
                        {activeHistorySubTab === 'chat' && showFieldServiceTabs && itemType !== 'project' && canViewHistoryAdmin && (
                          <JobChatTab
                            workItem={workItem}
                            isActive={activeTab === 'history' && activeHistorySubTab === 'chat'}
                            currentUserId={currentUserId}
                            currentUserName={currentUserName}
                            userRole={currentUserRole}
                            onOpenInMessenger={(threadId) => {
                              console.log('[WorkItemDetail] Open in messenger:', threadId);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })()}

                {!isTeamMember && workItem.item_type === 'project' && !showOperationalBillingTab ? (
                  loadedTabs.has('time') ? (
                    <div className={activeTab === 'time' ? 'block' : 'hidden'}>
                      <ProjectTimeTrackingTab
                        key={`time-project-${workItem.id}`}
                        workItem={workItem}
                      />
                    </div>
                  ) : null
                ) : !isTeamMember && !showOperationalBillingTab && loadedTabs.has('time') ? (
                  <div className={activeTab === 'time' ? 'block' : 'hidden'}>
                    <TimeEntriesTab
                      workItemId={workItem.id}
                      isActive={activeTab === 'time'}
                      userRole={currentUserRole}
                      workItemStatus={workItem.status}
                      itemType={workItem.item_type}
                      parentAllowsManualTimeFromOperations={isOwnerOrRootUser || (canEditTimersForCompletion && canEditTimersAction)}
                    />
                  </div>
                ) : null}

                {!isTeamMember && loadedTabs.has('relationships') && (
                  <div className={activeTab === 'relationships' ? 'block' : 'hidden'}>
                    <RelationshipsTab
                      workItemId={workItem.id}
                      isActive={activeTab === 'relationships'}
                      onOpenItem={(itemId) => {
                        console.log('Open item:', itemId);
                      }}
                    />
                  </div>
                )}

                {/* Document Viewer Tab */}
                {!isTeamMember && loadedTabs.has('document') && (
                  <div className={activeTab === 'document' ? 'block' : 'hidden'}>
                    <DocViewerOverviewTab
                      workItemId={workItem.id}
                      isActive={activeTab === 'document'}
                      isDark={isDark}
                    />
                  </div>
                )}

                {/* Details Tab - Mobile only - Shows DetailsSidebar content */}
                {supportsMobileDetailSidebar && loadedTabs.has('details') && !isSharedDetailShellLoading && (
                  <div className={`lg:hidden ${activeTab === 'details' ? 'block' : 'hidden'}`}>
                    <DetailsSidebar
                      workItem={detailSidebarWorkItem || workItem}
                      onUpdate={async (updates) => {
                        mergeFormDataUpdates(updates);
                        setHasUnsavedChanges(true);
                      }}
                      onPersistedUpdate={(updates) => {
                        clearPendingFieldSave();
                        applyPersistedWorkItemUpdates(updates);
                        mergeFormDataUpdates(updates);
                        setHasUnsavedChanges(false);
                        setLastSavedAt(new Date());
                      }}
                      onAssigneesChange={async (assignees) => {
                        const primaryAssignee = assignees[0];
                        const assignedToName = primaryAssignee
                          ? [primaryAssignee.first_name, primaryAssignee.last_name].filter(Boolean).join(' ').trim() || primaryAssignee.email || null
                          : null;
                        const assigneeUpdates: Partial<WorkItem> = {
                          assignees,
                          assigned_to: primaryAssignee?.user_id ?? null,
                          assigned_to_name: assignedToName,
                        };

                        clearPendingFieldSave();
                        applyPersistedWorkItemUpdates(assigneeUpdates);
                        mergeFormDataUpdates(assigneeUpdates);
                        setHasUnsavedChanges(false);
                        setLastSavedAt(new Date());
                        onRefresh?.();
                      }}
                      onStatusChange={handleSidebarStatusChange}
                      onStatusTransitionStart={handleStatusTransitionStart}
                      onStatusTransitionEnd={handleStatusTransitionEnd}
                      canReopenLockedStatus={(isTerminalWorkItemStatus(effectiveModalStatus, workItem?.item_type) || normalizeWorkflowStatusKey(effectiveModalStatus) === 'admin_reopened') && canReopenLockedStatusFromSidebar}
                      canManageAdminWorkflowOverride={canReopenLockedStatusFromSidebar}
                      canCloseStatus={canCloseStatusFromSidebar}
                      lockedStatusCycleMode={lockedStatusCycleMode}
                      readOnly={isViewMode || isTeamMember || (isStakeholderEntity && !isStakeholderContactEntity)}
                      onOpenProcessChecklist={openProcessChecklist}
                      canConfigureItemType={isOwnerOrRootUser}
                      initialData={detailSidebarInitialData}
                      initialDataLoading={detailBundleStatus === 'loading'}
                      activeSolutionKey={activeSolutionKey}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Details Sidebar - Jira-like right panel - For tasks/jobs and financial documents, not inventory items */}
          {/* Hidden on mobile, shown on lg+ screens. Mobile users can access via tabs */}
          {supportsDesktopDetailSidebar && !isSharedDetailShellLoading && (
            <div className="hidden lg:flex lg:flex-col lg:h-full">
              <DetailsSidebar
                workItem={detailSidebarWorkItem || workItem}
                onUpdate={async (updates) => {
                  // Update form data with changes from sidebar
                  mergeFormDataUpdates(updates);
                  setHasUnsavedChanges(true);
                }}
                onPersistedUpdate={(updates) => {
                  clearPendingFieldSave();
                  applyPersistedWorkItemUpdates(updates);
                  mergeFormDataUpdates(updates);
                  setHasUnsavedChanges(false);
                  setLastSavedAt(new Date());
                }}
                onAssigneesChange={async (assignees) => {
                  const primaryAssignee = assignees[0];
                  const assignedToName = primaryAssignee
                    ? [primaryAssignee.first_name, primaryAssignee.last_name].filter(Boolean).join(' ').trim() || primaryAssignee.email || null
                    : null;
                  const assigneeUpdates: Partial<WorkItem> = {
                    assignees,
                    assigned_to: primaryAssignee?.user_id ?? null,
                    assigned_to_name: assignedToName,
                  };

                  clearPendingFieldSave();
                  // Assignee changes are saved directly via API in DetailsSidebar
                  // Update the saved state indicator to reflect the change
                  applyPersistedWorkItemUpdates(assigneeUpdates);
                  mergeFormDataUpdates(assigneeUpdates);
                  setHasUnsavedChanges(false);
                  setLastSavedAt(new Date());
                  // Optionally refresh parent data
                  onRefresh?.();
                }}
                onStatusChange={handleSidebarStatusChange}
                onStatusTransitionStart={handleStatusTransitionStart}
                onStatusTransitionEnd={handleStatusTransitionEnd}
                canReopenLockedStatus={(isTerminalWorkItemStatus(effectiveModalStatus, workItem?.item_type) || normalizeWorkflowStatusKey(effectiveModalStatus) === 'admin_reopened') && canReopenLockedStatusFromSidebar}
                canManageAdminWorkflowOverride={canReopenLockedStatusFromSidebar}
                canCloseStatus={canCloseStatusFromSidebar}
                lockedStatusCycleMode={lockedStatusCycleMode}
                readOnly={isViewMode}
                onOpenProcessChecklist={openProcessChecklist}
                canConfigureItemType={isOwnerOrRootUser}
                initialData={detailSidebarInitialData}
                initialDataLoading={detailBundleStatus === 'loading'}
                activeSolutionKey={activeSolutionKey}
              />
            </div>
          )}
        </div>

        {/* Footer - Simplified */}
        <div
          className="px-3 sm:px-6 py-1.5 sm:py-2 border-t border-[#1a2d4d] flex items-center justify-between"
          style={{
            backgroundColor: '#111f38',
            paddingBottom: `calc(${MOBILE_SAFE_AREA_BOTTOM} + 0.25rem)`
          }}
        >
          <div className="flex items-center gap-2">
            {/* QuickAddTaskButton only shown for tasks/jobs in edit mode */}
            {!isViewMode && isTaskOrJob && (
              <QuickAddTaskButton
                variant="icon"
                title={`${workItem.item_number}: ${workItem.title}`}
                referenceType="work_item"
                referenceId={workItem.id}
                referenceUrl={`/client-zone/modules/total-operations?item=${workItem.id}`}
                contextData={{
                  item_type: workItem.item_type,
                  item_number: workItem.item_number,
                  current_status: workItem.status,
                  priority: workItem.priority,
                  due_date: workItem.due_date,
                  progress: workItem.progress_percentage
                }}
                priority={workItem.priority === 'urgent' || workItem.priority === 'critical' ? 'urgent' :
                  workItem.priority === 'high' ? 'high' :
                    workItem.priority === 'medium' ? 'medium' : 'low'}
                size="md"
              />
            )}
            {!isViewMode && canDeleteWorkItem && (
              <button
                onClick={handleDelete}
                className="px-2 sm:px-3 py-1.5 text-red-400 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-xs hover:bg-red-900/20"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {/* AI sub-menu — presents AI Assistant and AI Training Coach options.
                The assistant opens the contextual assistant panel (existing behavior).
                The training coach dispatches 'ai-coach-open' (picked up by the
                GuidedTour mounted at the bottom of this modal). */}
            <div ref={aiMenuRef} className="relative">
              <button
                onClick={() => setShowAiMenu((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${showContextualAssistant
                  ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm hover:from-indigo-600 hover:to-purple-700'
                  }`}
                title="AI Assistant"
                aria-expanded={showAiMenu}
                aria-haspopup="menu"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{showContextualAssistant ? 'AI Assistant' : 'AI Assistant'}</span>
                <span className="sm:hidden">AI</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${showAiMenu ? 'rotate-180' : ''}`} />
              </button>
              {showAiMenu && (
                <div
                  role="menu"
                  className="absolute bottom-full right-0 mb-2 w-64 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
                >
                  <button
                    role="menuitem"
                    onClick={() => {
                      setShowAiMenu(false);
                      if (showContextualAssistant) {
                        setShowContextualAssistant(false);
                      } else {
                        openContextualAssistant();
                        // Notify the coach to close (mutual exclusivity).
                        // `local` tells the unified AI surface not to claim
                        // this — the contextual assistant is the surface here.
                        window.dispatchEvent(new CustomEvent('ai-assistant-open', { detail: { local: true } }));
                      }
                    }}
                    className="flex w-full items-start gap-3 rounded-t-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
                      <Brain className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">AI Assistant</span>
                      <span className="block text-[10px] text-slate-500 dark:text-slate-400">Ask questions about this work item</span>
                    </span>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setShowAiMenu(false);
                      setShowContextualAssistant(false);
                      window.dispatchEvent(new CustomEvent('ai-coach-open'));
                    }}
                    className="flex w-full items-start gap-3 rounded-b-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white">
                      <GraduationCap className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">AI Training Coach</span>
                      <span className="block text-[10px] text-slate-500 dark:text-slate-400">Guided, step-by-step tour of this work item</span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {shouldShowContextualAssistant && showContextualAssistant && (
          <AIOperationsAssistant
            floatingPanel
            contextJobId={contextualAssistantJobId}
            contextProjectId={contextualAssistantProjectId}
            contextWorkItem={contextualAssistantWorkItem}
            initialPrompt={contextualAssistantPrompt}
            onClose={closeContextualAssistantPanel}
            showProviderToggle={false}
          />
        )}

        {/* Subtask Detail Drawer — slides in over content */}
        <SubtaskDetailDrawer
          subtaskId={subtaskDrawerId}
          isOpen={isSubtaskDrawerOpen}
          onClose={() => { setIsSubtaskDrawerOpen(false); setSubtaskDrawerId(null); }}
          onSaved={() => {
            // Refresh subtask counts on parent
            onRefresh?.();
          }}
          onOpenFullModal={(id) => {
            setIsSubtaskDrawerOpen(false);
            setSubtaskDrawerId(null);
            openWorkItemById(id);
          }}
          isDark={isDark}
        />

        {/* Process Checklist Overlay */}
        {showProcessChecklist && (
          <>
            <div
              className="absolute inset-0 z-40 bg-black/30"
              onClick={closeProcessChecklist}
            />
            <div className="absolute inset-y-0 right-0 z-50 w-full max-w-md flex flex-col bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-200">
              <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Process Checklist</h3>
                <button onClick={closeProcessChecklist} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <ProcessGatePanel
                  workItemId={workItem.id}
                  workItemType={workItem.item_type}
                  targetStatus={processChecklistTargetStatus}
                  onComplete={() => {
                    const pendingTarget = processChecklistTargetStatus;
                    const shouldResumeStatusChange = processGateResumeAfterCompleteRef.current;
                    processGateResumeAfterCompleteRef.current = true;
                    closeProcessChecklist();
                    if (pendingTarget && shouldResumeStatusChange) {
                      window.dispatchEvent(new CustomEvent('processGateCleared', {
                        detail: { workItemId: String(workItem.id), targetStatus: pendingTarget },
                      }));
                    }
                  }}
                  onConfigureInForms={openProcessChecklistDesigner}
                />
              </div>
            </div>
          </>
        )}

        {/* Print Preview Modal */}
        {showPreview && (
          canOpenStakeholderFinancePreview ? (
            <FinanceReportPreviewModal
              isOpen={showPreview}
              onClose={() => setShowPreview(false)}
              documentType={stakeholderDefaultFinancePreviewOption?.documentType || 'customer_statement'}
              reportOptions={stakeholderFinanceReportOptions}
              initialReportOptionId={stakeholderDefaultFinancePreviewOptionId || undefined}
              reportOptionPreferenceKey={stakeholderFinancePreviewPreferenceKey}
              templatePreferenceNamespace={stakeholderFinanceTemplatePreferenceNamespace}
              resolveReportContext={resolveStakeholderFinancePreviewContext}
            />
          ) : (
            <WorkItemPreviewModal
              workItem={currentWorkItem}
              isOpen={showPreview}
              onClose={() => setShowPreview(false)}
              contactPeople={previewContactPeople}
              timerAggregationMode={previewTimerAggregationMode}
            />
          )
        )}

        {/* Comprehensive Report Modal */}
        {showComprehensiveReport && (
          <WorkItemPreviewModal
            workItem={currentWorkItem}
            isOpen={showComprehensiveReport}
            onClose={() => setShowComprehensiveReport(false)}
            comprehensive
            contactPeople={previewContactPeople}
            timerAggregationMode={previewTimerAggregationMode}
          />
        )}
      </div>
    </div>

      <GuidedTour
        moduleKey="total-operations"
        activeTab={activeTab}
        onNavigate={handleTourNavigate}
        embeddedInModal
        autoFlowId="work-item-detail"
        contextFlowIds={WORK_ITEM_DETAIL_TOUR_FLOW_IDS}
      />

      {/* Nested related-item modal — renders above this modal so closing it
          returns here instead of leaving the stack */}
      {nestedWorkItemLoading && !nestedWorkItem && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40"
          style={{ zIndex: containerZIndex + 20 }}
        >
          <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
        </div>
      )}
      {nestedWorkItem && (
        <WorkItemDetailModal
          isOpen={true}
          onClose={() => setNestedWorkItem(null)}
          workItem={nestedWorkItem}
          onRefresh={onRefresh}
          listModuleContext={listModuleContext}
          activeSolutionKey={activeSolutionKey}
          containerZIndex={containerZIndex + 20}
        />
      )}
    </>
  );
}
